import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createImageThumbnailFromBlob,
  createVideoThumbnailFromBlob,
  getMediaManifest,
  isAudioMediaItem,
  isRestrictedOnceMediaItem,
  isVideoMediaItem,
  mediaKind as mediaKindOfItem,
  mediaMime,
  normalizeVoiceMediaItem,
} from '../media/mediaMessageUtils';
import { copyRedactedSupportBundle } from "../support/exportSupportBundle";
import {
  decryptWebMediaToObjectUrl,
  ensureWebMediaEncryptedBlobCached,
} from '../media/webMediaDecrypt';
import {
  deleteCachedEncryptedMediaBlob,
  hasCachedEncryptedMediaBlob,
  pruneOldEncryptedMediaBlobs,
} from '../media/webMediaBlobCache';
import { enableSupportDebugMode, disableSupportDebugMode, supportDebugModeEnabled } from "../support/debugFlags";
import { listMessagesE2EE, sendMessageE2EE, readReceiptsE2EE, listMessageDeleteEvents, deleteMessageItemWeb, listMediaManifestsWeb, sendTypingHintWeb } from "../api/messagesE2EE";
import { getMessageContextSummary, listGroupMembers, listMyGroups } from "../api/groups";
import {
  createMediaRequest,
  listMediaRequests,
  respondMediaRequest,
  fulfillMediaRequest,
  type MediaRequestItem,
} from "../api/mediaRequests";
import {
  createMediaEnvelope,
  createP2PShare,
  addP2PShareSignal,
  listTeaseTemplates,
  sendTeaseEvent,
  listTeaseEvents,
} from "../api/realtimeMessaging";
import { decryptWebWrappedPrivatePayload, parseWebEncryptedFlirtPlaintext, webWrappedPayloadHasLocalRecipientKey } from "../crypto/e2eeWrappedPrivatePayloadWeb";
import type { Message } from "../api/types";
import { useI18n } from "../i18n";
import MessageLiveKitPanelLazy from '../components/MessageLiveKitPanelLazy';
import { getMessageCryptoProvider } from "../crypto/messages/messageCryptoProvider";
import { normalizeEnvelopeLike } from "../crypto/messages/devPlainCompat";
import { completeViewOnceMedia, getEncryptedMediaBlobStatus, startViewOnceMedia } from '../api/mediaE2EE';
import { enqueueEncryptedWebMediaForThread, flushWebMediaOutbox } from "../offline/mediaSyncWeb";
import { decryptWebManifestThumbnailToDataUrl, hasWebManifestThumbnail, inspectWebManifestThumbnail } from '../media/webManifestThumbnail';
import { publishE2EEIdentityKey } from "../api/e2eeKeys";
import { ensureWebE2EEIdentityKey, readWebE2EEIdentityKey } from "../crypto/e2eeIdentityKeyStoreWeb";


function blockRestrictedOnceMediaAction(item: any, setStatus?: (value: string) => void) {
  if (!isRestrictedOnceMediaItem(item)) return false;

  if (setStatus) setStatus('This media can only be opened once and cannot be shared, copied, downloaded, or saved.');
  else window.alert('This media can only be opened once and cannot be shared, copied, downloaded, or saved.');

  return true;
}

function isMessagesDebugEnabled() {
  return supportDebugModeEnabled();
}

type MessageCallInvite = {
  callId: string;
  conversationId: string;
  originGroupId: string;
  callKind: string;
  transport: string;
  routingMode: string;
  participantCount: string;
};

function extractLiveKitCallInviteFromText(textInput: string, fallback?: {
  conversationId?: string;
  originGroupId?: string;
}): MessageCallInvite | null {
  const text = String(textInput || "");

  const callId =
    text.match(/\bCall ID:\s*(call_[A-Za-z0-9_]+)/i)?.[1] ||
    text.match(/\bcallId=(call_[A-Za-z0-9_]+)/i)?.[1] ||
    text.match(/\bcall_id=(call_[A-Za-z0-9_]+)/i)?.[1] ||
    text.match(/\b(call_[A-Za-z0-9_]{8,})\b/)?.[1] ||
    "";

  if (!callId) return null;

  const getParam = (name: string) => {
    const m = text.match(new RegExp(`${name}=([^\\s&]+)`, "i"));
    if (!m?.[1]) return "";
    try {
      return decodeURIComponent(m[1]);
    } catch {
      return m[1];
    }
  };

  return {
    callId,
    conversationId: getParam("conversationId") || getParam("conversation_id") || fallback?.conversationId || "",
    originGroupId: getParam("originGroupId") || getParam("groupId") || fallback?.originGroupId || "",
    callKind: getParam("callKind") || "voice",
    transport: getParam("transport") || "sfu_livekit",
    routingMode: getParam("routingMode") || "sfu_livekit",
    participantCount: getParam("participantCount") || "unknown",
  };
}

function liveKitCallHref(invite: MessageCallInvite) {
  const q = new URLSearchParams();
  q.set("callId", invite.callId);
  if (invite.conversationId) q.set("conversationId", invite.conversationId);
  if (invite.originGroupId) q.set("originGroupId", invite.originGroupId);
  q.set("callKind", invite.callKind || "voice");
  q.set("transport", invite.transport || "sfu_livekit");
  q.set("routingMode", invite.routingMode || "sfu_livekit");
  if (invite.participantCount) q.set("participantCount", invite.participantCount);

  // Used by MessageLiveKitPanel to immediately request a token and join.
  q.set("liveKitAutoJoin", "1");
  q.set("openCallTools", "1");

  return `/messages?${q.toString()}`;
}





function utf8ToBase64ForWeb(input: string): string {
  // Kept as a compatibility wrapper for older code paths.
  const bytes = new TextEncoder().encode(input);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function decodeDevPlainEnvelopeForWeb(cipherText: string, iv: string, envelope?: any): string {
  const normalized = normalizeEnvelopeLike({
    envelope: {
      ...(envelope || {}),
      ciphertext: cipherText || envelope?.ciphertext || "",
      nonce: iv || envelope?.nonce || "",
    },
  });

  const senderKeyId = String(normalized.senderKeyId || "");
  const algorithm = String(normalized.algorithm || "");

  if (
    String(normalized.nonce || "") !== "dev-plain" &&
    senderKeyId !== "mobile-dev-plain" &&
    senderKeyId !== "web-dev-plain" &&
    algorithm !== "DEV-PLAINTEXT-BASE64"
  ) {
    return "";
  }

  try {
    const bin = atob(String(normalized.ciphertext || ""));
    try {
      return decodeURIComponent(
        Array.from(bin)
          .map((ch) => "%" + ch.charCodeAt(0).toString(16).padStart(2, "0"))
          .join("")
      );
    } catch {
      return bin;
    }
  } catch {
    return "";
  }
}


function getRetryAfterMsFromResponse(res: Response, fallbackMs = 60_000) {
  const raw = res.headers.get("Retry-After") || res.headers.get("retry-after") || "";
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) {
    return Math.max(1_000, Math.min(10 * 60_000, Math.round(n * 1000)));
  }
  return fallbackMs;
}

let webRuntimeSettingsBackoffUntil = 0;
let WEB_MESSAGES_FALLBACK_ENABLED = true;
let WEB_MESSAGES_INBOX_POLL_MS = 60_000;
let WEB_MESSAGES_MEDIA_POLL_MS = 90_000;

let webInboxBackoffUntil = 0;
let webMediaBackoffUntil = 0;
let webReadReceiptBackoffUntil = 0;

function webMessagesVerboseSseEnabled() {
  try {
    const params = new URL(window.location.href).searchParams;
    return (
      params.get("debugSse") === "1" ||
      localStorage.getItem("intimsync.messages.verboseSse") === "1"
    );
  } catch {
    return false;
  }
}

function webMessagesDebugToolsEnabled() {
  try {
    const params = new URL(window.location.href).searchParams;
    return (
      params.get("debugMessages") === "1" ||
      localStorage.getItem("intimsync.messages.debugTools") === "1"
    );
  } catch {
    return false;
  }
}

function debugWebMessagesLog(...args: any[]) {
  if (webMessagesDebugToolsEnabled()) {
    console.log(...args);
  }
}


let webMessagesRealtimeConnected = false;

function isProbably429Error(e: any) {
  return (
    Number(e?.status) === 429 ||
    Number(e?.response?.status) === 429 ||
    String(e?.message || '').includes('429') ||
    String(e?.error || '').includes('polling_') ||
    String(e?.data?.error || '').includes('polling_')
  );
}

function getRetryAfterMsFromError(e: any, fallbackMs = 120_000) {
  const retryAfterSeconds =
    Number(e?.data?.retryAfterSeconds) ||
    Number(e?.retryAfterSeconds) ||
    Number(e?.response?.data?.retryAfterSeconds) ||
    0;

  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    return Math.max(1_000, Math.min(10 * 60_000, Math.round(retryAfterSeconds * 1000)));
  }

  return fallbackMs;
}

function clampMessagePollMs(value: unknown, fallback: number) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(30_000, Math.min(600_000, Math.round(n)));
}

function messageRuntimeAuthHeaders(): HeadersInit {
  const token =
    localStorage.getItem('intimsync.access_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('admin_jwt') ||
    '';

  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function loadMessagePollingRuntimeSettings() {
    if (Date.now() < webRuntimeSettingsBackoffUntil) {
      console.warn("Skipping runtime message settings fetch due to Retry-After backoff");
      return;
    }

  try {
    const res = await fetch('/api/v1/app-runtime-settings/messages', {
      headers: messageRuntimeAuthHeaders(),
      credentials: 'include',
    });

    
      if (res.status === 429) {
        const retryAfterMs = getRetryAfterMsFromResponse(res);
        webRuntimeSettingsBackoffUntil = Date.now() + retryAfterMs;
        console.warn("Runtime message settings throttled; using current/default settings", { retryAfterMs });
        return;
      }
if (!res.ok) return;

    const data = await res.json();
    const polling = data?.polling || {};

    WEB_MESSAGES_FALLBACK_ENABLED = polling.webFallbackEnabled !== false;
    WEB_MESSAGES_INBOX_POLL_MS = clampMessagePollMs(
      polling.webInboxPollMs,
      WEB_MESSAGES_INBOX_POLL_MS
    );
    WEB_MESSAGES_MEDIA_POLL_MS = clampMessagePollMs(
      polling.webMediaPollMs,
      WEB_MESSAGES_MEDIA_POLL_MS
    );
  } catch {
    // Keep safe defaults when runtime settings cannot be loaded.
  }
}

void loadMessagePollingRuntimeSettings();

if (typeof window !== 'undefined') {
  window.addEventListener('focus', () => {
    void loadMessagePollingRuntimeSettings();
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void loadMessagePollingRuntimeSettings();
  });
}

function shouldRunMessagesFallbackPoll() {
  if (!WEB_MESSAGES_FALLBACK_ENABLED) return false;
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

type MessageRealtimeEvent = {
  type?: string;
  conversationId?: string;
  [key: string]: any;
};

const activeMessageRealtimeStreams = new Map<string, AbortController>();
const activeMessageRealtimeStartsAt = new Map<string, number>();

async function streamMessageEvents(
  conversationId: string,
  signal: AbortSignal,
  onEvent: (event: MessageRealtimeEvent) => void
) {
  if (!conversationId || typeof fetch === 'undefined') return;

  const q = new URLSearchParams();
  q.set('conversationId', conversationId);

  const res = await fetch(`/api/v1/messages/events?${q.toString()}`, {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      ...messageRuntimeAuthHeaders(),
    },
    credentials: 'include',
    signal,
  });

  if (webMessagesVerboseSseEnabled()) {
    debugWebMessagesLog("[Messages web] SSE fetch response", {
      conversationId,
      status: res.status,
      ok: res.ok,
      hasBody: !!res.body,
      contentType: res.headers.get("content-type"),
    });
  }

  if (!res.ok || !res.body) {
    throw new Error(`message_events_http_${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  function consumeBlock(block: string) {
    const lines = String(block || '').split(/\r?\n/);
    let eventName = 'message';
    const dataLines: string[] = [];

    for (const line of lines) {
      if (!line || line.startsWith(':')) continue;

      if (line.startsWith('event:')) {
        eventName = line.slice('event:'.length).trim() || 'message';
        continue;
      }

      if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trimStart());
      }
    }

    if (!dataLines.length) return;

    const rawData = dataLines.join('\n');

    if (webMessagesVerboseSseEnabled()) {
      debugWebMessagesLog("[Messages web] SSE block", {
        conversationId,
        eventName,
        rawDataPreview: rawData.slice(0, 160),
      });
    }

    try {
      const parsed = JSON.parse(rawData);
      onEvent({
        ...parsed,
        type: parsed?.type || eventName,
      } as MessageRealtimeEvent);
    } catch {
      onEvent({
        type: eventName,
        conversationId,
        raw: rawData,
      } as any);
    }
  }

  function consumeChunk(chunk: string) {
    buffer += chunk;

    let match: RegExpMatchArray | null;
    while ((match = buffer.match(/\r?\n\r?\n/))) {
      const splitAt = match.index ?? -1;
      if (splitAt < 0) break;

      const block = buffer.slice(0, splitAt);
      buffer = buffer.slice(splitAt + match[0].length);
      consumeBlock(block);
    }
  }

  while (!signal.aborted) {
    const { value, done } = await reader.read();
    if (done) break;

    const text = decoder.decode(value, { stream: true });
    if (webMessagesVerboseSseEnabled()) {
      debugWebMessagesLog("[Messages web] SSE chunk", {
        conversationId,
        length: text.length,
        preview: text.slice(0, 120),
      });
    }
    consumeChunk(text);
  }

  const tail = buffer.trim();
  if (tail) consumeBlock(tail);
}


function shouldShowRealtimeDebugTools() {
  try {
    const qs = new URLSearchParams(window.location.search);
    return (
      qs.get('debugRealtime') === '1' ||
      qs.get('debugP2P') === '1' ||
      localStorage.getItem('intimsync.debugRealtime') === '1'
    );
  } catch {
    return false;
  }
}

type UiMessage = Message & {
  id: string;
  body: string;
  createdAt: string;
  senderUserId?: string;
  senderDisplayName?: string;
  offline?: boolean;
  clientMessageId?: string | null;
  deliveryState?: "pending" | "sent" | "server" | "read";
  replyToClientMessageId?: string | null;
  replyPreview?: string | null;
  messageKind?: "text" | "action" | "media";
  readByCount?: number;
  timelineKind?: "text" | "media";
  mediaItem?: any;
};

type GroupMember = {
  user_id: number | string;
  public_id?: string;
  display_name?: string;
  display_label?: string;
  role_name?: string;
  status?: string;
  allow_context_dm?: number | boolean;
  open_to_new_contacts?: number | boolean;
  can_contact_private?: number | boolean;
  group_allow_member_private_messages?: number | boolean;

  // Optional relationship metadata. Backend may not send this yet.
  is_partner?: number | boolean;
  is_current_partner?: number | boolean;
  partner_status?: string | null;
  partner_link_status?: string | null;
  partner_link_id?: string | number | null;
  can_request_partner?: number | boolean;
  can_add_partner?: number | boolean;
  multi_partner_unlocked?: number | boolean;
};

type ChannelRow = {
  group_id: string;
  label?: string;
  description?: string;
  group_type?: string | null;
  role_name?: string;
  member_count?: number;
  unread_count?: number;
  is_partner?: boolean;
};

type ChatMode = "group" | "private";
type ActionButtonMode = "icon_label" | "icon" | "label";
type MediaFlow = "camera" | "voice" | "request" | "attach" | null;
type TemporaryMode = "normal" | "view_once";

function readMessageContextParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    originGroupId: params.get("originGroupId") || "",
    originActivityId: params.get("originActivityId") || "",
    partnerUserId: params.get("partnerUserId") || "",
    questInviteId: params.get("questInviteId") || "",
    activityInviteId: params.get("activityInviteId") || "",
    messageId: params.get("messageId") || "",
    channelsOpen: params.get("channels") === "1",
  };
}

function makeGroupChatUrl(params: { originGroupId: string; originActivityId: string }) {
  const q = new URLSearchParams();

  if (params.originGroupId) q.set("originGroupId", params.originGroupId);
  if (params.originActivityId) q.set("originActivityId", params.originActivityId);

  return `/messages?${q.toString()}`;
}


function groupToolHref(groupId: string, tool: string) {
  return `/groups/${encodeURIComponent(groupId)}?tab=${encodeURIComponent(tool)}`;
}

function makePrivateChatUrl(params: { originGroupId: string; originActivityId: string }, recipientUserId: string) {
  const q = new URLSearchParams();

  if (params.originGroupId) q.set("originGroupId", params.originGroupId);
  if (params.originActivityId) q.set("originActivityId", params.originActivityId);
  if (recipientUserId) q.set("partnerUserId", recipientUserId);
  q.set("mode", "private");

  return `/messages?${q.toString()}`;
}

function getCurrentUserId(): string {
  const token = window.localStorage.getItem("intimsync.access_token");
  if (!token) return "";

  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload?.sub ? String(payload.sub) : "";
  } catch {
    return "";
  }
}

function memberId(member: GroupMember) {
  return String(member.user_id || "");
}

function boolish(value: any, fallback = false) {
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;
  return fallback;
}

function canPrivateContactMember(member: GroupMember) {
  return boolish((member as any).can_contact_private, false);
}

function memberLabel(member: GroupMember) {
  return (
    member.display_name ||
    member.display_label ||
    member.public_id ||
    String(member.user_id || "Member")

  );
}

function contextTitle(summary: any, params: { originGroupId: string; originActivityId: string }) {
  const groupLabel = summary?.group?.label || summary?.group?.name || params.originGroupId || "";
  const activityTitle = summary?.activity?.title || summary?.activity?.label || params.originActivityId || "";

  if (groupLabel && activityTitle) return `${groupLabel} · ${activityTitle}`;
  if (activityTitle) return activityTitle;
  if (groupLabel) return groupLabel;
  return "Messages";
}

function groupConversationId(params: { originGroupId: string; originActivityId: string }) {
  if (params.originActivityId) return `activity:${params.originActivityId}:group`;
  if (params.originGroupId) return `group:${params.originGroupId}:group`;
  return "";
}

function privateConversationId(params: { originGroupId: string; originActivityId: string }, recipientUserId: string) {
  if (params.originActivityId) return `activity:${params.originActivityId}:dm:${recipientUserId}`;
  if (params.originGroupId) return `group:${params.originGroupId}:dm:${recipientUserId}`;
  return `dm:${recipientUserId}`;
}

function makeClientMessageId() {
  return `web_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function isPartnerGroupLike(item: any) {
  const groupType = String(item?.group_type || item?.kind || "");
  const groupId = String(item?.group_id || "");
  return groupType === "partner_group" || groupId.startsWith("group_partner_");
}

function readLastOpenedMessageGroupId() {
  try {
    return window.localStorage.getItem("intimsync.last_opened_group_id") || "";
  } catch {
    return "";
  }
}



function renderMessageBodyWithLinks(body: string) {
  const text = String(body || "");
  const urlRe = /(https?:\/\/[^\s<>"']+|intimsync:\/\/[^\s<>"']+)/g;
  const parts: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = urlRe.exec(text)) !== null) {
    const url = match[0];
    const start = match.index;

    if (start > last) {
      parts.push(text.slice(last, start));
    }

    const href = url.startsWith("intimsync://")
      ? `https://app.intimsync.com/messages?call=${encodeURIComponent(url)}`
      : url;

    parts.push(
      <a
        key={`${url}-${start}`}
        href={href}
        target="_blank"
        rel="noreferrer"
        className="underline decoration-sky-300/70 underline-offset-2 hover:text-sky-200 break-all"
      >
        {url}
      </a>
    );

    last = start + url.length;
  }

  if (last < text.length) {
    parts.push(text.slice(last));
  }

  return parts.length ? parts : text;
}


function shouldOpenLiveKitCallToolsFromUrl() {
  try {
    const params = new URL(window.location.href).searchParams;
    return (
      params.get("openCallTools") === "1" ||
      params.get("liveKitAutoJoin") === "1" ||
      !!params.get("callId") ||
      !!params.get("call_id")
    );
  } catch {
    return false;
  }
}


let webE2eeIdentityBootstrapInFlight: Promise<void> | null = null;

const WEB_E2EE_IDENTITY_PUBLISHED_MARKER_KEY = 'intimsync.web.e2ee.identity_published_key_id.v1';

function hasLocalWebE2EEIdentity() {
  try {
    return !!readWebE2EEIdentityKey();
  } catch {
    return false;
  }
}

function isCurrentWebE2EEIdentityMarkedPublished() {
  try {
    const key = readWebE2EEIdentityKey();
    if (!key?.keyId) return false;

    return window.localStorage.getItem(WEB_E2EE_IDENTITY_PUBLISHED_MARKER_KEY) === key.keyId;
  } catch {
    return false;
  }
}

function clearWebRecipientKeyCaches() {
  try {
    for (const cacheKey of Object.keys(window.localStorage || {})) {
      const normalized = cacheKey.toLowerCase();

      if (
        normalized.includes('intimsync_recipient_key:') ||
        normalized.includes('recipient_key:') ||
        normalized.includes('recipientkey:')
      ) {
        window.localStorage.removeItem(cacheKey);
      }
    }
  } catch {
    // Non-fatal. This only clears stale recipient-key caches.
  }
}

function ensureAndPublishWebE2EEIdentityOnce() {
  if (webE2eeIdentityBootstrapInFlight) return webE2eeIdentityBootstrapInFlight;

  webE2eeIdentityBootstrapInFlight = (async () => {
    const key = await ensureWebE2EEIdentityKey();

    if (isCurrentWebE2EEIdentityMarkedPublished()) {
      return;
    }

    await publishE2EEIdentityKey({
      keyId: key.keyId,
      algorithm: key.algorithm,
      publicKeyBase64: key.publicKeyBase64,
    });

    try {
      window.localStorage.setItem(WEB_E2EE_IDENTITY_PUBLISHED_MARKER_KEY, key.keyId);
    } catch {
      // Non-fatal. Publish already succeeded.
    }

    clearWebRecipientKeyCaches();
  })().finally(() => {
    webE2eeIdentityBootstrapInFlight = null;
  });

  return webE2eeIdentityBootstrapInFlight;
}



function PrivateMediaEyeSlashIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: '0 0 auto', opacity: 0.9 }}
    >
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20C7 20 2.73 16.89 1 12c.8-2.27 2.2-4.2 4-5.56" />
      <path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5c.96 0 1.83-.39 2.47-1.03" />
      <path d="M14.12 5.17A10.85 10.85 0 0 1 23 12c-.5 1.41-1.32 2.7-2.36 3.78" />
      <path d="M3 3l18 18" />
    </svg>
  );
}

function PrivateMediaStatusIcon({
  state,
  size = 16,
}: {
  state: 'sending' | 'sent' | 'delivered' | 'read';
  size?: number;
}) {
  const isRead = state === 'read';
  const double = state === 'delivered' || state === 'read';
  const stroke = isRead ? '#ffffff' : 'var(--muted)';
  const fill = isRead ? 'var(--primary)' : 'transparent';
  const border = isRead ? 'var(--primary)' : 'var(--muted)';

  if (state === 'sending') {
    return (
      <span
        title="Sending"
        style={{
          width: size,
          height: size,
          borderRadius: 999,
          border: '2px dotted var(--border)',
          display: 'inline-block',
          opacity: 0.85,
        }}
      />
    );
  }

  const circle = (left: number, opacity: number, zIndex: number) => (
    <span
      style={{
        position: double ? 'absolute' : 'relative',
        left: double ? left : undefined,
        top: 0,
        width: size,
        height: size,
        borderRadius: 999,
        border: `2px solid ${border}`,
        background: fill,
        opacity,
        zIndex,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        color: stroke,
        fontSize: Math.round(size * 0.72),
        lineHeight: 1,
        fontWeight: 900,
      }}
    >
      ✓
    </span>
  );

  if (!double) {
    return (
      <span
        title="Sent"
        style={{
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {circle(0, 0.95, 1)}
      </span>
    );
  }

  return (
    <span
      title={state === 'read' ? 'Read' : 'Received'}
      style={{
        position: 'relative',
        width: size + 12,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
      }}
    >
      {circle(0, isRead ? 0.92 : 0.62, 1)}
      {circle(10, 1, 2)}
    </span>
  );
}



function webMessageDateKey(value: any) {
  const d = new Date(value || Date.now());
  if (!Number.isFinite(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}


function webMessageTimeLabel(value: any) {
  const d = new Date(value || Date.now());
  if (!Number.isFinite(d.getTime())) return '';

  const time = d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const msgStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const ageDays = Math.floor((todayStart - msgStart) / 86400000);

  if (ageDays <= 0) return time;

  if (ageDays < 7) {
    const weekday = d.toLocaleDateString([], { weekday: "short" });
    return `${weekday} ${time}`;
  }

  const fullDate = d.toLocaleDateString([], {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return `${fullDate} ${time}`;
}

function webMessageDateLabel(value: any) {
  const d = new Date(value || Date.now());
  if (!Number.isFinite(d.getTime())) return '';

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const key = webMessageDateKey(d);
  if (key === webMessageDateKey(today)) return 'Today';
  if (key === webMessageDateKey(yesterday)) return 'Yesterday';

  return d.toLocaleDateString([], {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}


function WebMessageDateSeparator({ label }: { label: string }) {
  if (!label) return null;

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        margin: "12px 0 10px",
        position: "sticky",
        top: 0,
        zIndex: 1,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          border: "1px solid rgba(148,163,184,0.22)",
          background: "rgba(15,23,42,0.86)",
          color: "var(--muted)",
          borderRadius: 999,
          padding: "0.24rem 0.7rem",
          fontSize: "0.72rem",
          fontWeight: 850,
          boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        }}
      >
        {label}
      </div>
    </div>
  );
}

function privateMediaStatusStateForWeb(item: any): 'sending' | 'sent' | 'delivered' | 'read' {
  const deliveryState = String(
    item?.effectiveDeliveryState ||
    item?.effective_delivery_state ||
    item?.deliveryState ||
    item?.delivery_state ||
    item?.delivery_state_for_recipient ||
    item?.recipientDeliveryState ||
    item?.recipient_delivery_state ||
    item?.mediaItem?.effectiveDeliveryState ||
    item?.mediaItem?.effective_delivery_state ||
    item?.mediaItem?.deliveryState ||
    item?.mediaItem?.delivery_state ||
    item?.mediaItem?.delivery_state_for_recipient ||
    item?.mediaItem?.recipientDeliveryState ||
    item?.mediaItem?.recipient_delivery_state ||
    ''
  ).toLowerCase();

  const explicitRead =
    !!(item?.readAt || item?.read_at || item?.seenAt || item?.seen_at || item?.viewedAt || item?.viewed_at) ||
    !!(item?.viewedByRecipientAt || item?.viewed_by_recipient_at) ||
    !!(item?.mediaItem?.viewedByRecipientAt || item?.mediaItem?.viewed_by_recipient_at) ||
    item?.readByRecipient === true ||
    item?.read_by_recipient === true ||
    item?.mediaItem?.readByRecipient === true ||
    item?.mediaItem?.read_by_recipient === true ||
    deliveryState.includes('read') ||
    deliveryState.includes('seen') ||
    deliveryState.includes('viewed');

  if (explicitRead) return 'read';

  if (
    deliveryState.includes('delivered') ||
    deliveryState.includes('received') ||
    deliveryState.includes('downloaded') ||
    deliveryState.includes('acknowledged')
  ) {
    return 'delivered';
  }

  if (
    deliveryState.includes('sending') ||
    deliveryState.includes('pending') ||
    deliveryState.includes('uploading')
  ) {
    return 'sending';
  }

  return 'sent';
}


export default function MessagesPage() {
  const showRealtimeDebugTools = webMessagesDebugToolsEnabled();

  const { t } = useI18n();
  const params = useMemo(() => readMessageContextParams(), []);
  const currentUserId = useMemo(() => getCurrentUserId(), []);


  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const hadIdentity = hasLocalWebE2EEIdentity();
        const alreadyPublished = isCurrentWebE2EEIdentityMarkedPublished();

        if (hadIdentity && alreadyPublished) return;

        if (!hadIdentity) {
          console.warn('[Messages web] local web E2EE identity missing; generating and publishing a new one');
        } else {
          console.info('[Messages web] local web E2EE identity exists but is not marked published; publishing');
        }

        await ensureAndPublishWebE2EEIdentityOnce();

        if (!cancelled) {
          console.info('[Messages web] local web E2EE identity ready/published');
        }
      } catch (error) {
        console.warn('[Messages web] could not bootstrap local web E2EE identity', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const [summary, setSummary] = useState<any | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [chatMode, setChatMode] = useState<ChatMode>(() => {
    const mode = new URLSearchParams(window.location.search).get("mode");
    if (mode === "private") return "private";
    return params.originGroupId ? "group" : "private";
  });
  const [membersOpen, setMembersOpen] = useState(false);
  const [selectedPrivateIds, setSelectedPrivateIds] = useState<string[]>([]);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [callStatusById, setCallStatusById] = useState<Record<string, any>>({});
  const [typingUsersById, setTypingUsersById] = useState<Record<string, { label: string; until: number }>>({});
  const typingClearTimerRef = useRef<number | null>(null);
  const [chatSearchQuery, setChatSearchQuery] = useState("");
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  const [chatSearchIndex, setChatSearchIndex] = useState(0);
  const [chatSearchFilterOnly, setChatSearchFilterOnly] = useState(false);
  const messageRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const typingHintLastSentAtRef = useRef(0);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState<UiMessage | null>(null);
  const [ackedClientMessageIds, setAckedClientMessageIds] = useState<Set<string>>(() => new Set());
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [gifOpen, setGifOpen] = useState(false);
  const [mediaFlow, setMediaFlow] = useState<MediaFlow>(null);
  const [mediaKind, setMediaKind] = useState<"photo" | "video" | "voice" | null>(null);
  const [temporaryMode, setTemporaryMode] = useState<TemporaryMode>("normal");
  const [selectedMediaRequestTargetId, setSelectedMediaRequestTargetId] = useState<string>("");
  const [mediaOpen, setMediaOpen] = useState(false);
  const [privatePhotoResponseActive, setPrivatePhotoResponseActive] = useState(false);
  const [respondedEncryptedFlirtIds, setRespondedEncryptedFlirtIds] = useState<Record<string, true>>({});
  const [webMediaPreview, setWebMediaPreview] = useState<{
    item: any;
    mediaId: string;
    title: string;
    mimeType: string;
    kind: string;
    url: string;
    restrictedOnce: boolean;
    ownedObjectUrl?: boolean;
    viewOnceStarted?: boolean;
    sourceMessage?: any;
    canDeleteEveryone?: boolean;
    deleteAfterViewSeconds?: number;
  } | null>(null);
  const [webMediaPreviewCountdown, setWebMediaPreviewCountdown] = useState<number | null>(null);
  const [webVideoThumbById, setWebVideoThumbById] = useState<Record<string, string>>({});
  const [webMediaThumbById, setWebMediaThumbById] = useState<Record<string, string>>({});
  const webMediaThumbInFlightRef = useRef<Set<string>>(new Set());
  const [webUnavailableMediaById, setWebUnavailableMediaById] = useState<Record<string, string>>({});
  const [webDeletedMediaById, setWebDeletedMediaById] = useState<Record<string, string>>({});
  const [webMediaBlobStatusById, setWebMediaBlobStatusById] = useState<Record<string, any>>({});
  const [webCachedMediaById, setWebCachedMediaById] = useState<Record<string, true>>({});
  const webMediaBlobStatusRequestedRef = useRef<Set<string>>(new Set());

  const webMediaAutoCacheRequestedRef = useRef<Set<string>>(new Set());
  const webMediaAutoCacheRunningRef = useRef(false);

  const webApiBackoffRef = useRef<Record<string, number>>({});

  function webApiBackoffActive(key: string) {
    const until = webApiBackoffRef.current[key] || 0;
    return until > Date.now();
  }

  function markWebApiBackoff(key: string, error: any, fallbackMs = 15000) {
    const message = String(error?.message || error || '').toLowerCase();
    const status = Number(error?.status || error?.response?.status || 0);

    if (
      status === 429 ||
      status === 502 ||
      status === 503 ||
      status === 504 ||
      message.includes('429') ||
      message.includes('502') ||
      message.includes('503') ||
      message.includes('504') ||
      message.includes('rate') ||
      message.includes('bad gateway')
    ) {
      webApiBackoffRef.current[key] = Date.now() + fallbackMs;
      return true;
    }

    return false;
  }

  const [webConsumedOnceMediaById, setWebConsumedOnceMediaById] = useState<Record<string, string>>({});
  const [webMediaFitMode, setWebMediaFitMode] = useState<'fit' | 'original'>('fit');
  const [webMediaControlsVisible, setWebMediaControlsVisible] = useState(true);
  const [webMediaDeleteDialog, setWebMediaDeleteDialog] = useState<{
    message: any;
    restrictedOnce: boolean;
    canDeleteEveryone: boolean;
  } | null>(null);
  const webMediaControlsTimerRef = useRef<number | null>(null);
  const webMediaPreviewObjectUrlRef = useRef<string | null>(null);
  const completedNormalWebMediaViewIdsRef =
    useRef<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [olderMessagesLoading, setOlderMessagesLoading] = useState(false);
  const [olderMessagesHasMore, setOlderMessagesHasMore] = useState(false);
  const [olderMessagesBeforeId, setOlderMessagesBeforeId] = useState<string | number | null>(null);
  const [olderMessagesCooldownUntil, setOlderMessagesCooldownUntil] = useState(0);
  const olderMessagesCooldownTimerRef = useRef<number | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [channelsOpen, setChannelsOpen] = useState(false);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [mediaRequests, setMediaRequests] = useState<MediaRequestItem[]>([]);
  const [mediaRequestSending, setMediaRequestSending] = useState(false);
  const [readMessageIds, setReadMessageIds] = useState<Set<string>>(() => new Set());
  const [realtimeBusy, setRealtimeBusy] = useState(false);
  const [supportBundleBusy, setSupportBundleBusy] = useState(false);
  const [supportBundleStatus, setSupportBundleStatus] = useState("");
  const [supportDebugEnabled, setSupportDebugEnabled] = useState(() =>
    supportDebugModeEnabled()
  );

  const [showP2PDebugModal, setShowP2PDebugModal] = useState(false);
  useEffect(() => {
    if (!shouldOpenLiveKitCallToolsFromUrl()) return;

    setShowP2PDebugModal(true);

    const t = window.setTimeout(() => {
      try {
        document.getElementById("message-livekit-call-tools")?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } catch {}
    }, 150);

    return () => window.clearTimeout(t);
  }, []);

  const [teaseTemplates, setTeaseTemplates] = useState<any[]>([]);
  const [encryptedFlirts, setEncryptedFlirts] = useState<Array<{
    eventId: string;
    senderUserId: string;
    plaintext: string;
    contentType: string;
    mediaRequest?: any;
    mediaOffer?: any;
    createdAt?: string;
  }>>([]);
  const [encryptedFlirtsBusy, setEncryptedFlirtsBusy] = useState(false);


  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastSeenMessageCountRef = useRef(0);
  const stickToBottomRef = useRef(true);
  const userScrolledAwayRef = useRef(false);
  const webMediaAutoFollowPendingRef = useRef(false);
  const webMediaUnreadPendingRef = useRef(0);
  const mediaRowsFallbackTimerRef = useRef<number | null>(null);
  const mediaRowsRefreshInFlightRef =
    useRef<Record<string, Promise<void> | undefined>>({});
  const mediaRowsRefreshQueuedTimerRef =
    useRef<Record<string, number | undefined>>({});
  const mediaRowsRefreshQueuedSourceRef =
    useRef<Record<string, string>>({});
  const mediaRowsRefreshLastAtRef =
    useRef<Record<string, number>>({});
  const MEDIA_ROWS_REFRESH_DEDUPE_MS = 1200;

  const initialInboxMediaCountByConversationRef =
    useRef<Record<string, number>>({});

  const webVisibleMediaIdsRef = useRef<Set<string>>(new Set());
  const [webVisibleMediaEpoch, setWebVisibleMediaEpoch] = useState(0);

  const [actionButtonMode, setActionButtonMode] = useState<ActionButtonMode>(() => {
    const saved = window.localStorage.getItem("intimsync.messages.action_button_mode");
    return saved === "icon" || saved === "label" || saved === "icon_label" ? saved : "icon";
  });

  useEffect(() => {
    function refreshActionButtonMode() {
      const saved = window.localStorage.getItem("intimsync.messages.action_button_mode");
      setActionButtonMode(saved === "icon" || saved === "label" || saved === "icon_label" ? saved : "icon");
    }

    void refreshTeasesForRealtime();

    window.addEventListener("focus", refreshActionButtonMode);
    window.addEventListener("storage", refreshActionButtonMode);
    refreshActionButtonMode();

    return () => {
      window.removeEventListener("focus", refreshActionButtonMode);
      window.removeEventListener("storage", refreshActionButtonMode);
    };
  }, []);

  function tt(key: string, fallback: string) {
    const value = t(key);
    return value && value !== key ? value : fallback;
  }

  
  function activeConversationIdForRealtime() {
    if (chatMode === "group") return groupConversationId(params);
    const targetId = selectedPrivateIds[0] || params.partnerUserId;
    return privateConversationId(params, targetId);
  }

  async function handleCopyRedactedWebSupportBundle() {
    setSupportBundleBusy(true);
    setSupportBundleStatus("");

    try {
      await copyRedactedSupportBundle({
        conversationId: activeConversationIdForRealtime(),
        realtimeConnected: webMessagesRealtimeConnected,
      });

      setSupportBundleStatus(
        "Redacted support bundle copied. Paste it into a support message."
      );
    } catch (e: any) {
      setSupportBundleStatus(
        String(e?.message || "Could not create support bundle")
      );
    } finally {
      setSupportBundleBusy(false);
    }
  }

  function handleEnableWebSupportDebugMode() {
    enableSupportDebugMode();
    setSupportDebugEnabled(true);
    setSupportBundleStatus(
      "Support debug mode enabled for 24 hours. Reproduce the issue, then copy a redacted support bundle."
    );
  }

  function handleDisableWebSupportDebugMode() {
    disableSupportDebugMode();
    setSupportDebugEnabled(false);
    setSupportBundleStatus("Support debug mode disabled.");
  }

  async function refreshTeasesForRealtime() {
    if (!params.originGroupId) return;
    try {
      const data = await listTeaseTemplates(params.originGroupId, "spicy");
      setTeaseTemplates(Array.isArray((data as any)?.items) ? (data as any).items : []);
    } catch {
      setTeaseTemplates([]);
    }
  }

  async function sendMediaEnvelopePlaceholder(kind: "photo" | "video" | "voice" | "file") {
    const conversationId = activeConversationIdForRealtime();
    if (!conversationId) {
      setError("Open a group or private conversation first.");
      return;
    }

    setRealtimeBusy(true);
    setStatusMsg(null);
    setError(null);

    try {
      await createMediaEnvelope({
        conversationId,
        mediaKind: kind,
        clientMessageId: makeClientMessageId(),
        encryptedManifest: {
          version: 1,
          algorithm: "XCHACHA20-POLY1305",
          blobRef: `web-placeholder-${kind}-${Date.now()}`,
          keyEnvelope: "placeholder",
          nonce: "placeholder",
          ciphertextSha256: "placeholder",
        },
        captionEnvelope: null,
        mimeType:
          kind === "voice" ? "audio/webm" :
          kind === "video" ? "video/webm" :
          kind === "photo" ? "image/jpeg" :
          "application/octet-stream",
        durationMs: kind === "voice" || kind === "video" ? 1000 : null,
        width: kind === "photo" || kind === "video" ? 640 : null,
        height: kind === "photo" || kind === "video" ? 480 : null,
        expiresAt: null,
        sizeBytes: 1,
      });
      setStatusMsg(`${kind} E2EE media envelope created.`);
    } catch (e: any) {
      setError(
        e?.data?.error ||
        e?.response?.data?.error ||
        e?.message ||
        "Could not create media envelope."
      );
    } finally {
      setRealtimeBusy(false);
    }
  }

  async function createP2PSharePlaceholder(kind: "image" | "video" | "voice" | "file" | "collab") {
    const conversationId = activeConversationIdForRealtime();
    if (!conversationId) {
      setError("Open a group or private conversation first.");
      return;
    }

    setRealtimeBusy(true);
    setStatusMsg(null);
    setError(null);

    try {
      const created = await createP2PShare({
        conversationId,
        shareKind: kind,
        originGroupId: params.originGroupId || null,
        originActivityId: params.originActivityId || null,
        meta: { client: "web", placeholder: true },
      });

      const shareId =
        (created as any)?.item?.shareId ||
        (created as any)?.item?.share_id ||
        (created as any)?.shareId ||
        (created as any)?.share_id;

      if (shareId) {
        await addP2PShareSignal(String(shareId), {
          signalType: "offer",
          payload: { placeholder: true, kind },
        });
      }

      setStatusMsg(`${kind} P2P share offer created.`);
    } catch (e: any) {
      setError(e?.message || "Could not create P2P share.");
    } finally {
      setRealtimeBusy(false);
    }
  }

  async function loadRecentEncryptedFlirts() {
    if (!params.originGroupId) {
      setError("Encrypted flirts require a group context.");
      return;
    }

    setEncryptedFlirtsBusy(true);
    setError(null);

    try {
      const res = await listTeaseEvents(params.originGroupId, 10);
      const items = Array.isArray((res as any)?.items) ? (res as any).items : [];
      const out: Array<{
        eventId: string;
        senderUserId: string;
        plaintext: string;
        contentType: string;
        mediaRequest?: any;
        mediaOffer?: any;
        createdAt?: string;
      }> = [];

      for (const item of items) {
        const payload = item?.privatePayload || item?.private_payload || item || {};
        const encryptedPayloadJson =
          payload?.encryptedPayloadJson ||
          payload?.encrypted_payload_json ||
          item?.encryptedPayloadJson ||
          item?.encrypted_payload_json;

        if (!encryptedPayloadJson) continue;

        const env = typeof encryptedPayloadJson === "string"
          ? JSON.parse(encryptedPayloadJson)
          : encryptedPayloadJson;

        if (env?.algorithm !== "aes-256-cbc+hmac-sha256+wrapped-content-key-v1") continue;
        if (!webWrappedPayloadHasLocalRecipientKey(env)) continue;

        try {
          const plaintext = await decryptWebWrappedPrivatePayload({ envelope: env });
          const parsed = parseWebEncryptedFlirtPlaintext(plaintext);

          out.push({
            eventId: String(item?.eventId || item?.event_id || payload?.eventId || payload?.event_id || ""),
            senderUserId: String(item?.senderUserId || item?.sender_user_id || payload?.senderUserId || payload?.sender_user_id || ""),
            plaintext: parsed.text,
            contentType: String(env?.contentType || payload?.payloadContentType || payload?.payload_content_type || ""),
            mediaRequest: parsed.mediaRequest,
            mediaOffer: parsed.mediaOffer,
            createdAt: item?.createdAt ? String(item.createdAt) : item?.created_at ? String(item.created_at) : undefined,
          });
        } catch (e) {
          console.warn("[web-encrypted-flirt-decrypt] failed", e);
        }
      }

      setEncryptedFlirts(out);
      setStatusMsg(out.length ? `Loaded ${out.length} encrypted flirt(s).` : "No decryptable encrypted flirts found.");
    } catch (e: any) {
      setError(e?.message || "Could not load encrypted flirts.");
    } finally {
      setEncryptedFlirtsBusy(false);
    }
  }

  async function sendFirstTeasePlaceholder() {
    if (!params.originGroupId) {
      setError("Teases require a group context.");
      return;
    }

    const conversationId = activeConversationIdForRealtime();
    if (!conversationId) {
      setError("Open a group conversation first.");
      return;
    }

    setRealtimeBusy(true);
    setStatusMsg(null);
    setError(null);

    try {
      let templates = teaseTemplates;
      if (!templates.length) {
        const data = await listTeaseTemplates(params.originGroupId, "spicy");
        templates = Array.isArray((data as any)?.items) ? (data as any).items : [];
        setTeaseTemplates(templates);
      }

      const first = templates[0];
      const templateId = first?.template_id || first?.templateId;
      if (!templateId) {
        setError("No tease templates available.");
        return;
      }

      await sendTeaseEvent(params.originGroupId, {
        conversationId,
        templateId: String(templateId),
        packCode: "spicy",
      });

      setStatusMsg(`Tease sent: ${first?.title || templateId}`);
    } catch (e: any) {
      setError(e?.message || "Could not send tease.");
    } finally {
      setRealtimeBusy(false);
    }
  }

  function actionContent(icon: string, label: string) {
    if (actionButtonMode === "icon") return icon;
    if (actionButtonMode === "label") return <span style={{ fontSize: "0.78rem" }}>{label}</span>;

    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
        <span aria-hidden="true">{icon}</span>
        <span style={{ fontSize: "0.74rem" }}>{label}</span>
      </span>
    );
  }

  const iconButtonStyle = {
    minHeight: 26,
    paddingInline: actionButtonMode === "icon" ? 8 : 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    fontSize: "0.74rem",
    fontWeight: 850,
  } as const;

  const pageShellStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    height: "calc(100dvh - 112px)",
    maxHeight: "calc(100dvh - 112px)",
    minHeight: 0,
    display: "grid",
    gridTemplateRows: "minmax(0, 1fr)",
    gap: 0,
    overflow: "hidden",
    boxSizing: "border-box",
  } as const;

  const chatPanelStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
    display: "grid",
    gridTemplateRows: "auto minmax(0, 1fr) auto",
    borderRadius: 22,
    boxShadow: "0 18px 48px rgba(0,0,0,0.22)",
    marginBottom: 0,
    boxSizing: "border-box",
  } as const;

  const allOtherMembers = useMemo(
    () => members.filter((m) => memberId(m) && memberId(m) !== currentUserId),
    [members, currentUserId]
  );

  const otherMembers = useMemo(
    () => allOtherMembers.filter((m) => canPrivateContactMember(m)),
    [allOtherMembers]
  );

  const unavailableMembers = useMemo(
    () => allOtherMembers.filter((m) => !canPrivateContactMember(m)),
    [allOtherMembers]
  );

  const allRecipientIds = useMemo(
    () => members.map(memberId).filter(Boolean),
    [members]
  );

  const visiblePrivateId =
    selectedPrivateIds[0] ||
    (otherMembers[0] ? memberId(otherMembers[0]) : "");

  const mediaRequestTargets = useMemo(
    () => allOtherMembers.filter((m) => memberId(m) && memberId(m) !== currentUserId),
    [allOtherMembers, currentUserId]
  );

  const effectiveMediaRequestTargetId = useMemo(() => {
    if (chatMode === "private" && visiblePrivateId) return visiblePrivateId;
    if (selectedMediaRequestTargetId) return selectedMediaRequestTargetId;
    if (mediaRequestTargets.length === 1) return memberId(mediaRequestTargets[0]);
    return "";
  }, [chatMode, visiblePrivateId, selectedMediaRequestTargetId, mediaRequestTargets]);

  const conversationId = useMemo(() => {
    if (chatMode === "group") return groupConversationId(params);
    return visiblePrivateId ? privateConversationId(params, visiblePrivateId) : "";
  }, [chatMode, params, visiblePrivateId]);

  useEffect(() => {
    if (!conversationId) return;

    let cancelled = false;

    async function runInitialLoad() {
      // Initial fetch for the active conversation.
      // Without this, web may only show stored messages after a send/realtime event
      // triggers loadMessages indirectly.
      await loadMessages({ force: true, realtime: true, source: "initial" });
      if (!cancelled) scrollToBottomSoon();

      // Avoid hammering media request endpoint on every page refresh.
      // It is also polled by the runtime fallback path and refreshed on media actions.
      if (mediaOpen) {
        await refreshMediaRequests(true);
        if (!cancelled) scrollToBottomSoon();
      }
    }

    void runInitialLoad();

    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  useEffect(() => {
    if (chatMode !== "private") return;

    const selectedStillAvailable = selectedPrivateIds.some((id) =>
      otherMembers.some((m) => memberId(m) === id)
    );

    if (selectedStillAvailable) return;

    if (otherMembers.length) {
      setSelectedPrivateIds([memberId(otherMembers[0])]);
    } else {
      setSelectedPrivateIds([]);
      setChatMode("group");
    }
  }, [chatMode, selectedPrivateIds, otherMembers]);

  useEffect(() => {
    if (!params.originGroupId && !params.originActivityId) return;

    const label = contextTitle(summary, params);
    const url =
      chatMode === "private" && visiblePrivateId
        ? makePrivateChatUrl(params, visiblePrivateId)
        : makeGroupChatUrl(params);

    try {
      window.localStorage.setItem("intimsync.last_chat_url", url);
      window.localStorage.setItem("intimsync.last_chat_label", label || "Last chat");
      window.localStorage.setItem("intimsync.last_chat_mode", chatMode);
      if (params.originGroupId) {
        window.localStorage.setItem("intimsync.last_opened_group_id", params.originGroupId);
      }
    } catch {
      // ignore
    }
  }, [chatMode, visiblePrivateId, params.originGroupId, params.originActivityId, summary]);

  useEffect(() => {
    let cancelled = false;

    if (!params.originGroupId && !params.originActivityId) return;

    getMessageContextSummary({
      originGroupId: params.originGroupId,
      originActivityId: params.originActivityId,
    })
      .then((res) => {
        if (!cancelled) setSummary(res);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      });

    return () => {
      cancelled = true;
    };
  }, [params.originGroupId, params.originActivityId]);

  async function refreshGroupMembers() {
    if (!params.originGroupId) {
      setMembers([]);
      return;
    }

    const res = await listGroupMembers(params.originGroupId);
    const list = Array.isArray(res?.items) ? res.items : [];
    setMembers(list);

    const contactable = list.filter((m: GroupMember) => {
      const id = memberId(m);
      return id && id !== currentUserId && canPrivateContactMember(m);
    });

    const requestedPrivateId = params.partnerUserId;
    const requestedMember = requestedPrivateId
      ? contactable.find((m: GroupMember) => memberId(m) === requestedPrivateId)
      : null;

    setSelectedPrivateIds((prev) => {
      const validPrev = prev.filter((id) =>
        contactable.some((m: GroupMember) => memberId(m) === id)
      );

      if (validPrev.length) return validPrev;
      if (requestedMember) return [memberId(requestedMember)];
      return contactable[0] ? [memberId(contactable[0])] : [];
    });

    if (chatMode === "private" && !contactable.length) {
      setChatMode("group");
      setStatusMsg("No available private contacts in this group.");
    }
  }

  useEffect(() => {
    let cancelled = false;

    if (!params.originGroupId) return;

    refreshGroupMembers().catch(() => {
      if (!cancelled) setMembers([]);
    });

    return () => {
      cancelled = true;
    };
  }, [params.originGroupId, currentUserId]);

  useEffect(() => {
    function onFocusRefreshMembers() {
      if (params.originGroupId) {
        refreshGroupMembers().catch(() => {});
      }
    }

    window.addEventListener("focus", onFocusRefreshMembers);
    return () => window.removeEventListener("focus", onFocusRefreshMembers);
  }, [params.originGroupId, currentUserId, chatMode]);

  function unb64(str: string) {
    const bin = atob(str);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }

  function b64(buf: ArrayBuffer) {
    return btoa(String.fromCharCode(...new Uint8Array(buf)));
  }

  async function getOrCreateLocalKey() {
    // DEV PLACEHOLDER:
    // The old placeholder used one browser-local AES key, so other users saw "[encrypted message]".
    // This deterministic conversation key lets test users decrypt each other's messages.
    // Replace with real per-recipient E2EE envelopes before production use.
    const scope = conversationId || "global";
    const seed = new TextEncoder().encode(`intimsync-dev-message-key-v1:${scope}`);
    const digest = await crypto.subtle.digest("SHA-256", seed);

    return crypto.subtle.importKey(
      "raw",
      digest,
      "AES-GCM",
      false,
      ["encrypt", "decrypt"]
    );
  }

  async function encryptForSend(plainText: string, clientMessageId: string) {
    const envelope = await getMessageCryptoProvider().encryptText({
      plaintext: plainText,
      aad: {
        conversationId,
        clientMessageId,
        senderUserId: String(currentUserId || "web"),
        recipientScope: `conversation:${conversationId}`,
        version: 2,
      },
      keyRef: `web-dev-xchacha20:${conversationId}`,
    });

    return {
      version: Number(envelope.version || 1),
      cipherText: String(envelope.ciphertext || ""),
      iv: String(envelope.nonce || ""),
      algorithm: String(envelope.algorithm || "DEV-PLAINTEXT-BASE64"),
      senderKeyId: String(envelope.senderKeyId || "web-dev-plain"),
    };
  }


  async function decryptIncomingEnvelope(cipherText: string, iv: string, envelope?: any, aad?: any): Promise<string> {
    const normalizedEnvelope = normalizeEnvelopeLike({
      envelope: {
        ...(envelope || {}),
        ciphertext: cipherText || envelope?.ciphertext || "",
        nonce: iv || envelope?.nonce || "",
      },
    });

    try {
      const providerPlain = await getMessageCryptoProvider().decryptText({
        envelope: normalizedEnvelope,
        aad,
      });

      if (providerPlain) return providerPlain;
    } catch {
      // Fall through to legacy fallback below.
    }

    const devPlain = decodeDevPlainEnvelopeForWeb(cipherText, iv, envelope);
    if (devPlain) return devPlain;

    if (!cipherText || !iv) return "[encrypted message]";

    // Legacy AES-GCM placeholder fallback for older web-only rows.
    try {
      const key = await getOrCreateLocalKey();
      const plainBuf = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: unb64(iv) },
        key,
        unb64(cipherText)
      );
      return new TextDecoder().decode(plainBuf);
    } catch {
      return "[encrypted message]";
    }
  }

  function isScrolledNearBottom() {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }

  function scrollToBottom() {
    const el = scrollRef.current;
    if (!el) return;

    stickToBottomRef.current = true;
    userScrolledAwayRef.current = false;

    const run = () => {
      const next = scrollRef.current;
      if (!next) return;
      next.scrollTop = next.scrollHeight;
      lastSeenMessageCountRef.current = messages.length;
      setUnreadCount(0);
    };

    run();
    requestAnimationFrame(run);
  }

  function scrollToBottomSoon() {
    stickToBottomRef.current = true;

    requestAnimationFrame(() => scrollToBottom());
    window.setTimeout(scrollToBottom, 0);
    window.setTimeout(scrollToBottom, 80);
    window.setTimeout(scrollToBottom, 220);
    window.setTimeout(scrollToBottom, 520);
    window.setTimeout(scrollToBottom, 900);
  }

    const lastInboxLoadAtRef = useRef<Record<string, number>>({});
  const inboxLoadInFlightRef = useRef<Record<string, boolean>>({});
  const pendingInboxRefreshRef = useRef<Record<string, boolean>>({});
  const mediaRequestsBackoffUntilRef = useRef<Record<string, number>>({});
  const realtimeStreamConversationRef = useRef<string>("");
  const realtimeStreamStartedRef = useRef(false);
  const senderNameByUserIdRef = useRef<Map<string, string>>(new Map());

  function buildWebMediaRow(item: any): UiMessage | null {
    const manifest =
      item?.manifestJson ||
      item?.manifest_json ||
      item?.encryptedManifest ||
      {};

    const mediaId = String(
      item?.mediaId ||
      item?.media_id ||
      item?.mediaMessageId ||
      item?.media_message_id ||
      manifest?.mediaId ||
      manifest?.media_id ||
      ""
    ).trim();

    if (!mediaId) return null;

    const mimeType = String(
      item?.mimeType ||
      item?.mime_type ||
      manifest?.mimeType ||
      manifest?.mime_type ||
      ""
    );

    const mediaKind = String(
      item?.mediaKind ||
      item?.media_kind ||
      manifest?.mediaKind ||
      manifest?.kind ||
      "file"
    ).toLowerCase();

    const senderUserId = String(
      item?.senderUserId ||
      item?.sender_user_id ||
      ""
    );

    const createdAt = String(
      item?.createdAt ||
      item?.created_at ||
      manifest?.createdAt ||
      manifest?.created_at ||
      new Date().toISOString()
    );

    return {
      id: `media:${mediaId}`,
      clientMessageId: `media:${mediaId}`,
      mediaId,
      media_id: mediaId,
      body:
        mediaKind === "image" || mimeType.startsWith("image/")
          ? "Shared encrypted image"
          : mediaKind === "video" || mimeType.startsWith("video/")
            ? "Shared encrypted video"
            : "Shared encrypted media",
      createdAt,
      senderUserId,
      fromId: senderUserId,
      toId: String(
        item?.recipientUserId ||
        item?.recipient_user_id ||
        ""
      ),
      senderDisplayName: mediaSenderDisplayName(item, senderUserId),
      deliveryState: "server",
      messageKind: "media",
      timelineKind: "media",
      mediaItem: {
        ...item,
        mediaId,
        media_id: mediaId,
        mimeType,
        mime_type: mimeType,
        mediaKind,
        media_kind: mediaKind,
        visibilityMode: item?.visibilityMode || item?.visibility_mode || "",
        visibility_mode: item?.visibility_mode || item?.visibilityMode || "",
        allowSave: item?.allowSave ?? item?.allow_save,
        allow_save: item?.allow_save ?? item?.allowSave,
        allowShare: item?.allowShare ?? item?.allow_share,
        allow_share: item?.allow_share ?? item?.allowShare,
        allowThumbnail: item?.allowThumbnail ?? item?.allow_thumbnail,
        allow_thumbnail: item?.allow_thumbnail ?? item?.allowThumbnail,
        blobStatus: item?.blobStatus || item?.blob_status || "",
        blob_status: item?.blob_status || item?.blobStatus || "",
        blobDeletedAt: item?.blobDeletedAt || item?.blob_deleted_at || null,
        blob_deleted_at: item?.blob_deleted_at || item?.blobDeletedAt || null,
        blobDeleteReason: item?.blobDeleteReason || item?.blob_delete_reason || "",
        blob_delete_reason: item?.blob_delete_reason || item?.blobDeleteReason || "",
        viewedByMe: item?.viewedByMe ?? item?.viewed_by_me ?? null,
        viewed_by_me: item?.viewed_by_me ?? item?.viewedByMe ?? null,
        viewCompletedAt: item?.viewCompletedAt || item?.view_completed_at || null,
        view_completed_at: item?.view_completed_at || item?.viewCompletedAt || null,
      },
    } as UiMessage;
  }


  function rememberSenderName(userIdValue: any, nameValue: any) {
    const userId = String(userIdValue || "").trim();
    const name = String(nameValue || "").trim();

    if (
      !userId ||
      !name ||
      name === "Member" ||
      name === "Unknown" ||
      name === "undefined" ||
      name === "null"
    ) {
      return;
    }

    senderNameByUserIdRef.current.set(userId, name);
  }

  function mediaSenderDisplayName(item: any, senderUserId: string) {
    const explicit = String(
      item?.senderDisplayName ||
      item?.sender_display_name ||
      item?.senderUsername ||
      item?.sender_username ||
      item?.senderName ||
      item?.sender_name ||
      item?.fromDisplayName ||
      item?.from_display_name ||
      item?.fromUsername ||
      item?.from_username ||
      item?.username ||
      ""
    ).trim();

    if (explicit) {
      rememberSenderName(senderUserId, explicit);
      return explicit;
    }

    const remembered = senderNameByUserIdRef.current.get(String(senderUserId || "").trim());
    if (remembered) return remembered;

    return senderUserId === String(currentUserId || "") ? "You" : "Member";
  }

  async function refreshMediaRowsOnly(source = "media_refresh") {
    const refreshConversationId = String(conversationId || "").trim();
    if (!refreshConversationId) return;

    const existing =
      mediaRowsRefreshInFlightRef.current[refreshConversationId];

    if (existing) {
      mediaRowsRefreshQueuedSourceRef.current[refreshConversationId] = source;
      return existing;
    }

    const now = Date.now();
    const lastStartedAt = Number(
      mediaRowsRefreshLastAtRef.current[refreshConversationId] || 0
    );
    const ageMs = now - lastStartedAt;

    if (ageMs < MEDIA_ROWS_REFRESH_DEDUPE_MS) {
      mediaRowsRefreshQueuedSourceRef.current[refreshConversationId] = source;

      if (!mediaRowsRefreshQueuedTimerRef.current[refreshConversationId]) {
        const delay = Math.max(
          50,
          MEDIA_ROWS_REFRESH_DEDUPE_MS - ageMs
        );

        mediaRowsRefreshQueuedTimerRef.current[refreshConversationId] =
          window.setTimeout(() => {
            delete mediaRowsRefreshQueuedTimerRef.current[
              refreshConversationId
            ];

            const queuedSource =
              mediaRowsRefreshQueuedSourceRef.current[
                refreshConversationId
              ] || "media_refresh_queued";

            delete mediaRowsRefreshQueuedSourceRef.current[
              refreshConversationId
            ];

            void refreshMediaRowsOnly(queuedSource);
          }, delay);
      }

      return;
    }

    mediaRowsRefreshLastAtRef.current[refreshConversationId] = now;

    const refreshPromise = (async () => {
      const wasNearBottom = isScrolledNearBottom();
      const shouldFollow =
        wasNearBottom ||
        stickToBottomRef.current ||
        source === "send" ||
        source === "sse_own_echo_media";

      try {
        const mediaItems = await listMediaManifestsWeb(
          refreshConversationId
        );

        const mediaRows = (
          Array.isArray(mediaItems) ? mediaItems : []
        )
          .map((item: any) => buildWebMediaRow(item))
          .filter(Boolean) as UiMessage[];

        setMessages((old: UiMessage[]) => {
          const byId = new Map<string, UiMessage>();
          const beforeIds = new Set<string>();
          let addedMediaRows = 0;

          for (const row of old || []) {
            const key = String(row.id || row.clientMessageId);
            beforeIds.add(key);
            byId.set(key, row);
          }

          for (const row of mediaRows) {
            const mediaId = webMediaStableId(
              row.mediaItem || row,
              row.clientMessageId || row.id
            );

            if (mediaId && webDeletedMediaById[mediaId]) continue;

            const key = String(row.id || row.clientMessageId);

            if (!beforeIds.has(key)) {
              addedMediaRows += 1;
            }

            byId.set(key, row);
          }

          if (addedMediaRows > 0 && shouldFollow) {
            webMediaAutoFollowPendingRef.current = true;
            webMediaUnreadPendingRef.current = 0;
          } else if (addedMediaRows > 0) {
            webMediaUnreadPendingRef.current += addedMediaRows;
          }

          return Array.from(byId.values()).sort(
            (a: any, b: any) => {
              const aa = new Date(a.createdAt || 0).getTime();
              const bb = new Date(b.createdAt || 0).getTime();
              return aa - bb;
            }
          );
        });

        debugWebMessagesLog(
          "[Messages web] media rows refreshed directly",
          {
            conversationId: refreshConversationId,
            source,
            count: mediaRows.length,
            shouldFollow,
            last: mediaRows.slice(-3).map((row: any) => ({
              id: row.id,
              mediaId: row.mediaId,
              createdAt: row.createdAt,
            })),
          }
        );
      } catch (e) {
        console.warn(
          "[Messages web] direct media refresh failed",
          e
        );
      }
    })();

    mediaRowsRefreshInFlightRef.current[refreshConversationId] =
      refreshPromise;

    try {
      await refreshPromise;
    } finally {
      if (
        mediaRowsRefreshInFlightRef.current[refreshConversationId] ===
        refreshPromise
      ) {
        delete mediaRowsRefreshInFlightRef.current[
          refreshConversationId
        ];
      }

      const queuedSource =
        mediaRowsRefreshQueuedSourceRef.current[
          refreshConversationId
        ];

      if (
        queuedSource &&
        !mediaRowsRefreshQueuedTimerRef.current[
          refreshConversationId
        ]
      ) {
        delete mediaRowsRefreshQueuedSourceRef.current[
          refreshConversationId
        ];

        mediaRowsRefreshQueuedTimerRef.current[
          refreshConversationId
        ] = window.setTimeout(() => {
          delete mediaRowsRefreshQueuedTimerRef.current[
            refreshConversationId
          ];

          void refreshMediaRowsOnly(queuedSource);
        }, MEDIA_ROWS_REFRESH_DEDUPE_MS);
      }
    }
  }




  const webVisibleReadIdsRef = useRef<Set<string>>(new Set());

  function markVisibleRemoteTextRowsRead(rowsForRead: UiMessage[], source = "web-visible-after-load") {
    const messagesPageIsActive = () => {
      if (document.visibilityState !== "visible") return false;
      if (typeof document.hasFocus === "function" && !document.hasFocus()) return false;

      const pathname = String(window.location.pathname || "").replace(/\/+$/, "");
      return pathname === "/messages" || pathname.endsWith("/messages");
    };

    if (!conversationId || !currentUserId) return;
    if (!messagesPageIsActive()) return;

    const ids = rowsForRead
      .filter((msg: any) => {
        const sender = String(msg?.senderUserId || msg?.sender_user_id || msg?.fromUserId || msg?.from_user_id || "");
        if (!sender || sender === String(currentUserId || "")) return false;

        const status = String(msg?.deliveryState || msg?.delivery_state || "").toLowerCase();
        const readCount = Number(msg?.readByCount || msg?.read_by_count || 0);
        if (status === "read" || readCount > 0) return false;

        return true;
      })
      .map((msg: any) => Number(msg?.id || msg?.messageId || msg?.message_id || msg?.backendMessageId || msg?.backend_message_id || 0))
      .filter((id: number) => Number.isFinite(id) && id > 0)
      .filter((id: number) => {
        const key = `${conversationId}:${id}`;
        return !webVisibleReadIdsRef.current.has(key);
      });

    if (!ids.length) return;

    window.setTimeout(async () => {
      if (!messagesPageIsActive()) return;

      try {
        await readReceiptsE2EE(conversationId, ids, {
          realtime: true,
          source,
        });

        for (const id of ids) {
          webVisibleReadIdsRef.current.add(`${conversationId}:${id}`);
        }
      } catch (e) {
        console.warn("[Messages web] visible read receipt failed", e);
      }
    }, 2200);
  }

async function loadMessages(options: boolean | {
  force?: boolean;
  realtime?: boolean;
  source?: string;
  beforeId?: string | number | null;
  appendOlder?: boolean;
} = false) {
    const loadOptions =
      typeof options === "boolean"
        ? { force: options }
        : (options || {});

    const force = !!loadOptions.force;

    if (Date.now() < webInboxBackoffUntil) {
      debugWebMessagesLog("[Messages web] loadMessages skipped backoff", {
        conversationId,
        force,
        source: loadOptions.source,
        realtime: loadOptions.realtime,
        webInboxBackoffUntil,
        now: Date.now(),
      });
      return;
    }

    if (!conversationId) return;

    debugWebMessagesLog("[Messages web] loadMessages start", {
      conversationId,
      force,
      realtime: !!loadOptions.realtime,
      source: loadOptions.source || "",
    });

    const inboxThrottleKey = String(conversationId || "");
    const inboxNow = Date.now();

    const loadOptionsForThrottle =
      typeof options !== "undefined" && options
        ? options
        : {};

    const inboxForce = Boolean((loadOptionsForThrottle as any).force);
    const inboxSource = String((loadOptionsForThrottle as any).source || "");
    const inboxRealtime =
      Boolean((loadOptionsForThrottle as any).realtime) ||
      inboxSource === "sse" ||
      inboxSource === "send" ||
      inboxSource === "initial" ||
      inboxSource === "queued";

    // Only true realtime/send/initial/queued refreshes bypass the local inbox throttle.
    // Bare force=true with source="" is usually focus/fallback noise and should be throttled.
    const inboxShouldBypassLocalThrottle =
      inboxSource === "send" ||
      inboxSource === "sse" ||
      inboxSource === "initial" ||
      inboxSource === "queued" ||
      inboxSource === "older" ||
      Boolean((loadOptionsForThrottle as any).realtime);

    if (inboxLoadInFlightRef.current[inboxThrottleKey]) {
      if (inboxShouldBypassLocalThrottle) {
        pendingInboxRefreshRef.current[inboxThrottleKey] = true;
      }

      debugWebMessagesLog("[Messages web] loadMessages skipped in-flight", {
        conversationId,
        force: inboxForce,
        realtime: inboxRealtime,
        source: inboxSource,
        queued: inboxShouldBypassLocalThrottle,
      });
      return;
    }

    if (
      !inboxShouldBypassLocalThrottle &&
      inboxNow - Number(lastInboxLoadAtRef.current[inboxThrottleKey] || 0) < 10000
    ) {
      debugWebMessagesLog("[Messages web] loadMessages skipped local inbox throttle", {
        conversationId,
        force: inboxForce,
        realtime: inboxRealtime,
        source: inboxSource,
        ageMs: inboxNow - Number(lastInboxLoadAtRef.current[inboxThrottleKey] || 0),
      });
      return;
    }

    inboxLoadInFlightRef.current[inboxThrottleKey] = true;
    lastInboxLoadAtRef.current[inboxThrottleKey] = inboxNow;

    setLoading(true);
    setError(null);

    try {
      const isOlderPageLoad = !!loadOptions.appendOlder || !!loadOptions.beforeId;
      const previousScrollElement = scrollRef.current;
      const previousScrollHeight = previousScrollElement?.scrollHeight || 0;
      const previousScrollTop = previousScrollElement?.scrollTop || 0;

      const res = await listMessagesE2EE(
        conversationId,
        {
          limit: 100,
          beforeId: loadOptions.beforeId || null,
          ...(loadOptions.realtime
            ? { realtime: true, source: loadOptions.source || "sse" }
            : {}),
        }
      );
      const remote = Array.isArray(res?.items) ? res.items : [];

      debugWebMessagesLog("[Messages web] listMessagesE2EE response", {
        conversationId,
        count: remote.length,
        last: remote.slice(-3).map((x: any) => ({
          id: x.id,
          clientMessageId: x.clientMessageId,
          senderUserId: x.senderUserId || x.fromUserId,
          recipientUserId: x.recipientUserId || x.toUserId,
          iv: x.iv || x.envelope?.nonce,
        })),
      });

      const rows: UiMessage[] = [];

      for (const m of remote) {
        const envelope = (m as any).envelope || {};
        const cipherText = String((m as any).cipherText || envelope.ciphertext || "");
        const iv = String((m as any).iv || envelope.nonce || "");
        const createdAt = String((m as any).createdAt || (m as any).sentAt || new Date().toISOString());
        const clientMessageId = (m as any).clientMessageId || null;
        const senderUserId = String((m as any).senderUserId || (m as any).fromUserId || "");

        const plainBody = await decryptIncomingEnvelope(cipherText, iv, envelope, {
          conversationId,
          clientMessageId: String(clientMessageId || ""),
          senderUserId,
          recipientScope: `conversation:${conversationId}`,
          version: 2,
        });

        rows.push({
          id: String((m as any).id),
          clientMessageId,
          body: plainBody,
          createdAt,
          senderUserId,
          senderDisplayName: (m as any).senderDisplayName || "Member",
          deliveryState: String((m as any).deliveryState || (clientMessageId ? "server" : "sent")) as UiMessage["deliveryState"],
          messageKind: plainBody.startsWith("* ") ? "action" : "text",
          readByCount: Number((m as any).readByCount || (m as any).read_by_count || 0),
        } as UiMessage);
      }

      const previousCount = lastSeenMessageCountRef.current || messages.length;
      const wasNearBottom = isScrolledNearBottom();

      rows.sort((a, b) => {
        const aa = new Date(a.createdAt || 0).getTime();
        const bb = new Date(b.createdAt || 0).getTime();
        return aa - bb;
      });

      for (const row of rows as any[]) {
        rememberSenderName(
          row?.senderUserId || row?.sender_user_id || row?.fromId || row?.from_id,
          row?.senderDisplayName ||
            row?.sender_display_name ||
            row?.fromDisplayName ||
            row?.from_display_name ||
            row?.senderUsername ||
            row?.sender_username ||
            row?.username
        );
      }

      debugWebMessagesLog("[Messages web] setMessages", {
        conversationId,
        count: rows.length,
        last: rows.slice(-3).map((x) => ({
          id: x.id,
          clientMessageId: x.clientMessageId,
          senderUserId: x.senderUserId,
          body: x.body,
        })),
      });

      try {
        const mediaItems = await listMediaManifestsWeb(conversationId);

        for (const item of mediaItems) {
          const mediaRow = buildWebMediaRow(item);
          if (mediaRow) rows.push(mediaRow);
        }

        rows.sort((a, b) => {
          const aa = new Date(a.createdAt || 0).getTime();
          const bb = new Date(b.createdAt || 0).getTime();
          return aa - bb;
        });
      } catch (e) {
        console.warn("[Messages web] media manifest load failed", e);
      }

      let visibleRows = rows;

      try {
        if (!webApiBackoffActive('delete-events')) {
          const deleteEvents = await listMessageDeleteEvents(conversationId);

          const deletedForEveryone = new Set(
            deleteEvents
              .filter((ev: any) => String(ev?.scope || "") === "everyone")
              .map((ev: any) => `${ev?.itemKind || ev?.item_kind}:${ev?.itemId || ev?.item_id}`)
          );

          visibleRows = rows.filter((m: any) => {
            const textId = String(
              m?.clientMessageId ||
              m?.client_message_id ||
              m?.messageId ||
              m?.message_id ||
              m?.id ||
              ""
            ).trim();

            return !(textId && deletedForEveryone.has(`text:${textId}`));
          });
        }
      } catch (e) {
        markWebApiBackoff('delete-events', e, 30000);
        console.warn("[Messages web] delete-events filter failed", e);
      }

      const finalVisibleRows = visibleRows.filter((row: any) => {
        if (row?.timelineKind !== "media") return true;

        const mediaId = webMediaStableId(
          row?.mediaItem || row,
          row?.clientMessageId || row?.id
        );

        return !(mediaId && webDeletedMediaById[mediaId]);
      });

      const isInitialLoad = loadOptions.source === "initial";

      if (isInitialLoad) {
        initialInboxMediaCountByConversationRef.current[
          conversationId
        ] = finalVisibleRows.reduce(
          (count: number, row: any) =>
            count + (row?.timelineKind === "media" ? 1 : 0),
          0
        );

        debugWebMessagesLog(
          "[Messages web] recorded initial inbox media rows",
          {
            conversationId,
            mediaCount:
              initialInboxMediaCountByConversationRef.current[
                conversationId
              ],
          }
        );
      }

      const responseHasMore = !!((res as any)?.hasMore ?? (res as any)?.has_more);
      const responseNextBeforeId =
        (res as any)?.nextBeforeId ??
        (res as any)?.next_before_id ??
        null;

      setOlderMessagesHasMore(responseHasMore);
      setOlderMessagesBeforeId(responseNextBeforeId);

      setMessages((currentRows) => {
        if (!isOlderPageLoad) return finalVisibleRows;

        const byKey = new Map<string, UiMessage>();

        const keyForRow = (row: any) => {
          if (row?.timelineKind === "media") {
            const mediaId = String(
              row?.mediaItem?.mediaId ||
              row?.mediaItem?.media_id ||
              row?.mediaId ||
              row?.media_id ||
              row?.clientMessageId ||
              row?.id ||
              ""
            ).trim();

            return `media:${mediaId}`;
          }

          return `text:${String(
            row?.id ||
            row?.clientMessageId ||
            row?.client_message_id ||
            ""
          ).trim()}`;
        };

        for (const row of finalVisibleRows) {
          const key = keyForRow(row);
          if (key !== "text:" && key !== "media:") byKey.set(key, row);
        }

        for (const row of currentRows) {
          const key = keyForRow(row);
          if (key !== "text:" && key !== "media:" && !byKey.has(key)) {
            byKey.set(key, row);
          }
        }

        return Array.from(byKey.values()).sort((a, b) => {
          const aa = new Date(a.createdAt || 0).getTime();
          const bb = new Date(b.createdAt || 0).getTime();
          return aa - bb;
        });
      });

      if (isOlderPageLoad && previousScrollElement) {
        window.setTimeout(() => {
          const nextScrollHeight = previousScrollElement.scrollHeight || 0;
          previousScrollElement.scrollTop =
            nextScrollHeight - previousScrollHeight + previousScrollTop;
        }, 0);
      }

      markVisibleRemoteTextRowsRead(
        finalVisibleRows,
        isOlderPageLoad ? "web-visible-after-older-load" : "web-visible-after-load"
      );

      const mayAutoFollow =
        !userScrolledAwayRef.current && (force || stickToBottomRef.current);

      if (!isOlderPageLoad && (isInitialLoad || mayAutoFollow)) {
        scrollToBottomSoon();
      }

      setAckedClientMessageIds((prev) => {
        const next = new Set(prev);
        for (const row of rows) {
          if (row.clientMessageId) next.add(row.clientMessageId);
        }
        return next;
      });

      const hasNewMessages = rows.length > previousCount;
      const shouldAutoScroll =
        !userScrolledAwayRef.current && (wasNearBottom || stickToBottomRef.current);

      if (!isOlderPageLoad && shouldAutoScroll) {
        setUnreadCount(0);
        lastSeenMessageCountRef.current = rows.length;
        scrollToBottomSoon();
      } else if (!isOlderPageLoad && hasNewMessages) {
        setUnreadCount((prev) => Math.max(prev, 0) + (rows.length - previousCount));
      }
    } catch (e: any) {
      if (isProbably429Error(e)) {
        const retryAfterMs = getRetryAfterMsFromError(e);
        webInboxBackoffUntil = Date.now() + retryAfterMs;
        console.warn("Inbox polling throttled; backing off", { retryAfterMs });
        return;
      }

      setError(e?.message || "Could not load messages.");
    } finally {
      delete inboxLoadInFlightRef.current[inboxThrottleKey];

      if (pendingInboxRefreshRef.current[inboxThrottleKey]) {
        delete pendingInboxRefreshRef.current[inboxThrottleKey];
        window.setTimeout(() => {
          void loadMessages({ force: true, realtime: true, source: "queued" });
        }, 250);
      }


      setLoading(false);
    }
  }

  useEffect(() => {
    if (!conversationId) return;

    let cancelled = false;
    let inboxTimer: number | null = null;
    let mediaTimer: number | null = null;
    let directMediaRowsTimer: number | null = null;
    let inboxInFlight = false;
    let mediaInFlight = false;

    function clearTimers() {
      if (inboxTimer) window.clearTimeout(inboxTimer);
      if (mediaTimer) window.clearTimeout(mediaTimer);
      if (directMediaRowsTimer) window.clearTimeout(directMediaRowsTimer);
      inboxTimer = null;
      mediaTimer = null;
      directMediaRowsTimer = null;
    }

    async function pollInboxOnce() {
      if (cancelled || !shouldRunMessagesFallbackPoll()) return;
      if (inboxInFlight) return;

      inboxInFlight = true;
      try {
        await loadMessages({ force: false, realtime: false, source: "poll" });
      } finally {
        inboxInFlight = false;
      }
    }

    async function pollMediaOnce() {
      if (cancelled || !shouldRunMessagesFallbackPoll()) return;
      if (mediaInFlight) return;

      // Do not poll media requests in the background when the media panel is closed.
      // Message realtime uses SSE; media request polling is only needed when the UI is visible
      // or when active media requests are already displayed.
      if (!mediaOpen && !mediaRequests.length) return;

      mediaInFlight = true;
      try {
        await refreshMediaRequests();
      } finally {
        mediaInFlight = false;
      }
    }

    function scheduleInbox() {
      if (cancelled || !WEB_MESSAGES_FALLBACK_ENABLED) return;

      if (webMessagesRealtimeConnected) {
        debugWebMessagesLog("[Messages web] fallback inbox poll skipped; SSE connected", { conversationId });
        return;
      }

      const delay = clampMessagePollMs(
        WEB_MESSAGES_INBOX_POLL_MS,
        60_000
      );

      inboxTimer = window.setTimeout(async () => {
        await pollInboxOnce();
        scheduleInbox();
      }, delay);
    }

    function scheduleMedia() {
      if (cancelled || !WEB_MESSAGES_FALLBACK_ENABLED) return;

      // Avoid recurring /messages/media-requests polling unless the media UI needs it.
      if (!mediaOpen && !mediaRequests.length) return;

      const delay = clampMessagePollMs(
        WEB_MESSAGES_MEDIA_POLL_MS,
        90_000
      );

      mediaTimer = window.setTimeout(async () => {
        await pollMediaOnce();
        scheduleMedia();
      }, delay);
    }

    function scheduleDirectMediaRowsRefresh() {
      if (mediaRowsFallbackTimerRef.current) return;

      mediaRowsFallbackTimerRef.current = window.setTimeout(() => {
        mediaRowsFallbackTimerRef.current = null;
        if (cancelled) return;

        const initialMediaCount = Number(
          initialInboxMediaCountByConversationRef.current[
            conversationId
          ] || 0
        );

        // The combined inbox already supplied the existing media timeline.
        // New/two-phase media is refreshed by the explicit SSE handlers.
        if (initialMediaCount > 0) {
          debugWebMessagesLog(
            "[Messages web] media rows fallback skipped",
            {
              conversationId,
              reason: "initial_inbox_has_media",
              initialMediaCount,
            }
          );
          return;
        }

        void refreshMediaRowsOnly("media_rows_fallback");
      }, 2500);
    }

    async function startRuntimeFallbackPolling() {
      await loadMessagePollingRuntimeSettings();

      if (cancelled || !WEB_MESSAGES_FALLBACK_ENABLED) return;

      // Initial refresh happens once on conversation change.
      // Scheduled fallback polls then use admin-configured runtime intervals.
      scheduleInbox();
      scheduleMedia();
      scheduleDirectMediaRowsRefresh();
    }

let realtimeReconnectTimer: number | null = null;
    let realtimeRestartCount = 0;

    let realtimeRefreshTimer: number | null = null;
    let realtimeMediaFollowupTimer: number | null = null;

    function scheduleRealtimeRefresh() {
      if (realtimeRefreshTimer) {
        window.clearTimeout(realtimeRefreshTimer);
        realtimeRefreshTimer = null;
      }

      if (realtimeMediaFollowupTimer) {
        window.clearTimeout(realtimeMediaFollowupTimer);
        realtimeMediaFollowupTimer = null;
      }

      // One combined inbox refresh is enough for text plus the currently
      // available media manifest rows.
      realtimeRefreshTimer = window.setTimeout(() => {
        realtimeRefreshTimer = null;
        if (cancelled) return;

        void loadMessages({
          force: true,
          realtime: true,
          source: 'sse',
        });
      }, 250);

      // Media upload is two-phase. Recheck only the media manifests after the
      // blob is likely ready; do not fetch/decrypt the entire text inbox again.
      realtimeMediaFollowupTimer = window.setTimeout(() => {
        realtimeMediaFollowupTimer = null;
        if (cancelled) return;

        void refreshMediaRowsOnly('sse_media_followup');
      }, 1800);
    }

        let ownedRealtimeAbort: AbortController | null = null;
function clearRealtimeReconnect() {
      if (realtimeReconnectTimer) {
        window.clearTimeout(realtimeReconnectTimer);
        realtimeReconnectTimer = null;
      }
    }


  function handleTypingRealtimeEvent(event: MessageRealtimeEvent) {
    if (!event || String(event.conversationId || "") !== String(conversationId)) return;

    const userId = String(event.userId || event.user_id || event.senderUserId || event.fromUserId || "");
    if (!userId || userId === String(currentUserId || "")) return;

    const label = String(event.displayName || event.display_name || event.senderDisplayName || "").trim() || `Member ${userId}`;

    setTypingUsersById((old) => ({
      ...old,
      [userId]: {
        label,
        until: Date.now() + 6000,
      },
    }));

    if (typingClearTimerRef.current) window.clearTimeout(typingClearTimerRef.current);
    typingClearTimerRef.current = window.setTimeout(() => {
      const now = Date.now();
      setTypingUsersById((old) => {
        const next: Record<string, { label: string; until: number }> = {};
        for (const [id, value] of Object.entries(old)) {
          if (value.until > now) next[id] = value;
        }
        return next;
      });
    }, 6500);
  }


    function realtimeEventMessageIdSet(event: any) {
      return new Set(
        [
          ...(Array.isArray(event?.messageIds) ? event.messageIds : []),
          ...(Array.isArray(event?.message_ids) ? event.message_ids : []),
          ...(Array.isArray(event?.clientMessageIds) ? event.clientMessageIds : []),
          ...(Array.isArray(event?.client_message_ids) ? event.client_message_ids : []),
          event?.messageId,
          event?.message_id,
          event?.clientMessageId,
          event?.client_message_id,
          event?.id,
        ]
          .filter((x) => x !== null && x !== undefined)
          .map((x) => String(x).replace(/^remote:/, '').trim())
          .filter(Boolean)
      );
    }

    function applyMessageStatusRealtimeEvent(event: any) {
      const ids = realtimeEventMessageIdSet(event);
      if (!ids.size) return false;

      const status = String(
        event?.deliveryState ||
        event?.delivery_state ||
        (event?.readReceipt || event?.read_receipt || event?.reason === 'read_receipt' ? 'read' : '')
      ).toLowerCase();

      if (!status) return false;

      setMessages((old: any[]) =>
        old.map((msg: any) => {
          const candidates = [
            msg?.id,
            msg?.remoteId,
            msg?.messageId,
            msg?.message_id,
            msg?.backendMessageId,
            msg?.backend_message_id,
            msg?.clientMessageId,
            msg?.client_message_id,
            msg?.clientId,
            msg?.client_id,
          ]
            .filter((x) => x !== null && x !== undefined)
            .map((x) => String(x).replace(/^remote:/, '').trim());

          if (!candidates.some((id) => ids.has(id))) return msg;

          return {
            ...msg,
            deliveryState: status,
            delivery_state: status,
            readByCount: status === 'read' ? Math.max(1, Number(msg?.readByCount || 0)) : msg?.readByCount,
            read_by_count: status === 'read' ? Math.max(1, Number(msg?.read_by_count || 0)) : msg?.read_by_count,
          };
        })
      );

      return true;
    }

    function handleRealtimeEvent(event: MessageRealtimeEvent) {
    if (
      (event as any)?.readReceipt === true ||
      (event as any)?.read_receipt === true ||
      (event as any)?.reason === 'read_receipt'
    ) {
      applyMessageStatusRealtimeEvent({
        ...(event as any),
        deliveryState: 'read',
      });
      scheduleRealtimeRefresh();
      return;
    }

      if (cancelled) return;

      debugWebMessagesLog("[Messages web] SSE event", {
        activeConversationId: conversationId,
        event,
      });

      if (!event || String(event.conversationId || '') !== String(conversationId)) return;

      if (event.type === 'message:typing') {
        handleTypingRealtimeEvent(event);
        return;
      }

      if (event.type === 'connected') {
        debugWebMessagesLog("[Messages web] SSE connected", { conversationId });
        webMessagesRealtimeConnected = true;
        clearTimers();
        return;
      }

      const realtimeEventType = String(
        event.type ||
        (event as any).eventType ||
        (event as any).payloadType ||
        (event as any).payload?.type ||
        ''
      );

      if (
        realtimeEventType === 'message:read' ||
        realtimeEventType === 'read:receipt' ||
        (
          realtimeEventType === 'message:updated' &&
          String((event as any)?.deliveryState || (event as any)?.delivery_state || '').toLowerCase() === 'read'
        )
      ) {
        applyMessageStatusRealtimeEvent(
          event as any,
        );
        scheduleRealtimeRefresh();
        return;
      }

      if (
        realtimeEventType === 'message:new' ||
        realtimeEventType === 'media:new' ||
        realtimeEventType === 'media:manifest' ||
        realtimeEventType === 'media:ready' ||
        realtimeEventType === 'media:viewed' ||
        realtimeEventType === 'message:media' ||
        realtimeEventType === 'media:deleted' ||
        realtimeEventType === 'media:delete' ||
        realtimeEventType === 'message:media_deleted' ||
        realtimeEventType === 'media_request:changed'
      ) {
        const eventSenderUserId = String(event.senderUserId || event.fromUserId || "");
        const eventClientMessageId = String(event.clientMessageId || "");

        // Even own echoes can carry/precede media manifest availability.
        // Keep the delayed reload, but log it separately so we can see it.
        if (
          realtimeEventType === 'message:new' &&
          eventSenderUserId &&
          eventSenderUserId === String(currentUserId || "")
        ) {
          debugWebMessagesLog("[Messages web] SSE own echo media refresh scheduled", {
            conversationId,
            clientMessageId: eventClientMessageId,
          });

          void refreshMediaRowsOnly('sse_own_echo_media');
          return;
        }

        debugWebMessagesLog("[Messages web] SSE refresh requested", {
          conversationId,
          type: realtimeEventType,
          clientMessageId: event.clientMessageId,
        });

        if (
          realtimeEventType === 'media:deleted' ||
          realtimeEventType === 'media:delete' ||
          realtimeEventType === 'message:media_deleted'
        ) {
          const deletedMediaId = String(
            (event as any)?.mediaId ||
              (event as any)?.media_id ||
              (event as any)?.itemId ||
              (event as any)?.item_id ||
              ""
          ).trim();

          if (deletedMediaId) {
            setWebDeletedMediaById((old) => ({
              ...old,
              [deletedMediaId]: '1',
            }));

            setMessages((old: UiMessage[]) =>
              (old || []).filter((row: any) => {
                const rowMediaId = webMediaStableId(
                  row?.mediaItem || row,
                  row?.clientMessageId || row?.id
                );
                return rowMediaId !== deletedMediaId;
              })
            );

            setWebMediaThumbById((old) => {
              if (!old?.[deletedMediaId]) return old;
              const next = { ...old };
              delete next[deletedMediaId];
              return next;
            });

            setWebMediaPreview((current: any) => {
              const currentMediaId = String(
                current?.mediaId || current?.media_id || current?.id || ""
              ).trim();
              return currentMediaId === deletedMediaId ? null : current;
            });
          }

          void refreshMediaRowsOnly(`sse_${realtimeEventType.replace(/[^a-z0-9_]+/gi, "_")}`);
          return;
        }

        if (
          realtimeEventType === 'media:manifest' ||
          realtimeEventType === 'media:ready' ||
          realtimeEventType === 'media:viewed' ||
          realtimeEventType === 'media:new' ||
          realtimeEventType === 'message:media'
        ) {
          if (
            realtimeEventType === 'media:ready' ||
            realtimeEventType === 'media:viewed'
          ) {
            const eventMediaId = String(
              (event as any)?.mediaId ||
              (event as any)?.media_id ||
              (event as any)?.mediaMessageId ||
              (event as any)?.media_message_id ||
              ''
            ).replace(/^media:/, '').trim();

            const eventState =
              realtimeEventType === 'media:viewed'
                ? 'read'
                : 'received';

            if (eventMediaId) {
              setMessages((old: UiMessage[]) =>
                (old || []).map((row: any) => {
                  const rowMediaId = String(
                    webMediaStableId(
                      row?.mediaItem || row,
                      row?.clientMessageId || row?.id
                    ) || ''
                  ).replace(/^media:/, '').trim();

                  if (rowMediaId !== eventMediaId) return row;

                  return {
                    ...row,
                    deliveryState: eventState,
                    delivery_state: eventState,
                    effectiveDeliveryState: eventState,
                    effective_delivery_state: eventState,
                    mediaItem: row?.mediaItem
                      ? {
                          ...row.mediaItem,
                          deliveryState: eventState,
                          delivery_state: eventState,
                          effectiveDeliveryState: eventState,
                          effective_delivery_state: eventState,
                        }
                      : row?.mediaItem,
                  };
                })
              );
            }
          }

          if (realtimeEventType !== 'media:viewed') {
            void refreshMediaRowsOnly(
              `sse_${realtimeEventType.replace(/[^a-z0-9_]+/gi, "_")}`
            );
          }

          return;
        }

        scheduleRealtimeRefresh();

        if (realtimeEventType === 'media_request:changed') {
          void refreshMediaRequests(true);
        }
      }
    }

    function startRealtimeStream() {
      // Abort stale streams for other conversations only.
      // Do not abort the current conversation stream from React cleanup/rerenders.
      for (const [key, ctrl] of activeMessageRealtimeStreams.entries()) {
        if (key !== conversationId && ctrl && !ctrl.signal.aborted) {
          ctrl.abort();
          activeMessageRealtimeStreams.delete(key);
        }
      }
      if (cancelled) return;

      const now = Date.now();
      const lastStartAt = Number(activeMessageRealtimeStartsAt.get(conversationId) || 0);
      if (now - lastStartAt < 5000) {
        debugWebMessagesLog("[Messages web] SSE start skipped recent duplicate", { conversationId, ageMs: now - lastStartAt });
        return;
      }

      const existing = activeMessageRealtimeStreams.get(conversationId);
      if (existing && !existing.signal.aborted) {
        debugWebMessagesLog("[Messages web] SSE start skipped existing global stream", { conversationId });
        return;
      }

      if (
        realtimeStreamStartedRef.current &&
        realtimeStreamConversationRef.current === conversationId
      ) {
        debugWebMessagesLog("[Messages web] SSE start skipped already active", { conversationId });
        return;
      }

      const streamAbort = new AbortController();

      activeMessageRealtimeStartsAt.set(conversationId, Date.now());
      activeMessageRealtimeStreams.set(conversationId, streamAbort);
      ownedRealtimeAbort = streamAbort;
      realtimeStreamStartedRef.current = true;
      realtimeStreamConversationRef.current = conversationId;

      debugWebMessagesLog("[Messages web] SSE start", { conversationId });

      void streamMessageEvents(conversationId, streamAbort.signal, handleRealtimeEvent)
        .then(() => {
          if (cancelled || streamAbort.signal.aborted) return;

          webMessagesRealtimeConnected = false;
          realtimeStreamStartedRef.current = false;
          if (realtimeStreamConversationRef.current === conversationId) {
            realtimeStreamConversationRef.current = "";
          }
          if (ownedRealtimeAbort === streamAbort && activeMessageRealtimeStreams.get(conversationId) === streamAbort) {
            activeMessageRealtimeStreams.delete(conversationId);
          }

          const delay = Math.min(30000, 1000 * Math.pow(2, realtimeRestartCount++));
          realtimeReconnectTimer = window.setTimeout(startRealtimeStream, delay);
        })
        .catch(() => {
          if (cancelled || streamAbort.signal.aborted) return;

          webMessagesRealtimeConnected = false;
          realtimeStreamStartedRef.current = false;
          if (realtimeStreamConversationRef.current === conversationId) {
            realtimeStreamConversationRef.current = "";
          }
          if (ownedRealtimeAbort === streamAbort && activeMessageRealtimeStreams.get(conversationId) === streamAbort) {
            activeMessageRealtimeStreams.delete(conversationId);
          }

          const delay = Math.min(30000, 1000 * Math.pow(2, realtimeRestartCount++));
          realtimeReconnectTimer = window.setTimeout(startRealtimeStream, delay);
        });
    }
    startRealtimeStream();

    void startRuntimeFallbackPolling();

    function handleVisibilityOrFocus() {
      if (cancelled) return;

      void loadMessagePollingRuntimeSettings().then(() => {
        if (cancelled || !WEB_MESSAGES_FALLBACK_ENABLED) {
          clearTimers();
          return;
        }

        // On focus/visibility return, do one catch-up only when SSE is not connected.
        clearTimers();
        if (!webMessagesRealtimeConnected) {
          void pollInboxOnce();
        }

        if (mediaOpen || mediaRequests.length) {
          void pollMediaOnce();
        }

        scheduleInbox();
        scheduleMedia();
      });
    }

    window.addEventListener("focus", handleVisibilityOrFocus);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);

    return () => {
      const activeStream = activeMessageRealtimeStreams.get(conversationId);
      const keepLiveSseHandler =
        activeStream &&
        !activeStream.signal.aborted &&
        realtimeStreamConversationRef.current === conversationId;

      // If the active SSE stream is still alive for this same conversation, keep
      // this handler closure alive too. Otherwise the stream keeps receiving chunks
      // but handleRealtimeEvent returns early because cancelled=true.
      if (!keepLiveSseHandler) {
        cancelled = true;
        realtimeStreamStartedRef.current = false;
        if (realtimeStreamConversationRef.current === conversationId) {
          realtimeStreamConversationRef.current = "";
        }
      }

      clearTimers();
      clearRealtimeReconnect();

      if (realtimeRefreshTimer) {
        window.clearTimeout(realtimeRefreshTimer);
        realtimeRefreshTimer = null;
      }

      if (realtimeMediaFollowupTimer) {
        window.clearTimeout(realtimeMediaFollowupTimer);
        realtimeMediaFollowupTimer = null;
      }

      const queuedMediaRowsTimer =
        mediaRowsRefreshQueuedTimerRef.current[conversationId];

      if (queuedMediaRowsTimer) {
        window.clearTimeout(queuedMediaRowsTimer);
        delete mediaRowsRefreshQueuedTimerRef.current[conversationId];
      }

      delete mediaRowsRefreshQueuedSourceRef.current[conversationId];

      // Do not abort/delete the live stream here. Stale streams for other
      // conversations are aborted in startRealtimeStream().
      window.removeEventListener("focus", handleVisibilityOrFocus);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
    };
  }, [conversationId, chatMode, visiblePrivateId]);

  useEffect(() => {
    let cancelled = false;

    async function redirectToDefaultChatIfNeeded() {
      if (params.originGroupId || params.originActivityId || params.partnerUserId) return;

      try {
        const res = await listMyGroups();
        if (cancelled) return;

        const items = Array.isArray(res?.items) ? res.items : [];
        if (!items.length) return;

        const partner = items.find((item: any) => isPartnerGroupLike(item));

        if (partner) {
          const partnerGroupId = String(partner?.group_id || "");
          if (partnerGroupId) {
            window.location.replace(groupChatHref(partnerGroupId));
          }
          return;
        }

        const lastGroupId = readLastOpenedMessageGroupId();
        const last = lastGroupId
          ? items.find((item: any) => String(item.group_id || "") === lastGroupId)
          : null;

        const chosen = last || items[0];
        const groupId = String(chosen?.group_id || "");

        if (groupId) {
          window.location.replace(groupChatHref(groupId, { channelsOpen: true }));
          return;
        }

        setChannelsOpen(true);
        void loadChannels();
      } catch {
        // stay on /messages
      }
    }

    void redirectToDefaultChatIfNeeded();

    return () => {
      cancelled = true;
    };
  }, [params.originGroupId, params.originActivityId, params.partnerUserId]);


  function selectPrivateRecipient(userId: string) {
    if (!userId || userId === currentUserId) return;
    setSelectedPrivateIds([userId]);
  }

  function groupChatHref(groupId: string, options?: { channelsOpen?: boolean }) {
    const q = new URLSearchParams();
    q.set("originGroupId", groupId);
    if (options?.channelsOpen) q.set("channels", "1");
    return `/messages?${q.toString()}`;
  }

  function channelLabel(channel: ChannelRow) {
    return channel.label || channel.group_id || "Channel";
  }

  function channelSubtitle(channel: ChannelRow) {
    const type = String(channel.group_type || "");
    if (channel.is_partner || type === "partner_group") return "Private partner group";
    if (type === "interest_group") return "Public channel";
    if (type === "event_group") return "Private event group";
    if (type === "study_group") return "Planning group";
    if (type === "private_group") return "Private group";
    return "Group";
  }

  async function loadChannels() {
    setChannelsLoading(true);

    try {
      const res = await listMyGroups();
      const items = Array.isArray(res?.items) ? res.items : [];

      const mapped: ChannelRow[] = items.map((item: any) => {
        const groupType = item.group_type || item.kind || null;
        return {
          group_id: String(item.group_id || ""),
          label: item.label || item.name || "",
          description: item.description || "",
          group_type: groupType,
          role_name: item.role_name || "",
          member_count: Number(item.member_count || 0),
          unread_count: Number(item.unread_count || item.unreadCount || 0),
          is_partner: groupType === "partner_group" || String(item.group_id || "").startsWith("group_partner_"),
        };
      }).filter((item) => item.group_id);

      mapped.sort((a, b) => {
        if (a.is_partner && !b.is_partner) return -1;
        if (!a.is_partner && b.is_partner) return 1;

        const au = Number(a.unread_count || 0);
        const bu = Number(b.unread_count || 0);
        if (au !== bu) return bu - au;

        return channelLabel(a).localeCompare(channelLabel(b));
      });

      setChannels(mapped);
    } catch {
      setChannels([]);
    } finally {
      setChannelsLoading(false);
    }
  }

  function openChannelsDrawer() {
    setChannelsOpen(true);
    void loadChannels();
  }

  useEffect(() => {
    if (!params.channelsOpen) return;
    setChannelsOpen(true);
    void loadChannels();
  }, [params.channelsOpen]);


  async function refreshMediaRequests(force = false) {
    const mediaBackoffKey = String(conversationId || "");
    const mediaBackoffUntil = Number(mediaRequestsBackoffUntilRef.current[mediaBackoffKey] || 0);

    if (Date.now() < mediaBackoffUntil) {
      if (isMessagesDebugEnabled()) {
        console.log("[Messages web] media requests skipped backoff", {
          conversationId,
          retryInMs: mediaBackoffUntil - Date.now(),
        });
      }
      return;
    }

    if (!force && Date.now() < webMediaBackoffUntil) return;

    if (!conversationId) {
      setMediaRequests([]);
      return;
    }

    try {
      const res = await listMediaRequests({
        conversationId,
      });

      const active = (Array.isArray(res?.items) ? res.items : [])
        .filter((item) => ["pending", "accepted"].includes(String(item.status || "")))
        .slice()
        .sort((a, b) => {
          const aa = new Date((a as any)?.requestedAt || (a as any)?.createdAt || 0).getTime();
          const bb = new Date((b as any)?.requestedAt || (b as any)?.createdAt || 0).getTime();
          return aa - bb;
        });

      setMediaRequests(active);
    } catch (e: any) {
      if (isProbably429Error(e)) {
        const retryAfterMs = getRetryAfterMsFromError(e);
        webMediaBackoffUntil = Date.now() + retryAfterMs;
        mediaRequestsBackoffUntilRef.current[String(conversationId || "")] =
          Date.now() + retryAfterMs;

        if (isMessagesDebugEnabled()) {
          console.warn("Media request polling throttled; backing off", {
            conversationId,
            retryAfterMs,
          });
        }
        return;
      }

      setMediaRequests([]);
    }
  }

  async function compressImageFileForEncryptedUpload(file: File): Promise<File> {
    if (!String(file.type || "").startsWith("image/")) return file;

    const maxBytes = 900 * 1024;
    if (file.size <= maxBytes) return file;

    const objectUrl = URL.createObjectURL(file);

    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("image_decode_failed"));
        image.src = objectUrl;
      });

      const maxEdge = 1440;
      const scale = Math.min(1, maxEdge / Math.max(img.width || 1, img.height || 1));
      const width = Math.max(1, Math.round((img.width || 1) * scale));
      const height = Math.max(1, Math.round((img.height || 1) * scale));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("canvas_unavailable");

      ctx.drawImage(img, 0, 0, width, height);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (value) => value ? resolve(value) : reject(new Error("image_compress_failed")),
          "image/jpeg",
          0.82
        );
      });

      return new File(
        [blob],
        String(file.name || "private-photo.jpg").replace(/\.[A-Za-z0-9]+$/, "") + ".jpg",
        {
          type: "image/jpeg",
          lastModified: Date.now(),
        }
      );
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onerror = () => reject(new Error("file_read_failed"));
      reader.onload = () => {
        const value = String(reader.result || "");
        const comma = value.indexOf(",");
        resolve(comma >= 0 ? value.slice(comma + 1) : value);
      };

      reader.readAsDataURL(file);
    });
  }

  async function sendSelectedWebMediaFile(file: File | null) {
    if (!file) return;

    const activeConversationId = activeConversationIdForRealtime();
    if (!activeConversationId) {
      setError("Open a conversation first.");
      return;
    }

    const targetRecipientIds = chatMode === "private"
      ? [selectedPrivateIds[0] || params.partnerUserId].filter(Boolean)
      : members
          .map((m) => memberId(m))
          .filter((id) => id && id !== currentUserId);

    if (!targetRecipientIds.length) {
      setError("No recipient keys available for this conversation.");
      return;
    }

    const selectedKind = mediaKind === "video" ? "video" : "image";
    const expectedPrefix = selectedKind === "video" ? "video/" : "image/";

    if (!String(file.type || "").startsWith(expectedPrefix)) {
      setError(selectedKind === "video" ? "Choose a video file." : "Choose an image file.");
      return;
    }

    setSending(true);
    setError(null);
    setStatusMsg(
      temporaryMode === "view_once"
        ? "Compressing and uploading encrypted 1x media…"
        : "Compressing and uploading encrypted media…"
    );

    try {
      const uploadFile = selectedKind === "image"
        ? await compressImageFileForEncryptedUpload(file)
        : file;

      if (uploadFile.size > 2 * 1024 * 1024) {
        throw new Error("Selected media is still too large after compression. Choose a smaller file for this web test.");
      }

      const plainBase64 = await fileToBase64(uploadFile);

      const effectiveTemporaryMode = privatePhotoResponseActive ? "view_once" : temporaryMode;

      const queuedMedia: any = await enqueueEncryptedWebMediaForThread({
        conversationId: activeConversationId,
        recipientUserIds: targetRecipientIds,
        mediaKind: selectedKind,
        mimeType: uploadFile.type || (selectedKind === "video" ? "video/mp4" : "image/jpeg"),
        originalFilename: uploadFile.name || undefined,
        sizeBytes: uploadFile.size || 0,
        plainBase64,
        temporaryMode: effectiveTemporaryMode,
      } as any);

      const queuedMediaId = String(
        queuedMedia?.mediaId ||
        queuedMedia?.media_id ||
        queuedMedia?.manifest?.mediaId ||
        queuedMedia?.manifest?.media_id ||
        ''
      ).trim();

      let senderLocalThumbnail = '';

      if (
        queuedMediaId &&
        selectedKind === 'image' &&
        effectiveTemporaryMode !== 'view_once'
      ) {
        try {
          senderLocalThumbnail =
            (await createImageThumbnailFromBlob(uploadFile, 720)) || '';
        } catch (thumbnailError) {
          console.warn('[web-media-send] local sender thumbnail failed', {
            mediaId: queuedMediaId,
            message:
              (thumbnailError as any)?.message ||
              String(thumbnailError),
          });
        }
      }

      await flushWebMediaOutbox();

      if (queuedMediaId && senderLocalThumbnail) {
        setWebMediaThumbById((old) => ({
          ...old,
          [queuedMediaId]: senderLocalThumbnail,
        }));
      }

      const sentStatusText =
        temporaryMode === "view_once"
          ? "Encrypted 1x media sent."
          : "Encrypted media sent.";
      setStatusMsg(sentStatusText);
      setTimeout(() => {
        setStatusMsg((current) => current === sentStatusText ? "" : current);
      }, 2500);
      setMediaOpen(false);
      setMediaFlow(null);
      setPrivatePhotoResponseActive(false);
      await loadMessages({ force: true, realtime: true, source: "web_media_sent" });
    } catch (e: any) {
      setError(e?.message || "Could not send encrypted media.");
    } finally {
      setSending(false);
    }
  }

  async function sendMediaRequest() {
    if (mediaFlow !== "request") return;

    const requestKind = mediaKind === "video" || mediaKind === "voice" ? mediaKind : "photo";

    const targetUserId = effectiveMediaRequestTargetId;

    if (!targetUserId) {
      setStatusMsg("Choose who should receive this media request.");
      return;
    }

    if (targetUserId === currentUserId) {
      setStatusMsg("You cannot send a media request to yourself.");
      return;
    }

    setMediaRequestSending(true);

    try {
      const res = await createMediaRequest({
        targetUserId,
        requestKind,
        temporaryMode: "view_once",
        originGroupId: params.originGroupId || null,
        originActivityId: params.originActivityId || null,
        conversationId,
        note: `Requesting ${requestKind}`,
      });

      setStatusMsg(`Requested ${requestKind} from ${userLabelById(targetUserId)}.`);
      setMediaOpen(false);
      setMediaFlow(null);
      setMediaKind(null);
      setTemporaryMode("normal");
      setSelectedMediaRequestTargetId("");

      setMediaRequests((prev) =>
        [...prev, res.item].slice().sort((a, b) => {
          const aa = new Date((a as any)?.requestedAt || (a as any)?.createdAt || 0).getTime();
          const bb = new Date((b as any)?.requestedAt || (b as any)?.createdAt || 0).getTime();
          return aa - bb;
        })
      );
      setTimeout(scrollToBottom, 0);
    } catch (e: any) {
      setStatusMsg(`Media request failed: ${e?.message || "server error"}`);
    } finally {
      setMediaRequestSending(false);
    }
  }

  function mediaRequestDirectionLabel(item: MediaRequestItem) {
    if (String(item.requesterUserId || "") === currentUserId) return "Outgoing";
    if (String(item.targetUserId || "") === currentUserId) return "Incoming";
    if (!item.targetUserId) return "Open request";
    return "Request";
  }

  function canCancelMediaRequest(item: MediaRequestItem) {
    return item.status === "pending" && String(item.requesterUserId || "") === currentUserId;
  }

  function canRespondToMediaRequest(item: MediaRequestItem) {
    if (item.status !== "pending") return false;
    if (String(item.requesterUserId || "") === currentUserId) return false;
    if (!item.targetUserId) return true;
    return String(item.targetUserId || "") === currentUserId;
  }

  async function handleMediaRequestAction(
    item: MediaRequestItem,
    action: "accepted" | "declined" | "cancelled"
  ) {
    try {
      await respondMediaRequest(item.id, { action });
      setStatusMsg(
        action === "accepted"
          ? `${item.requestKind} request accepted. Capture/upload is not implemented yet.`
          : action === "declined"
          ? `${item.requestKind} request declined.`
          : `${item.requestKind} request cancelled.`
      );
      await refreshMediaRequests();
      await loadMessages({ force: false, realtime: false, source: "poll" });
    } catch (e: any) {
      setStatusMsg(`Request update failed: ${e?.message || "server error"}`);
    }
  }

  async function handleMediaRequestFulfilledPlaceholder(item: MediaRequestItem) {
    try {
      await fulfillMediaRequest(item.id);
      setStatusMsg(`${item.requestKind} request marked fulfilled. Real media upload is still not implemented.`);
      await refreshMediaRequests();
      await loadMessages({ force: false, realtime: false, source: "poll" });
    } catch (e: any) {
      setStatusMsg(`Fulfill failed: ${e?.message || "server error"}`);
    }
  }

  function insertIntoDraft(value: string) {
    setDraft((prev) => `${prev}${value}`);
  }

  function truncatePreview(value: string, max = 96) {
    const oneLine = String(value || "").replace(/\s+/g, " ").trim();
    return oneLine.length > max ? `${oneLine.slice(0, max - 1)}…` : oneLine;
  }

  function ownActionName() {
    return "You";
  }

  function prepareOutgoingText(raw: string) {
    const trimmed = raw.trim();

    let body = trimmed;
    let messageKind: UiMessage["messageKind"] = "text";

    if (trimmed.toLowerCase().startsWith("/me ")) {
      const action = trimmed.slice(4).trim();
      body = action ? `* ${ownActionName()} ${action}` : trimmed;
      messageKind = "action";
    }

    if (replyTo) {
      const label = replyTo.senderDisplayName || "Member";
      body = `> ${label}: ${truncatePreview(replyTo.body, 120)}\n\n${body}`;
    }

    return {
      body,
      messageKind,
      replyToClientMessageId: replyTo?.clientMessageId || replyTo?.id || null,
      replyPreview: replyTo ? `${replyTo.senderDisplayName || "Member"}: ${truncatePreview(replyTo.body, 120)}` : null,
    };
  }

  function deliveryStatusStateForWebMessage(m: UiMessage, mine: boolean): 'sending' | 'sent' | 'delivered' | 'read' {
    if (!mine) return 'sent';

    const mediaLike = (m as any)?.mediaItem || (m as any);
    const isMediaMessage =
      !!(m as any)?.mediaItem ||
      String((m as any)?.timelineKind || '').toLowerCase() === 'media' ||
      String((m as any)?.kind || '').toLowerCase() === 'media';

    if (isMediaMessage) {
      const mediaState = privateMediaStatusStateForWeb(mediaLike);

      if (mediaState === 'read') return 'read';
      if (mediaState === 'delivered') return 'delivered';
      if (mediaState === 'sending') return 'sending';
    }

    const state = String(
      (m as any).effectiveDeliveryState ||
      (m as any).effective_delivery_state ||
      m.deliveryState ||
      (m as any).delivery_state ||
      ''
    ).toLowerCase();

    if ((m.readByCount && m.readByCount > 0) || state.includes("read") || state.includes("seen") || state.includes("viewed")) {
      return 'read';
    }

    if (
      state.includes("delivered") ||
      state.includes("received") ||
      state.includes("downloaded") ||
      state.includes("acknowledged") ||
      state === "server" ||
      (m.clientMessageId && ackedClientMessageIds.has(m.clientMessageId))
    ) {
      return 'delivered';
    }

    if (state.includes("pending") || state.includes("sending") || state.includes("uploading")) {
      return 'sending';
    }

    return m.clientMessageId ? 'sent' : 'sending';
  }


  function webMessageDeleteIdentity(m: any): { itemKind: "text" | "media"; itemId: string } {
    const mediaItem = m?.mediaItem || m?.media_item || null;

    const mediaId = String(
      m?.mediaId ||
      m?.media_id ||
      mediaItem?.mediaId ||
      mediaItem?.media_id ||
      mediaItem?.id ||
      ''
    ).replace(/^media:/, '').trim();

    if (m?.timelineKind === 'media' || mediaItem || mediaId) {
      return {
        itemKind: 'media',
        itemId: mediaId,
      };
    }

    const textId = String(
      m?.clientMessageId ||
      m?.client_message_id ||
      m?.messageId ||
      m?.message_id ||
      m?.id ||
      ''
    ).trim();

    return {
      itemKind: 'text',
      itemId: textId,
    };
  }


  async function deleteMessageFromWeb(m: UiMessage, scope: "me" | "everyone") {
    const { itemKind, itemId } = webMessageDeleteIdentity(m);

    if (!conversationId || !itemId) {
      setStatusMsg("Could not determine message to delete.");
      return;
    }

    const oldScrollTop = scrollRef.current?.scrollTop || 0;
    const oldStick = stickToBottomRef.current;
    stickToBottomRef.current = false;

    try {
      await deleteMessageItemWeb({
        conversationId,
        itemKind,
        itemId,
        scope,
      });

      if (itemKind === "media" && itemId) {
        setWebDeletedMediaById((old) => ({
          ...old,
          [itemId]: scope,
        }));

        setWebUnavailableMediaById((old) => {
          if (!old[itemId]) return old;
          const next = { ...old };
          delete next[itemId];
          return next;
        });

        setWebMediaBlobStatusById((old) => {
          if (!old[itemId]) return old;
          const next = { ...old };
          delete next[itemId];
          return next;
        });
      }

      setMessages((prev) =>
        prev.filter((row) => {
          const ident = webMessageDeleteIdentity(row);
          return !(ident.itemKind === itemKind && ident.itemId === itemId);
        })
      );

      setStatusMsg(scope === "everyone" ? "Deleted for everyone." : "Deleted for you.");

      window.setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = oldScrollTop;
        stickToBottomRef.current = oldStick;
      }, 0);
    } catch (e: any) {
      setStatusMsg(`Delete failed: ${e?.message || "server error"}`);
      stickToBottomRef.current = oldStick;
    }
  }




  async function refreshWebCachedMediaFlag(mediaIdValue: any) {
    const mediaId = String(mediaIdValue || '').replace(/^media:/, '').trim();
    if (!mediaId) return false;

    const cached = await hasCachedEncryptedMediaBlob(mediaId);

    setWebCachedMediaById((old) => {
      if (cached && old[mediaId]) return old;
      if (!cached && !old[mediaId]) return old;

      const next = { ...old };
      if (cached) next[mediaId] = true;
      else delete next[mediaId];
      return next;
    });

    return cached;
  }

  function clearWebUnavailableMediaFlag(mediaIdValue: any) {
    const mediaId = String(mediaIdValue || '').replace(/^media:/, '').trim();
    if (!mediaId) return;

    setWebUnavailableMediaById((old) => {
      if (!old[mediaId]) return old;
      const next = { ...old };
      delete next[mediaId];
      return next;
    });

    setWebMediaBlobStatusById((old) => {
      if (!old[mediaId]) return old;
      const next = { ...old };
      delete next[mediaId];
      return next;
    });

    webMediaBlobStatusRequestedRef.current.delete(mediaId);
    webMediaAutoCacheRequestedRef.current.delete(mediaId);
  }


  async function autoCacheNormalWebMediaBlob(itemLike: any, fallbackId?: any) {
    const item = itemLike || {};
    const mediaId = webMediaStableId(item, fallbackId);

    if (!mediaId) return false;
    if (isRestrictedOnceMediaItem(item)) return false;
    if (webDeletedMediaById[mediaId]) return false;
    if (webUnavailableMediaById[mediaId]) {
      const status = webMediaBlobStatusById[mediaId] as any;
      const statusLooksAvailable =
        status &&
        status.available !== false &&
        String(status.blobStatus || status.blob_status || '').toLowerCase() === 'uploaded';

      const manifestLooksUnavailable = webManifestBlobUnavailable(item);

      if (statusLooksAvailable || !manifestLooksUnavailable) {
        clearWebUnavailableMediaFlag(mediaId);
      } else {
        return false;
      }
    }
    if (webManifestBlobUnavailable(item)) return false;

    const alreadyCached = await refreshWebCachedMediaFlag(mediaId);
    if (alreadyCached) return true;

    if (webMediaAutoCacheRequestedRef.current.has(mediaId)) return false;
    webMediaAutoCacheRequestedRef.current.add(mediaId);

    try {
      const ok = await ensureWebMediaEncryptedBlobCached({
        item,
        mediaId,
        mimeType: mediaMime(item),
      });

      if (ok) {
        await refreshWebCachedMediaFlag(mediaId);
        return true;
      }
    } catch (e: any) {
      console.warn('[web-media-auto-cache] failed', {
        mediaId,
        message: e?.message || String(e),
        itemBlobStatus: item?.blobStatus || item?.blob_status || '',
        itemBlobReason: item?.blobDeleteReason || item?.blob_delete_reason || '',
        mimeType: mediaMime(item),
        visibilityMode: item?.visibilityMode || item?.visibility_mode || '',
        hasManifest: !!getMediaManifest(item),
        manifestKeys: Object.keys(getMediaManifest(item) || {}),
      });

      // Auto-cache runs in the background and can race the sender's blob PUT.
      // Do not poison the card as unavailable from one background fetch failure.
      // Manual open/status paths can still mark truly unavailable media.
      webMediaAutoCacheRequestedRef.current.delete(mediaId);
    }

    return false;
  }

  async function ensureWebMediaBlobStatus(itemLike: any, fallbackId?: any) {
    const item = itemLike || {};
    const mediaId = webMediaStableId(item, fallbackId);
    if (!mediaId) return null;

    if (webMediaBlobStatusById[mediaId]) {
      return webMediaBlobStatusById[mediaId];
    }

    if (webMediaBlobStatusRequestedRef.current.has(mediaId)) {
      return null;
    }

    webMediaBlobStatusRequestedRef.current.add(mediaId);

    try {
      const status = await getEncryptedMediaBlobStatus(mediaId);

      setWebMediaBlobStatusById((old) => ({
        ...old,
        [mediaId]: status,
      }));

      if (status?.isViewOnce || status?.allowThumbnail === false) {
        setWebVideoThumbById((old) => {
          if (!old[mediaId]) return old;
          const next = { ...old };
          delete next[mediaId];
          return next;
        });
      }

      if (status && status.available === false) {
        setWebUnavailableMediaById((old) => ({
          ...old,
          [mediaId]: String(status.blobStatus || status.reason || 'unavailable'),
        }));
      }

      return status;
    } catch (e: any) {
      // Non-fatal. Keep current card until open attempt gives a stronger signal.
      console.warn('[web-media-status] failed', mediaId, e?.message || e);
      return null;
    }
  }

  function webMediaStableId(item: any, fallback?: any) {
    const manifest = getMediaManifest(item || {});

    return String(
      item?.mediaId ||
      item?.media_id ||
      item?.id ||
      item?.clientMessageId ||
      item?.client_message_id ||
      manifest?.mediaId ||
      manifest?.media_id ||
      fallback ||
      ''
    ).replace(/^media:/, '').trim();
  }

  function webMediaTitle(item: any) {
    const manifest = getMediaManifest(item || {});
    const mime = mediaMime(item);
    const kind = mediaKindOfItem(item);

    return String(
      item?.title ||
      item?.name ||
      item?.filename ||
      item?.fileName ||
      manifest?.title ||
      manifest?.name ||
      (
        isVideoMediaItem(item)
          ? 'Encrypted video'
          : isAudioMediaItem(item)
            ? 'Encrypted voice'
            : kind === 'image' || mime.startsWith('image/')
              ? ''
              : 'Encrypted media'
      )
    );
  }

  function webMediaUsableUrl(item: any) {
    const manifest = getMediaManifest(item || {});

    return String(
      item?.objectUrl ||
      item?.objectURL ||
      item?.localUrl ||
      item?.localURL ||
      item?.previewUrl ||
      item?.preview_url ||
      item?.downloadUrl ||
      item?.download_url ||
      item?.url ||
      manifest?.objectUrl ||
      manifest?.localUrl ||
      manifest?.previewUrl ||
      manifest?.downloadUrl ||
      manifest?.url ||
      ''
    );
  }


  function isConsumedOrUnavailableOnceWebMedia(item: any) {
    const value = String(
      item?.viewOnceState ||
      item?.view_once_state ||
      item?.state ||
      item?.status ||
      item?.deliveryState ||
      item?.delivery_state ||
      ''
    ).toLowerCase();

    return (
      isRestrictedOnceMediaItem(item) &&
      (
        value.includes('complete') ||
        value.includes('completed') ||
        value.includes('consumed') ||
        value.includes('expired') ||
        value.includes('deleted') ||
        value.includes('gone') ||
        Boolean(item?.completedAt || item?.completed_at || item?.deletedAt || item?.deleted_at)
      )
    );
  }

  function webMediaKindLabel(item: any) {
    const mime = mediaMime(item);
    const kind = mediaKindOfItem(item);

    if (isVideoMediaItem(item)) return 'video';
    if (isAudioMediaItem(item)) return 'voice';
    if (kind === 'image' || mime.startsWith('image/')) return 'image';

    return 'media';
  }


  function showWebMediaControlsTemporarily() {
    setWebMediaControlsVisible(true);

    if (webMediaControlsTimerRef.current) {
      window.clearTimeout(webMediaControlsTimerRef.current);
    }

    webMediaControlsTimerRef.current = window.setTimeout(() => {
      setWebMediaControlsVisible(false);
      webMediaControlsTimerRef.current = null;
    }, 1800);
  }

  function releaseWebMediaObjectUrl() {
    const url = webMediaPreviewObjectUrlRef.current;
    if (!url) return;

    try {
      URL.revokeObjectURL(url);
    } catch {}

    webMediaPreviewObjectUrlRef.current = null;
  }

  async function markNormalWebMediaViewed(
    item: any,
    mediaIdValue: string,
  ) {
    const mediaId = String(mediaIdValue || '').trim();
    if (!mediaId) return;
    if (isRestrictedOnceMediaItem(item || {})) return;

    const selfUserId = String(currentUserId || '').trim();

    const senderUserId = String(
      item?.senderUserId ||
      item?.sender_user_id ||
      item?.fromUserId ||
      item?.from_user_id ||
      item?.raw?.senderUserId ||
      item?.raw?.sender_user_id ||
      ''
    ).trim();

    // Opening your own upload must not mark the recipient row viewed.
    if (
      !selfUserId ||
      !senderUserId ||
      senderUserId === selfUserId
    ) {
      return;
    }

    if (
      completedNormalWebMediaViewIdsRef.current.has(mediaId)
    ) {
      return;
    }

    completedNormalWebMediaViewIdsRef.current.add(mediaId);

    try {
      await completeViewOnceMedia(mediaId, {
        source: 'web_normal_media_opened',
      });

      debugWebMessagesLog(
        '[Messages web] normal media marked viewed',
        {
          mediaId,
          senderUserId,
          selfUserId,
        }
      );

      void refreshMediaRowsOnly('web_normal_media_viewed');
    } catch (error: any) {
      completedNormalWebMediaViewIdsRef.current.delete(mediaId);

      console.warn(
        '[Messages web] normal media view completion failed',
        {
          mediaId,
          status: error?.status || error?.response?.status,
          error:
            error?.response?.data?.error ||
            error?.message ||
            String(error),
        }
      );
    }
  }


  async function openWebMediaPreview(itemLike: any, fallbackId?: any, sourceMessage?: any, canDeleteEveryone = false) {
    const item = normalizeVoiceMediaItem(itemLike || {});
    const mediaId = webMediaStableId(item, fallbackId);
    const initialMimeType = mediaMime(item);
    const kind = webMediaKindLabel(item);
    let restrictedOnce = isRestrictedOnceMediaItem(item);
    let effectiveItem = item;

    if (restrictedOnce && mediaId && (webConsumedOnceMediaById[mediaId] || hasWebOnceConsumed(mediaId))) {
      setStatusMsg('This 1x media has already been opened on this browser.');
      return;
    }

    if (!restrictedOnce && webManifestBlobUnavailable(effectiveItem)) {
      if (mediaId) {
        setWebUnavailableMediaById((old) => ({
          ...old,
          [mediaId]: webManifestUnavailableReason(effectiveItem),
        }));
      }

      setStatusMsg('This media is not cached on this browser and is no longer available from the server.');
      return;
    }

    let url = webMediaUsableUrl(item);

    if (!url && mediaId) {
      const status = await ensureWebMediaBlobStatus(effectiveItem, mediaId);

      if (status?.isViewOnce) {
        restrictedOnce = true;
        effectiveItem = {
          ...effectiveItem,
          visibilityMode: 'view_once',
          visibility_mode: 'view_once',
          allowThumbnail: false,
          allow_thumbnail: false,
          allowSave: false,
          allow_save: false,
          allowShare: false,
          allow_share: false,
        };
      }

      if (status && status.available === false) {
        setWebUnavailableMediaById((old) => ({
          ...old,
          [mediaId]: String(status.blobStatus || status.reason || 'unavailable'),
        }));
        setStatusMsg('This media is not cached on this browser and is no longer available from the server.');
        return;
      }
    }

    let mimeType = initialMimeType;
    let ownedObjectUrl = false;
    let viewOnceStarted = false;
    let decryptedBlob: Blob | null = null;

    releaseWebMediaObjectUrl();
    setWebMediaFitMode('fit');
    showWebMediaControlsTemporarily();

    try {
      if (!url) {
        setStatusMsg(restrictedOnce ? 'Opening 1x encrypted media…' : 'Decrypting encrypted media…');

        if (restrictedOnce && mediaId) {
          await startViewOnceMedia(mediaId);
          viewOnceStarted = true;
        }

        const decrypted = await decryptWebMediaToObjectUrl({
          item: effectiveItem,
          currentUserId,
        });

        url = decrypted.objectUrl;
        mimeType = decrypted.mimeType || mimeType;
        ownedObjectUrl = true;
        decryptedBlob = decrypted.blob;

        webMediaPreviewObjectUrlRef.current = url;
      }

      setWebMediaPreview({
        item: effectiveItem,
        mediaId,
        title: webMediaTitle(item),
        mimeType,
        kind,
        url,
        restrictedOnce,
        deleteAfterViewSeconds:
          Number(
            getMediaManifest(effectiveItem)?.policy?.deleteAfterViewSeconds ||
            getMediaManifest(effectiveItem)?.policy?.delete_after_view_seconds ||
            getMediaManifest(effectiveItem)?.deleteAfterViewSeconds ||
            getMediaManifest(effectiveItem)?.delete_after_view_seconds ||
            (effectiveItem as any)?.deleteAfterViewSeconds ||
            (effectiveItem as any)?.delete_after_view_seconds ||
            10
          ) || 10,
        ownedObjectUrl,
        viewOnceStarted,
        sourceMessage,
        canDeleteEveryone,
      });

      if (!restrictedOnce && mediaId && url) {
        void markNormalWebMediaViewed(
          effectiveItem,
          mediaId,
        );
      }

      if (restrictedOnce && mediaId) {
        markWebOnceConsumed(mediaId);
      }

      setStatusMsg(
        restrictedOnce
          ? '1x media opened. It will be completed when you close the preview.'
          : ''
      );

      const openedStatus = mediaId ? webMediaBlobStatusById[mediaId] : null;
      const thumbnailsAllowed =
        !restrictedOnce &&
        !openedStatus?.isViewOnce &&
        openedStatus?.allowThumbnail !== false;

      if (thumbnailsAllowed && mediaId && !webVideoThumbById[mediaId]) {
        try {
          let thumbBlob = decryptedBlob;

          if (!thumbBlob && url) {
            const res = await fetch(url);
            thumbBlob = await res.blob();
          }

          let thumb: string | null = null;

          if (thumbBlob?.type?.startsWith('video/')) {
            thumb = await createVideoThumbnailFromBlob(thumbBlob, 1);
          } else if (thumbBlob?.type?.startsWith('image/')) {
            thumb = await createImageThumbnailFromBlob(thumbBlob, 720);
          }

          if (thumb) {
            setWebVideoThumbById((old) => ({ ...old, [mediaId]: thumb }));
          }
        } catch {
          // Non-fatal: keep media fallback.
        }
      }
    } catch (e: any) {
      console.warn('[web-media-open] failed', {
        message: e?.message || String(e || ''),
        mediaId,
        restrictedOnce,
        kind,
        mimeType,
        hasManifest: !!getMediaManifest(item),
        manifestKeys: Object.keys(getMediaManifest(item) || {}),
        hasMediaItem: !!item,
        senderUserId: item?.senderUserId || item?.sender_user_id,
        recipientUserId: item?.recipientUserId || item?.recipient_user_id,
        visibilityMode: item?.visibilityMode || item?.visibility_mode,
        blobStatus: item?.blobStatus || item?.blob_status,
        blobDeleteReason: item?.blobDeleteReason || item?.blob_delete_reason,
      });
      if (ownedObjectUrl) releaseWebMediaObjectUrl();

      const message = String(e?.message || '');

      // Do not open an empty preview shell for normal media failures.
      // It looks like a real unavailable image even when the server blob may
      // still be available and the failure is retry/decrypt/cache related.
      if (restrictedOnce) {
        setWebMediaPreview({
          item,
          mediaId,
          title: webMediaTitle(item),
          mimeType,
          kind,
          url: '',
          restrictedOnce,
          ownedObjectUrl: false,
          viewOnceStarted,
          sourceMessage,
          canDeleteEveryone,
        });
      } else {
        setWebMediaPreview(null);
      }

      if (message.includes('410') && mediaId) {
        setWebUnavailableMediaById((old) => ({
          ...old,
          [mediaId]: 'server_gone_no_local_cache',
        }));
      }

      setStatusMsg(
        message.includes('410')
          ? 'This encrypted media is no longer available from the server. Normal media can reopen on this browser after it has been cached once.'
          : message
            ? `Could not open encrypted media: ${message}`
            : 'Could not open encrypted media.'
      );
    }
  }

  useEffect(() => {
    const mediaId = String(webMediaPreview?.mediaId || '').trim();

    if (!webMediaPreview?.restrictedOnce || !mediaId || !webMediaPreview?.url) {
      setWebMediaPreviewCountdown(null);
      return undefined;
    }

    const totalSeconds = Math.max(1, Number(webMediaPreview.deleteAfterViewSeconds || 10) || 10);
    let remaining = totalSeconds;

    console.log('[web-view-once] countdown auto close', { mediaId, totalSeconds });
    setWebMediaPreviewCountdown(remaining);

    const timer = window.setInterval(() => {
      remaining -= 1;
      setWebMediaPreviewCountdown(Math.max(0, remaining));

      if (remaining <= 0) {
        window.clearInterval(timer);
        void closeWebMediaPreview();
      }
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [webMediaPreview?.mediaId, webMediaPreview?.restrictedOnce, webMediaPreview?.url]);


  async function closeWebMediaPreview() {
    const current = webMediaPreview;

    setWebMediaPreview(null);
    releaseWebMediaObjectUrl();

    if (current?.restrictedOnce && current?.viewOnceStarted && current?.mediaId) {
      try {
        await completeViewOnceMedia(current.mediaId, { source: 'web_media_preview_close' });
        await deleteCachedEncryptedMediaBlob(current.mediaId).catch(() => {});
        setStatusMsg('1x media completed.');
        await loadMessages({ force: true, realtime: true, source: 'web_view_once_complete' });
      } catch (e: any) {
        setStatusMsg(e?.message || 'Could not complete 1x media.');
      }
    }
  }



  function webOnceConsumedStorageKey(mediaId: string) {
    return `intimsync.onceMedia.consumed.${mediaId}`;
  }

  function hasWebOnceConsumed(mediaId: string) {
    if (!mediaId) return false;

    try {
      return window.localStorage.getItem(webOnceConsumedStorageKey(mediaId)) === '1';
    } catch {
      return false;
    }
  }

  function markWebOnceConsumed(mediaId: string) {
    if (!mediaId) return;

    try {
      window.localStorage.setItem(webOnceConsumedStorageKey(mediaId), '1');
    } catch {}

    setWebConsumedOnceMediaById((old) => ({
      ...old,
      [mediaId]: 'opened',
    }));
  }


  function webManifestBlobUnavailable(item: any) {
    const status = String(
      item?.blobStatus ||
      item?.blob_status ||
      ''
    ).toLowerCase();

    const reason = String(
      item?.blobDeleteReason ||
      item?.blob_delete_reason ||
      item?.reason ||
      ''
    ).toLowerCase();

    // Fresh media can appear in the manifest before the encrypted blob PUT has
    // completed. Treat pending/empty statuses as temporarily loading, not gone.
    if (!status) return false;

    if (
      status === 'uploaded' ||
      status === 'available' ||
      status === 'pending' ||
      status === 'uploading' ||
      status === 'created' ||
      status === 'reserved'
    ) {
      return false;
    }

    return (
      status === 'missing' ||
      status === 'deleted' ||
      status === 'expired' ||
      status === 'unavailable' ||
      reason === 'deleted' ||
      reason === 'expired' ||
      reason === 'missing'
    );
  }

  function webManifestUnavailableReason(item: any) {
    return String(
      item?.blobDeleteReason ||
      item?.blob_delete_reason ||
      item?.reason ||
      item?.blobStatus ||
      item?.blob_status ||
      'unavailable'
    );
  }

  function webMediaActionBlocked(item: any) {
    return blockRestrictedOnceMediaAction(item, setStatusMsg);
  }

  // Blob status is now included in the media manifest list.
  // Keep per-item /status calls out of the refresh path to avoid API request storms.
  // Open handlers may still query /status for a single item when needed.




  useEffect(() => {
    void pruneOldEncryptedMediaBlobs();

    return () => {
      releaseWebMediaObjectUrl();
    };
  }, []);



  useEffect(() => {
    const root = scrollRef.current;

    webVisibleMediaIdsRef.current = new Set();
    setWebVisibleMediaEpoch((value) => value + 1);

    if (!root || typeof IntersectionObserver === "undefined") {
      return;
    }

    let disposed = false;

    const observer = new IntersectionObserver(
      (entries) => {
        if (disposed) return;

        const next = new Set(
          webVisibleMediaIdsRef.current
        );

        let changed = false;

        for (const entry of entries) {
          const element = entry.target as HTMLElement;
          const mediaId = String(
            element.dataset.webMediaId || ""
          ).trim();

          if (!mediaId) continue;

          if (entry.isIntersecting) {
            if (!next.has(mediaId)) {
              next.add(mediaId);
              changed = true;
            }
          } else if (next.delete(mediaId)) {
            changed = true;
          }
        }

        if (!changed) return;

        webVisibleMediaIdsRef.current = next;
        setWebVisibleMediaEpoch((value) => value + 1);
      },
      {
        root,
        rootMargin: "600px 0px 600px 0px",
        threshold: 0.01,
      }
    );

    const elements = root.querySelectorAll<HTMLElement>(
      "[data-web-media-id]"
    );

    elements.forEach((element) => observer.observe(element));

    return () => {
      disposed = true;
      observer.disconnect();
      webVisibleMediaIdsRef.current = new Set();
    };
  }, [
    conversationId,
    messages.length,
  ]);


  useEffect(() => {
    let cancelled = false;

    async function loadWebManifestThumbnails() {
      const selfId = String(
        currentUserId || ""
      ).trim();

      if (!selfId) return;

      const allMediaRows = (messages || []).filter(
        (row: any) => row?.timelineKind === "media"
      );

      if (!allMediaRows.length) return;

      const nearVisibleIds =
        webVisibleMediaIdsRef.current;

      // Before IntersectionObserver has reported anything, preload only
      // the newest few media cards near the normal conversation entry point.
      const fallbackIds = new Set(
        allMediaRows
          .slice(-5)
          .map((row: any) => {
            const item =
              row?.mediaItem ||
              row?.media_item ||
              row;

            return webMediaStableId(
              item,
              row?.clientMessageId || row?.id
            );
          })
          .filter(Boolean)
      );

      const eligibleIds = nearVisibleIds.size
        ? nearVisibleIds
        : fallbackIds;

      const eligibleRows = allMediaRows.filter(
        (row: any) => {
          const item =
            row?.mediaItem ||
            row?.media_item ||
            row;

          const mediaId = webMediaStableId(
            item,
            row?.clientMessageId || row?.id
          );

          return !!mediaId && eligibleIds.has(mediaId);
        }
      );

      if (
        localStorage.getItem(
          "intimsync.messages.debugTools"
        ) === "1"
      ) {
        console.debug(
          "[Messages web] thumbnail viewport batch",
          {
            conversationId,
            totalMediaRows: allMediaRows.length,
            nearVisibleCount: nearVisibleIds.size,
            eligibleCount: eligibleRows.length,
            fallbackUsed: nearVisibleIds.size === 0,
          }
        );
      }

      for (const row of eligibleRows) {
        if (cancelled) return;

        const item =
          (row as any)?.mediaItem ||
          (row as any)?.media_item ||
          row;

        const mediaId = webMediaStableId(
          item,
          (row as any)?.clientMessageId ||
            (row as any)?.id
        );

        if (!mediaId) continue;
        if (webMediaThumbById[mediaId]) continue;

        if (
          webMediaThumbInFlightRef.current.has(mediaId)
        ) {
          continue;
        }

        if (!hasWebManifestThumbnail(item)) {
          continue;
        }

        const thumbInfo =
          inspectWebManifestThumbnail(item, selfId);

        if (
          localStorage.getItem(
            "intimsync.messages.debugTools"
          ) === "1"
        ) {
          console.debug("[web-thumb-debug]", {
            mediaId,
            selfId,
            mimeType:
              item?.mimeType ||
              item?.mime_type,
            senderUserId:
              item?.senderUserId ||
              item?.sender_user_id,
            recipientUserId:
              item?.recipientUserId ||
              item?.recipient_user_id,
            visibilityMode:
              item?.visibilityMode ||
              item?.visibility_mode,
            hasManifestThumbnail:
              hasWebManifestThumbnail(item),
            thumbInfo,
          });
        }

        webMediaThumbInFlightRef.current.add(mediaId);

        try {
          const dataUrl =
            await decryptWebManifestThumbnailToDataUrl({
              item,
              currentUserId: selfId,
            });

          if (!cancelled && dataUrl) {
            setWebMediaThumbById((old) => {
              if (old[mediaId] === dataUrl) {
                return old;
              }

              return {
                ...old,
                [mediaId]: dataUrl,
              };
            });
          }
        } catch {
          // Missing, legacy, or incompatible thumbnails are handled
          // by the bounded negative cache in webManifestThumbnail.ts.
        } finally {
          webMediaThumbInFlightRef.current.delete(mediaId);
        }
      }
    }

    void loadWebManifestThumbnails();

    return () => {
      cancelled = true;
    };
  }, [
    conversationId,
    currentUserId,
    messages,
    webVisibleMediaEpoch,
  ]);



  useEffect(() => {
    setMessages((old: UiMessage[]) => {
      if (!Array.isArray(old) || !old.length) return old;

      const nameByUserId = new Map<string, string>();

      for (const row of old as any[]) {
        const userId = String(
          row?.senderUserId ||
          row?.sender_user_id ||
          row?.fromId ||
          row?.from_id ||
          ""
        ).trim();

        const name = String(
          row?.senderDisplayName ||
          row?.sender_display_name ||
          row?.fromDisplayName ||
          row?.from_display_name ||
          row?.senderUsername ||
          row?.sender_username ||
          row?.username ||
          ""
        ).trim();

        if (
          userId &&
          name &&
          name !== "Member" &&
          name !== "Unknown" &&
          name !== "undefined" &&
          name !== "null"
        ) {
          nameByUserId.set(userId, name);
        }
      }

      let changed = false;

      const next = old.map((row: any) => {
        if (row?.timelineKind !== "media") return row;

        const userId = String(
          row?.senderUserId ||
          row?.sender_user_id ||
          row?.fromId ||
          row?.from_id ||
          row?.mediaItem?.senderUserId ||
          row?.mediaItem?.sender_user_id ||
          ""
        ).trim();

        if (!userId) return row;

        const replacement = nameByUserId.get(userId);
        if (!replacement) return row;

        const current = String(row?.senderDisplayName || "").trim();
        if (current && current !== "Member" && current !== "Unknown") return row;

        changed = true;

        return {
          ...row,
          senderDisplayName: replacement,
          mediaItem: row?.mediaItem
            ? {
                ...row.mediaItem,
                senderDisplayName: replacement,
              }
            : row?.mediaItem,
        };
      });

      return changed ? next : old;
    });
  }, [conversationId, messages.length]);



  useEffect(() => {
    if (webMediaAutoFollowPendingRef.current) {
      webMediaAutoFollowPendingRef.current = false;
      stickToBottomRef.current = true;
      userScrolledAwayRef.current = false;
      setUnreadCount(0);

      scrollToBottomSoon();
      requestAnimationFrame(scrollToBottomSoon);
      window.setTimeout(scrollToBottomSoon, 120);
      window.setTimeout(scrollToBottomSoon, 420);
      window.setTimeout(scrollToBottomSoon, 900);
      return;
    }

    if (webMediaUnreadPendingRef.current > 0) {
      const pending = webMediaUnreadPendingRef.current;
      webMediaUnreadPendingRef.current = 0;
      setUnreadCount((old) => Math.max(1, old + pending));
    }
  }, [messages.length, webMediaThumbById]);


  useEffect(() => {
    let cancelled = false;

    async function refreshVisibleMediaCacheFlags() {
      const mediaRows = (messages || [])
        .filter((row: any) => row?.timelineKind === "media")
        .slice(-40);

      for (const row of mediaRows) {
        if (cancelled) return;

        const item = (row as any)?.mediaItem || row;
        const mediaId = webMediaStableId(item, (row as any)?.clientMessageId || (row as any)?.id);
        if (!mediaId) continue;

        const manifest = getMediaManifest(item);
        const blobStatus = String(
          (item as any)?.blobStatus ||
          (item as any)?.blob_status ||
          (manifest as any)?.blobStatus ||
          (manifest as any)?.blob_status ||
          ''
        ).toLowerCase();

        if (
          webUnavailableMediaById[mediaId] &&
          (blobStatus === 'uploaded' || blobStatus === 'ready' || blobStatus === 'available')
        ) {
          clearWebUnavailableMediaFlag(mediaId);
        }

        const cached = await refreshWebCachedMediaFlag(mediaId);

        // If a visible normal media row is not cached and the manifest does not
        // provide a clear uploaded/unavailable state, ask the backend once.
        // This prevents old expired media from being labelled "Cloud available".
        if (
          !cached &&
          !isRestrictedOnceMediaItem(item) &&
          !webDeletedMediaById[mediaId] &&
          !webUnavailableMediaById[mediaId] &&
          !webMediaBlobStatusById[mediaId] &&
          !webMediaBlobStatusRequestedRef.current.has(mediaId)
        ) {
          void ensureWebMediaBlobStatus(item, mediaId);
        }
      }

      if (cancelled || webMediaAutoCacheRunningRef.current) return;

      webMediaAutoCacheRunningRef.current = true;

      try {
        const newestNormalRows = mediaRows
          .filter((row: any) => {
            const item = row?.mediaItem || row;
            const mediaId = webMediaStableId(item, row?.clientMessageId || row?.id);
            if (!mediaId) return false;
            if (isRestrictedOnceMediaItem(item)) return false;
            if (webDeletedMediaById[mediaId]) return false;
            if (webUnavailableMediaById[mediaId]) return false;
            if (webCachedMediaById[mediaId]) return false;
            if (webManifestBlobUnavailable(item)) return false;
            return true;
          })
          .slice(-6);

        for (const row of newestNormalRows) {
          if (cancelled) return;

          const item = (row as any)?.mediaItem || row;
          const mediaId = webMediaStableId(item, (row as any)?.clientMessageId || (row as any)?.id);
          if (!mediaId) continue;

          await autoCacheNormalWebMediaBlob(item, mediaId);
        }
      } finally {
        webMediaAutoCacheRunningRef.current = false;
      }
    }

    void refreshVisibleMediaCacheFlags();

    return () => {
      cancelled = true;
    };
  }, [
    conversationId,
    messages.length,
    webCachedMediaById,
    webDeletedMediaById,
    webUnavailableMediaById,
  ]);

  function renderMessageContent(m: UiMessage, callInvite: any, mine: boolean) {
    if (m.timelineKind === "media") {
      const item = normalizeVoiceMediaItem((m as any).mediaItem || m || {});
      const mediaId = webMediaStableId(item, m.clientMessageId || m.id);
      const mimeType = mediaMime(item);
      const kind = webMediaKindLabel(item);
      let restrictedOnce = isRestrictedOnceMediaItem(item);
      const isVideo = isVideoMediaItem(item);
      const isAudio = isAudioMediaItem(item);
      const isImage = kind === 'image' || mimeType.startsWith('image/');
      const url = webMediaUsableUrl(item);
      const mediaThumb = !restrictedOnce && mediaId ? (webMediaThumbById[mediaId] || webVideoThumbById[mediaId] || '') : '';
      const videoThumb = isVideo ? mediaThumb : '';
      const imageThumb = isImage ? mediaThumb : '';

      const title =
        restrictedOnce
          ? `1x encrypted ${kind}`
          : webMediaTitle(item);

      const fullMediaCachedOnThisDevice = !!(mediaId && webCachedMediaById[mediaId]);
      const fullMediaServerUnavailable =
        !!(mediaId && webUnavailableMediaById[mediaId]) || webManifestBlobUnavailable(item);
      const mediaStorageLabel = restrictedOnce
        ? "1x media"
        : fullMediaCachedOnThisDevice
          ? "Stored on this device"
          : fullMediaServerUnavailable
            ? "Preview only — full media unavailable"
            : "Cloud available — not stored yet";

      if (mediaId && webDeletedMediaById[mediaId]) {
        return null;
      }

      if (
        !restrictedOnce &&
        mediaId &&
        (webUnavailableMediaById[mediaId] || webManifestBlobUnavailable(item))
      ) {
        return (
          <div
            style={{
              marginTop: 2,
              maxWidth: 300,
              border: "1px solid rgba(148,163,184,0.20)",
              borderRadius: 12,
              padding: "0.55rem 0.7rem",
              background: "rgba(15,23,42,0.28)",
            }}
          >
            <div style={{ fontWeight: 900 }}>
              {kind} unavailable
            </div>
            <div className="text-muted small" style={{ marginTop: 4 }}>
              Preview only. The full encrypted media is not stored on this device and the server copy is no longer available.
            </div>
            <div className="chip-row" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn-outline"
                onClick={() => {
                  if (window.confirm("Remove this unavailable media card for you?")) {
                    void deleteMessageFromWeb(m, "me");
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        );
      }

      if (isConsumedOrUnavailableOnceWebMedia(item) || (restrictedOnce && mediaId && (webConsumedOnceMediaById[mediaId] || hasWebOnceConsumed(mediaId)))) {
        return (
          <div
            style={{
              marginTop: 2,
              maxWidth: 280,
              border: "1px solid rgba(148,163,184,0.20)",
              borderRadius: 12,
              padding: "0.55rem 0.7rem",
              background: "rgba(15,23,42,0.28)",
            }}
          >
            <div style={{ fontWeight: 900 }}>
              1x {kind}
            </div>
            <div className="text-muted small" style={{ marginTop: 4 }}>
              Received one-time media. It is no longer openable.
            </div>
            <div className="chip-row" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn-outline"
                onClick={() => {
                  if (window.confirm("Remove this 1x media card for you?")) {
                    void deleteMessageFromWeb(m, "me");
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        );
      }

      if (isImage && imageThumb) {
        return (
          <div
            style={{
              marginTop: 2,
              width: 260,
              maxWidth: "100%",
              border: "1px solid rgba(148,163,184,0.22)",
              borderRadius: 14,
              overflow: "hidden",
              background: "rgba(15,23,42,0.44)",
            }}
          >
            <button
              type="button"
              onClick={() => void openWebMediaPreview(item, mediaId, m, mine)}
              style={{
                display: "block",
                width: "100%",
                padding: 0,
                border: 0,
                color: "inherit",
                textAlign: "left",
                background: "transparent",
                cursor: "pointer",
              }}
            >
              <img
                src={imageThumb}
                alt=""
                style={{
                  width: "100%",
                  height: 180,
                  objectFit: "cover",
                  display: "block",
                  background: "rgba(2,6,23,0.72)",
                }}
              />

            </button>
          </div>
        );
      }

      return (
        <div
          style={{
            marginTop: 2,
            width: isImage || isVideo ? 260 : 240,
            maxWidth: "100%",
            minHeight: isImage || isVideo ? 180 : 92,
            border: "1px solid rgba(148,163,184,0.22)",
            borderRadius: 14,
            overflow: "hidden",
            background: "rgba(15,23,42,0.44)",
          }}
        >
          <button
            type="button"
            onClick={() => void openWebMediaPreview(item, mediaId, m, mine)}
            style={{
              display: "block",
              width: "100%",
              minHeight: isImage || isVideo ? 180 : 92,
              padding: 0,
              border: 0,
              color: "inherit",
              textAlign: "left",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            {isVideo ? (
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: 180,
                  background: "rgba(2,6,23,0.72)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {videoThumb ? (
                  <img
                    src={videoThumb}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : (
                  <div style={{ fontSize: "2.4rem", opacity: 0.9 }}>🎬</div>
                )}

                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    background: videoThumb ? "rgba(0,0,0,0.18)" : "transparent",
                  }}
                >
                  <div
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: 999,
                      display: "grid",
                      placeItems: "center",
                      background: "rgba(0,0,0,0.54)",
                      border: "1px solid rgba(255,255,255,0.35)",
                      color: "#fff",
                      fontWeight: 900,
                      fontSize: "1.5rem",
                      paddingLeft: 3,
                    }}
                  >
                    ▶
                  </div>
                </div>

                {restrictedOnce ? (
                  <div
                    style={{
                      position: "absolute",
                      left: 10,
                      top: 10,
                      borderRadius: 999,
                      padding: "0.22rem 0.5rem",
                      background: "rgba(15,23,42,0.78)",
                      border: "1px solid rgba(255,255,255,0.22)",
                      fontWeight: 900,
                      fontSize: "0.72rem",
                    }}
                  >
                    1x
                  </div>
                ) : null}
              </div>
            ) : isImage && mediaThumb && !restrictedOnce ? (
              <img
                src={mediaThumb}
                alt=""
                style={{
                  width: "100%",
                  height: 180,
                  objectFit: "cover",
                  display: "block",
                  background: "rgba(2,6,23,0.72)",
                }}
              />
            ) : isImage && url && !restrictedOnce ? (
              <img
                src={url}
                alt=""
                style={{
                  width: "100%",
                  height: 180,
                  objectFit: "cover",
                  display: "block",
                  background: "rgba(2,6,23,0.72)",
                }}
              />
            ) : (
              <div
                style={{
                  minHeight: 92,
                  padding: "0.75rem",
                  display: "grid",
                  gridTemplateColumns: "auto minmax(0, 1fr)",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <div style={{ fontSize: "1.7rem" }}>
                  {isAudio ? "🎙️" : isImage ? "🖼️" : "📎"}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 900 }}>
                    {title}
                  </div>
                  <div className="text-muted small" style={{ marginTop: 4 }}>
                    {restrictedOnce
                      ? "Can only be opened once. Sharing/download disabled."
                      : mimeType || "encrypted media"}
                  </div>
                </div>
              </div>
            )}
          </button>

          <div style={{ padding: "0.65rem 0.75rem" }}>
            <div style={{ fontWeight: 900 }}>
              {title}
            </div>
            <div className="text-muted small" style={{ marginTop: 4 }}>
              {restrictedOnce
                ? "1x media: no thumbnail, share, copy, download, or save."
                : mimeType || "encrypted media"}
            </div>
            {!restrictedOnce && (
              <div className="text-muted small" style={{ marginTop: 4 }}>
                {mediaStorageLabel}
              </div>
            )}
            
          </div>
        </div>
      );
    }

    return callInvite ? compactCallInviteTextForWeb(callInvite, mine) : renderMessageBodyWithLinks(m.body);
  }


  async function sendWebTypingHint() {
    if (!conversationId) {
      console.log("[typing] web skip: missing conversationId");
      return;
    }

    const now = Date.now();
    if (now - typingHintLastSentAtRef.current < 1800) return;

    typingHintLastSentAtRef.current = now;

    try {
      console.log("[typing] web send", { conversationId });

      await sendTypingHintWeb({
        conversationId,
        typing: true,
      });
    } catch (e) {
      console.warn("[typing] web send failed", e);
    }
  }

  function quoteMessage(m: UiMessage) {
    setReplyTo(m);
    setTimeout(() => {
      const el = document.querySelector<HTMLTextAreaElement>('textarea[aria-label="Message"]');
      el?.focus();
    }, 0);
  }

  function openMediaFlow(flow: Exclude<MediaFlow, null>) {
    setMediaFlow(flow);
    setTemporaryMode("normal");

    if (flow === "camera") setMediaKind("photo");
    if (flow === "voice") setMediaKind("voice");
    if (flow === "request") {
      setMediaKind("photo");

      if (chatMode === "private" && visiblePrivateId) {
        setSelectedMediaRequestTargetId(visiblePrivateId);
      } else if (mediaRequestTargets.length === 1) {
        setSelectedMediaRequestTargetId(memberId(mediaRequestTargets[0]));
      } else {
        setSelectedMediaRequestTargetId("");
      }
    }
    if (flow === "attach") setMediaKind(null);

    setStatusMsg("Media/call scaffold: live voice/video/image collaboration should use P2P WebRTC signaling; leave-a-message voice/video/image should use encrypted upload.");
  }

  function mediaKindLabel(kind: typeof mediaKind) {
    if (kind === "photo") return "photo";
    if (kind === "video") return "video";
    if (kind === "voice") return "voice";
    return "media";
  }

  function mediaSendModeLabel(mode: TemporaryMode) {
    return mode === "view_once" ? "1x private photo" : "∞ normal";
  }

  function findMemberById(userId: string | number | null | undefined) {
    const id = String(userId || "");
    if (!id) return null;
    return members.find((m) => memberId(m) === id) || null;
  }

  function userLabelById(userId: string | number | null | undefined) {
    const id = String(userId || "");
    if (!id) return "Unknown";
    if (id === currentUserId) return "You";
    const member = findMemberById(id);
    return member ? memberLabel(member) : `User ${id}`;
  }

  function partnerSetupHref(memberOrUserId: GroupMember | string) {
    const q = new URLSearchParams();

    if (typeof memberOrUserId === "string") {
      if (memberOrUserId) q.set("targetUserId", memberOrUserId);
    } else {
      const id = memberId(memberOrUserId);
      const publicId = memberOrUserId.public_id || "";

      if (id) q.set("targetUserId", id);
      if (publicId) q.set("invitee_public_id", publicId);
      if (publicId) q.set("targetPublicId", publicId);
      q.set("targetLabel", memberLabel(memberOrUserId));
    }

    if (params.originGroupId) q.set("originGroupId", params.originGroupId);
    return `/partners?${q.toString()}`;
  }

  function isPartnerGroupContext() {
    const type = String(summary?.group?.group_type || summary?.group?.kind || "");
    const groupId = String(params.originGroupId || "");
    return type === "partner_group" || groupId.startsWith("group_partner_");
  }

  function memberPartnerStatus(member: GroupMember) {
    return String(
      member.partner_status ||
      member.partner_link_status ||
      ""
    ).toLowerCase();
  }

  function isCurrentPartnerMember(member: GroupMember) {
    const status = memberPartnerStatus(member);

    return (
      boolish(member.is_partner, false) ||
      boolish(member.is_current_partner, false) ||
      status === "active" ||
      status === "accepted" ||
      status === "partner" ||
      !!member.partner_link_id
    );
  }

  function canAskMemberToPartner(member: GroupMember) {
    const id = memberId(member);
    if (!id || id === currentUserId) return false;
    if (isCurrentPartnerMember(member)) return false;

    if ((member as any).can_request_partner === false || (member as any).can_request_partner === 0) {
      return false;
    }

    // If user already has a partner, multi-partner additions require unlock + consent flow.
    if (!boolish((member as any).multi_partner_unlocked, false) && boolish((member as any).can_add_partner, false) === false) {
      return false;
    }

    return true;
  }

  function partnerActionLabel(member: GroupMember) {
    if (isCurrentPartnerMember(member)) return "Partner ✓";

    // Future backend rule:
    // If the user already has a partner, adding more partners should require
    // entitlement + consent from all active partners.
    if (!boolish(member.multi_partner_unlocked, true) && boolish(member.can_add_partner, false) === false) {
      return "Needs add-on";
    }

    return "Ask partner";
  }


  function mediaRequestTargetLabel() {
    if (chatMode === "private" && visiblePrivateId) {
      return userLabelById(visiblePrivateId);
    }

    if (selectedMediaRequestTargetId) {
      return userLabelById(selectedMediaRequestTargetId);
    }

    if (mediaRequestTargets.length === 1) {
      return memberLabel(mediaRequestTargets[0]);
    }

    return "";
  }

  function mentionedUserIdsForOutgoingTextWeb(
    textWanted: string,
  ) {
    const text = String(textWanted || "");

    if (!text.includes("@")) {
      return [] as string[];
    }

    function escapeRegex(value: string) {
      return value.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&",
      );
    }

    function matchesMention(
      displayName: string,
    ) {
      const words = String(displayName || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      if (!words.length) return false;

      const spaced =
        words
          .map(escapeRegex)
          .join("\\s+");

      const compact =
        escapeRegex(words.join(""));

      const start = "(^|[\\s([{])@";
      const end =
        "(?=$|[\\s.,!?;:)\\]}])";

      if (
        new RegExp(
          start + spaced + end,
          "i",
        ).test(text)
      ) {
        return true;
      }

      return (
        words.length > 1 &&
        new RegExp(
          start + compact + end,
          "i",
        ).test(text)
      );
    }

    const mentioned = new Set<string>();

    for (const member of members) {
      const userId = String(
        memberId(member) || "",
      ).trim();

      if (
        !userId ||
        userId === String(currentUserId || "")
      ) {
        continue;
      }

      const candidateNames = Array.from(
        new Set(
          [
            (member as any)?.display_name,
            (member as any)?.displayName,
            (member as any)?.full_name,
            (member as any)?.fullName,
            (member as any)?.nickname,
            (member as any)?.name,
            (member as any)?.username,
            memberLabel(member),
          ]
            .map((value) =>
              String(value || "").trim(),
            )
            .filter(Boolean),
        ),
      );

      if (
        candidateNames.some(
          matchesMention,
        )
      ) {
        mentioned.add(userId);
      }
    }

    return Array.from(mentioned);
  }


  function mentionedUserIdsForOutgoingText(
    textWanted: string,
  ) {
    const text = String(
      textWanted || "",
    );

    if (!text.includes("@")) {
      return [] as string[];
    }

    const selfId = String(
      currentUserId || "",
    ).trim();

    function normalizeMentionValue(
      value: unknown,
    ) {
      return String(value || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase()
        .replace(/[^\p{L}\p{N}@]+/gu, " ")
        .replace(/\s+/g, " ")
        .trim();
    }

    function aliasesForMember(
      member: GroupMember,
    ) {
      const raw = member as any;

      const rawAliases = [
        memberLabel(member),
        raw?.display_name,
        raw?.displayName,
        raw?.display_label,
        raw?.displayLabel,
        raw?.name,
        raw?.username,
        raw?.public_id,
        raw?.publicId,
        raw?.email,
        raw?.user?.display_name,
        raw?.user?.displayName,
        raw?.user?.display_label,
        raw?.user?.displayLabel,
        raw?.user?.name,
        raw?.user?.username,
        raw?.user?.public_id,
        raw?.user?.publicId,
        raw?.user?.email,
        raw?.profile?.display_name,
        raw?.profile?.displayName,
        raw?.profile?.display_label,
        raw?.profile?.displayLabel,
        raw?.profile?.name,
        raw?.profile?.username,
        raw?.profile?.public_id,
        raw?.profile?.publicId,
        raw?.profile?.email,
      ];

      const aliases = new Set<string>();

      for (const rawAlias of rawAliases) {
        const value = String(
          rawAlias || "",
        ).trim();

        if (!value) continue;

        aliases.add(value);

        if (value.includes("@")) {
          const localPart = value
            .split("@")[0]
            .trim();

          if (localPart) {
            aliases.add(localPart);
          }
        }
      }

      return Array.from(aliases)
        .map((alias) =>
          normalizeMentionValue(alias)
            .replace(/^@+/, "")
            .trim(),
        )
        .filter(
          (alias) =>
            !!alias &&
            alias !== "member" &&
            alias !== "unknown" &&
            alias !== "you",
        );
    }

    const normalizedText =
      normalizeMentionValue(text);

    type MentionMatch = {
      start: number;
      length: number;
      userId: string;
      alias: string;
    };

    const matches: MentionMatch[] = [];
    const candidates: Array<{
      userId: string;
      aliases: string[];
    }> = [];

    function recordMatches(
      userId: string,
      alias: string,
    ) {
      const forms = new Set<string>([
        alias,
        alias.replace(/\s+/g, ""),
      ]);

      for (const form of forms) {
        if (!form) continue;

        const needle = `@${form}`;
        let from = 0;

        while (from < normalizedText.length) {
          const index = normalizedText.indexOf(
            needle,
            from,
          );

          if (index < 0) break;

          const before =
            index > 0
              ? normalizedText[index - 1]
              : "";

          const afterIndex =
            index + needle.length;

          const after =
            afterIndex < normalizedText.length
              ? normalizedText[afterIndex]
              : "";

          const beforeBoundary =
            !before || before === " ";

          const afterBoundary =
            !after || after === " ";

          if (
            beforeBoundary &&
            afterBoundary
          ) {
            matches.push({
              start: index,
              length: needle.length,
              userId,
              alias: form,
            });
          }

          from = index + 1;
        }
      }
    }

    for (const member of members || []) {
      const userId = String(
        memberId(member) || "",
      ).trim();

      if (
        !userId ||
        userId === selfId
      ) {
        continue;
      }

      const aliases =
        aliasesForMember(member);

      candidates.push({
        userId,
        aliases,
      });

      for (const alias of aliases) {
        recordMatches(
          userId,
          alias,
        );
      }
    }

    const longestByStart =
      new Map<number, MentionMatch>();

    for (const match of matches) {
      const current =
        longestByStart.get(match.start);

      if (
        !current ||
        match.length > current.length
      ) {
        longestByStart.set(
          match.start,
          match,
        );
      }
    }

    const mentionedUserIds =
      Array.from(
        new Set(
          Array.from(
            longestByStart.values(),
          ).map((match) =>
            match.userId,
          ),
        ),
      );

    console.log(
      "[Messages web] mention resolver details",
      {
        currentUserId: selfId,
        candidates,
        matches:
          Array.from(
            longestByStart.values(),
          ),
        mentionedUserIds,
      },
    );

    return mentionedUserIds;
  }


  async function sendPlainTextMessage(plainTextInput: string, opts?: {
    clearDraft?: boolean;
    statusPrefix?: string;
  }) {
    if (sending) return false;

    const prepared = prepareOutgoingText(plainTextInput);
    const plain = prepared.body.trim();
    if (!plain || !conversationId) return false;

    setSending(true);

    const clientMessageId = makeClientMessageId();

    try {
      const enc = await encryptForSend(plain, clientMessageId);

      if (chatMode === "group") {
        if (!allRecipientIds.length) {
          setStatusMsg("No group recipients available.");
          return;
        }

        const mentionedUserIds =
          mentionedUserIdsForOutgoingText(
            plain,
          );

        console.log(
          '[Messages web] outgoing mention routing',
          {
            groupId:
              params.originGroupId,
            currentUserId,
            mentionedUserIds,
            memberNames:
              (members || []).map((member) =>
                memberLabel(member),
              ),
          },
        );

        await sendMessageE2EE({
          conversationId,
          contextId: conversationId,
          clientMessageId,
          conversationType: params.originActivityId ? "activity" : "group",
          originGroupId: params.originGroupId,
          originActivityId: params.originActivityId,
          mentionedUserIds,
          recipientEnvelopes: allRecipientIds.map((recipientUserId) => ({
            recipientUserId,
            envelope: {
              version: Number((enc as any).version || 1),
              algorithm: (enc as any).algorithm || "DEV-PLAINTEXT-BASE64",
              ciphertext: enc.cipherText,
              nonce: enc.iv,
              senderKeyId: (enc as any).senderKeyId || "web-dev-plain",
            },
          })),
        });

        if (opts?.clearDraft !== false) {
          setDraft("");
          setReplyTo(null);
        }
        setAckedClientMessageIds((prev) => new Set(prev).add(clientMessageId));
        setStatusMsg(opts?.statusPrefix || `Message sent to group (${allRecipientIds.length} members).`);
        await loadMessages({ force: true, realtime: true, source: 'send' });
        setTimeout(scrollToBottom, 0);
        return true;
      }

      const recipientUserId = visiblePrivateId;
      if (!recipientUserId) {
        setStatusMsg("Select a member for private message.");
        return false;
      }

      const selectedMember = members.find((m) => memberId(m) === recipientUserId);
      if (!selectedMember || !canPrivateContactMember(selectedMember)) {
        setStatusMsg("This member is not available for private messages from this group.");
        await refreshGroupMembers().catch(() => {});
        return false;
      }

      const privateId = privateConversationId(params, recipientUserId);

      await sendMessageE2EE({
        toUserId: recipientUserId,
        recipientUserId,
        conversationId: privateId,
        contextId: privateId,
        clientMessageId,
        conversationType: "direct",
        version: Number((enc as any).version || 1),
        algorithm: (enc as any).algorithm || "DEV-PLAINTEXT-BASE64",
        cipherText: enc.cipherText,
        iv: enc.iv,
        senderKeyId: (enc as any).senderKeyId || "web-dev-plain",
        originGroupId: params.originGroupId,
        originActivityId: params.originActivityId,
      });

      if (opts?.clearDraft !== false) {
        setDraft("");
        setReplyTo(null);
      }
      setAckedClientMessageIds((prev) => new Set(prev).add(clientMessageId));
      setStatusMsg(opts?.statusPrefix || "Private message sent.");
      await loadMessages({ force: true, realtime: true, source: 'send' });
      setTimeout(scrollToBottom, 0);
      return true;
    } catch (e: any) {
      setStatusMsg(`Send failed: ${e?.message || "server error"}`);
      return false;
    } finally {
      setSending(false);
    }
  }

  async function handleSend() {
    await sendPlainTextMessage(draft, { clearDraft: true });
  }

  async function postLiveKitCallInviteToChat(invite: {
    callId: string;
    conversationId: string;
    originGroupId: string;
    callKind: 'voice' | 'video';
    roomName?: string;
  }) {
    const q = new URLSearchParams();
    q.set("callId", invite.callId);
    q.set("conversationId", invite.conversationId || activeConversationIdForRealtime());
    q.set("originGroupId", invite.originGroupId || params.originGroupId || "");
    q.set("callKind", invite.callKind || "voice");
    q.set("transport", "sfu_livekit");
    q.set("routingMode", "sfu_livekit");

    const path = `/messages?${q.toString()}`;
    const webUrl = `${window.location.origin}${path}`;
    const mobileUrl = `intimsync://call?${q.toString()}`;
    const label = invite.callKind === "video" ? "Video call" : "Voice call";

    await sendPlainTextMessage(
      `📞 ${label} started\nWeb: ${webUrl}\nMobile: ${mobileUrl}\nCall ID: ${invite.callId}`,
      {
        clearDraft: false,
        statusPrefix: `${label} invite posted to chat.`,
      }
    );

    void refreshWebCallStatuses();
  }



  function compactCallDurationLabel(msLike: unknown) {
    const ms = Number(msLike || 0);
    if (!Number.isFinite(ms) || ms <= 0) return "";
    const minutes = Math.max(1, Math.round(ms / 60000));
    return `${minutes} min`;
  }

  
  async function fetchWebCallStatuses(conversationId: string) {
    const q = new URLSearchParams();
    q.set("conversationId", conversationId);
    q.set("history", "1");

    const res = await fetch(`/api/v1/messages/calls?${q.toString()}`, {
      credentials: "include",
      headers: messageRuntimeAuthHeaders(),
    });

    if (!res.ok) {
      throw new Error(`call_status_fetch_failed_${res.status}`);
    }

    return await res.json();
  }

  const refreshWebCallStatuses = useCallback(async () => {
    if (!conversationId) {
      setCallStatusById({});
      return;
    }

    try {
      const data = await fetchWebCallStatuses(conversationId);
      const items = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.data?.items)
          ? data.data.items
          : [];

      const next: Record<string, any> = {};
      for (const item of items) {
        const callId = item?.callId || item?.call_id;
        if (callId) next[String(callId)] = item;
      }

      setCallStatusById(next);
    } catch {
      setCallStatusById({});
    }
  }, [conversationId]);

  useEffect(() => {
    void refreshWebCallStatuses();
  }, [refreshWebCallStatuses]);

  function isCallInviteExpiredForWeb(invite: any) {
    const raw =
      invite?.expiresAt ||
      invite?.expires_at ||
      invite?.endedAt ||
      invite?.ended_at ||
      invite?.payload?.expiresAt ||
      invite?.payload?.expires_at ||
      "";

    if (raw) {
      const ts = Date.parse(String(raw));
      if (Number.isFinite(ts)) return ts < Date.now();
    }

    const callId = String(invite?.callId || invite?.call_id || invite?.payload?.callId || "");
    const m = callId.match(/^call_(\d{10,})_/);
    if (m?.[1]) {
      const idTs = Number(m[1]);
      if (Number.isFinite(idTs)) return Date.now() - idTs > 15 * 60 * 1000;
    }

    return false;
  }

  function webActiveCallLabel(invite: any) {
    const kind = callInviteKindLabelForWeb(invite);
    if (kind === "video call") return "Join video call";
    if (kind === "voice call") return "Join voice call";
    if (kind === "collab call") return "Join collab call";
    return "Join call";
  }

  function callInviteKindLabelForWeb(invite: any) {
    const raw = String(
      invite?.callKind ||
      invite?.call_kind ||
      invite?.callType ||
      invite?.call_type ||
      invite?.mediaKind ||
      invite?.media_kind ||
      invite?.kind ||
      invite?.payload?.callKind ||
      invite?.payload?.call_kind ||
      invite?.payload?.callType ||
      invite?.payload?.call_type ||
      invite?.payload?.mediaKind ||
      invite?.payload?.media_kind ||
      ""
    ).toLowerCase();

    if (raw.includes("video")) return "video call";
    if (raw.includes("voice") || raw.includes("audio")) return "voice call";
    if (raw.includes("collab")) return "collab call";
    return "call";
  }

  function compactCallInviteTextForWeb(invite: any, mine: boolean) {
    const callId = String(
      invite?.callId ||
      invite?.call_id ||
      invite?.payload?.callId ||
      invite?.payload?.call_id ||
      ""
    );

    const summary = callId ? callStatusById[callId] : null;
    const kind = callInviteKindLabelForWeb(summary || invite);
    const icon = kind.includes("video") ? "🎥" : kind.includes("voice") ? "🎙" : "📞";
    const myStatus = String(summary?.myStatus || summary?.my_status || "").toLowerCase();

    const duration = compactCallDurationLabel(
      summary?.durationMs ||
      summary?.duration_ms ||
      invite?.durationMs ||
      invite?.duration_ms ||
      invite?.payload?.durationMs ||
      invite?.payload?.duration_ms ||
      null
    );

    if (myStatus === "joined" || myStatus === "left") {
      return `${icon} Joined ${kind}${duration ? ` · ${duration}` : ""}`;
    }

    if (myStatus === "missed") {
      return `${icon} Missed ${kind}`;
    }

    if (myStatus === "declined") {
      return `${icon} Declined ${kind}`;
    }

    const neutral = `${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;
    return `${icon} ${neutral}${duration ? ` · ${duration}` : ""}`;
  }

  const typingUsersLabel = () => {
    const now = Date.now();
    const labels = Object.values(typingUsersById)
      .filter((x) => x.until > now)
      .map((x) => x.label)
      .slice(0, 3);

    if (!labels.length) return "";
    if (labels.length === 1) return `${labels[0]} is typing…`;
    if (labels.length === 2) return `${labels[0]} and ${labels[1]} are typing…`;
    return `${labels[0]}, ${labels[1]} and others are typing…`;
  };

  const handleTypingRealtimeEvent = (event: MessageRealtimeEvent) => {
    if (!event || String(event.conversationId || "") !== String(conversationId)) return;

    const userId = String(
      event.userId ||
      event.user_id ||
      event.senderUserId ||
      event.fromUserId ||
      ""
    ).trim();

    if (!userId || userId === String(currentUserId || "")) return;

    const label =
      String(
        event.displayName ||
        event.display_name ||
        event.senderDisplayName ||
        event.sender_display_name ||
        ""
      ).trim() || `Member ${userId}`;

    setTypingUsersById((old) => ({
      ...old,
      [userId]: {
        label,
        until: Date.now() + 6000,
      },
    }));

    if (typingClearTimerRef.current) {
      window.clearTimeout(typingClearTimerRef.current);
    }

    typingClearTimerRef.current = window.setTimeout(() => {
      const now = Date.now();

      setTypingUsersById((old) => {
        const next: Record<string, { label: string; until: number }> = {};

        for (const [id, value] of Object.entries(old)) {
          if (value.until > now) next[id] = value;
        }

        return next;
      });
    }, 6500);
  };






  function scheduleOlderMessagesCooldown(ms = 30000) {
    const until = Date.now() + ms;
    setOlderMessagesCooldownUntil(until);

    if (olderMessagesCooldownTimerRef.current) {
      window.clearTimeout(olderMessagesCooldownTimerRef.current);
    }

    olderMessagesCooldownTimerRef.current = window.setTimeout(() => {
      setOlderMessagesCooldownUntil(0);
      olderMessagesCooldownTimerRef.current = null;
    }, ms + 250);
  }

  async function loadOlderMessages() {
    if (!conversationId) return;
    if (olderMessagesLoading) return;
    if (!olderMessagesHasMore || !olderMessagesBeforeId) return;

    const now = Date.now();
    const backoffUntil = Math.max(
      Number(webInboxBackoffUntil || 0),
      Number(olderMessagesCooldownUntil || 0)
    );

    if (backoffUntil > now) {
      const waitSeconds = Math.ceil((backoffUntil - now) / 1000);
      setStatusMsg(`Please wait ${waitSeconds}s before loading older messages again.`);
      return;
    }

    setOlderMessagesLoading(true);
    scheduleOlderMessagesCooldown(30000);

    try {
      await loadMessages({
        force: true,
        realtime: false,
        source: "older",
        beforeId: olderMessagesBeforeId,
        appendOlder: true,
      });
    } finally {
      setOlderMessagesLoading(false);
    }
  }


  const normalizedChatSearchQuery = chatSearchQuery.trim().toLowerCase();

  const chatSearchHits = useMemo(() => {
    if (!normalizedChatSearchQuery) return [];

    return messages
      .map((m, index) => ({
        id: String(m.id || m.clientMessageId || index),
        index,
        message: m,
      }))
      .filter((item) => {
        const haystack = [
          item.message.body,
          item.message.senderDisplayName,
          item.message.createdAt,
        ]
          .join(" ")
          .toLowerCase();

        return haystack.includes(normalizedChatSearchQuery);
      });
  }, [messages, normalizedChatSearchQuery]);

  const visibleMessages = useMemo(() => {
    if (!normalizedChatSearchQuery || !chatSearchFilterOnly) return messages;
    const ids = new Set(chatSearchHits.map((x) => x.id));
    return messages.filter((m, index) => ids.has(String(m.id || m.clientMessageId || index)));
  }, [messages, normalizedChatSearchQuery, chatSearchFilterOnly, chatSearchHits]);



  const currentChatSearchHit = chatSearchHits.length
    ? chatSearchHits[Math.max(0, Math.min(chatSearchIndex, chatSearchHits.length - 1))]
    : null;

  function jumpToChatSearchHit(nextIndex: number) {
    if (!chatSearchHits.length) return;

    const bounded = ((nextIndex % chatSearchHits.length) + chatSearchHits.length) % chatSearchHits.length;
    setChatSearchIndex(bounded);

    const hit = chatSearchHits[bounded];
    window.setTimeout(() => {
      const el = messageRowRefs.current[hit.id];
      el?.scrollIntoView?.({
        block: "center",
        behavior: "smooth",
      });
    }, 0);
  }

  useEffect(() => {
    setChatSearchIndex(0);
  }, [normalizedChatSearchQuery, chatSearchHits.length]);



  const title = contextTitle(summary, params);
  const activePrivateLabels = selectedPrivateIds
    .map((id) => otherMembers.find((m) => memberId(m) === id))
    .filter(Boolean)
    .map((m) => memberLabel(m as GroupMember));

  const memberCountLabel = `(${members.length})`;

  const partnerChannels = channels.filter((channel) => channel.is_partner);
  const otherChannels = channels.filter((channel) => !channel.is_partner);

  const liveKitDisplayNameByIdentity = useMemo(() => {
    const map: Record<string, string> = {};

    for (const member of members) {
      const id = memberId(member);
      const label = memberLabel(member);
      const publicId = String((member as any).public_id || (member as any).publicId || "");

      if (id && label) {
        map[id] = label;
        map[`user_${id}`] = label;
      }

      if (publicId && label) {
        map[publicId] = label;
        map[`user_${publicId}`] = label;
      }
    }

    if (currentUserId) {
      map[currentUserId] = "You";
      map[`user_${currentUserId}`] = "You";
    }

    return map;
  }, [members, currentUserId]);



  return (
    <div style={pageShellStyle}>
      <div
        className="card"
        style={{
          ...chatPanelStyle,
          marginInline: 0,
          paddingInline: 0,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <div
          className="section"
          style={{
            paddingTop: 12,
            paddingBottom: 10,
            borderBottom: "1px solid rgba(148,163,184,0.16)",
          }}
        >
          {!!statusMsg && (
            <div className="text-xs text-emerald-300 bg-emerald-950/30 border border-emerald-800 rounded-lg px-3 py-2 mb-2">
              {statusMsg}
            </div>
          )}

          {!!error && (
            <div className="text-xs text-rose-300 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2 mb-2">
              {error}
            </div>
          )}

          {(params.questInviteId || params.activityInviteId || params.messageId) && (
            <div
              className="text-xs"
              style={{
                marginBottom: 8,
                border: "1px solid rgba(148,163,184,0.22)",
                background: "rgba(15,23,42,0.72)",
                color: "var(--text)",
                borderRadius: 12,
                padding: "0.55rem 0.7rem",
              }}
            >
              {params.questInviteId
                ? "Opened from a quest invite notification."
                : params.activityInviteId
                ? "Opened from an activity notification."
                : "Opened from a message notification."}
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr)",
              gap: 10,
              alignItems: "center",
              minWidth: 0,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <div style={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 8,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    fontWeight: 900,
                    fontSize: "1rem",
                    lineHeight: 1.15,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                >
                  {title}
                </div>

                {!!params.originGroupId && (
                  <button
                    type="button"
                    className="btn-ghost"
                    style={{
                      minHeight: "auto",
                      padding: 0,
                      fontWeight: 800,
                      opacity: 0.85,
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => setMembersOpen((v) => !v)}
                    title="Show members"
                  >
                    {memberCountLabel}
                  </button>
                )}
              </div>

              <div className="text-muted small" style={{ marginTop: 4 }}>
                {chatMode === "group"
                  ? "Group chat"
                  : `1:1 with ${activePrivateLabels[0] || "member"}`}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(36px, 1fr))",
              gap: 6,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <button
              className={chatMode === "group" ? "btn-primary" : "btn-outline"}
              onClick={() => setChatMode("group")}
              style={{ ...iconButtonStyle, minHeight: 28, width: "100%", minWidth: 0 }}
              title="Group chat"
            >
              {actionContent("💬", "Chat")}
            </button>

            <button
              className={chatMode === "private" ? "btn-primary" : "btn-outline"}
              onClick={() => {
                setChatMode("private");
                setMembersOpen(true);
              }}
              style={{ ...iconButtonStyle, minHeight: 28, width: "100%", minWidth: 0 }}
              disabled={!otherMembers.length}
              title="Private 1:1 chat"
            >
              {actionContent("🔒", "1:1")}
            </button>

            <button
              type="button"
              className="btn-outline"
              onClick={openChannelsDrawer}
              style={{ ...iconButtonStyle, minHeight: 28, width: "100%", minWidth: 0 }}
              title="Channels"
            >
              {actionContent("☰", "Channels")}
            </button>

            <button
              type="button"
              className="btn-outline"
              onClick={() => setShowP2PDebugModal(true)}
              style={{ ...iconButtonStyle, minHeight: 28, width: "100%", minWidth: 0 }}
              title="Test voice, video, file, and WebRTC signaling"
            >
              {actionContent("📞", "Calls")}
            </button>

            {!!params.originGroupId && (
              <>
                <a
                  className="btn-outline"
                  href={groupToolHref(params.originGroupId, "activities")}
                  style={{ ...iconButtonStyle, minHeight: 28, width: "100%", minWidth: 0 }}
                  title="Activities"
                >
                  {actionContent("📅", "Activities")}
                </a>

                <a
                  className="btn-outline"
                  href={groupToolHref(params.originGroupId, "todos")}
                  style={{ ...iconButtonStyle, minHeight: 28, width: "100%", minWidth: 0 }}
                  title="Todos"
                >
                  {actionContent("☑", "Todos")}
                </a>

                <a
                  className="btn-outline"
                  href={`/groups/${encodeURIComponent(params.originGroupId)}?tab=settings`}
                  style={{ ...iconButtonStyle, minHeight: 28, width: "100%", minWidth: 0 }}
                  title="Settings"
                >
                  {actionContent("⚙", "Settings")}
                </a>
              </>
            )}
          </div>

          {chatSearchOpen ? (
            <div
              style={{
                marginTop: 10,
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto auto auto auto",
                gap: 8,
                alignItems: "center",
              }}
            >
              <input
                value={chatSearchQuery}
                onChange={(e) => setChatSearchQuery(e.target.value)}
                placeholder="Search loaded messages"
                aria-label="Search loaded messages"
                autoFocus
                style={{
                  minWidth: 0,
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.24)",
                  background: "rgba(15,23,42,0.66)",
                  color: "inherit",
                  padding: "0.45rem 0.75rem",
                  outline: 0,
                  fontSize: "0.82rem",
                }}
              />

              <button
                type="button"
                className="btn-outline"
                disabled={!chatSearchHits.length}
                onClick={() => jumpToChatSearchHit(chatSearchIndex - 1)}
                title="Previous match"
                style={{ minHeight: 32, borderRadius: 999, paddingInline: 10 }}
              >
                ↑
              </button>

              <button
                type="button"
                className="btn-outline"
                disabled={!chatSearchHits.length}
                onClick={() => jumpToChatSearchHit(chatSearchIndex + 1)}
                title="Next match"
                style={{ minHeight: 32, borderRadius: 999, paddingInline: 10 }}
              >
                ↓
              </button>

              <button
                type="button"
                className={chatSearchFilterOnly ? "btn-primary" : "btn-outline"}
                disabled={!normalizedChatSearchQuery}
                onClick={() => setChatSearchFilterOnly((v: boolean) => !v)}
                title="Only show matching loaded messages"
                style={{ minHeight: 32, borderRadius: 999, paddingInline: 10, fontSize: "0.74rem" }}
              >
                {chatSearchHits.length ? `${Math.min(chatSearchIndex + 1, chatSearchHits.length)}/${chatSearchHits.length}` : "0/0"}
              </button>

              <button
                type="button"
                className="btn-outline"
                onClick={() => {
                  setChatSearchOpen(false);
                  setChatSearchQuery("");
                  setChatSearchFilterOnly(false);
                }}
                title="Close search"
                aria-label="Close search"
                style={{ minHeight: 32, borderRadius: 999, paddingInline: 12, fontWeight: 900 }}
              >
                ×
              </button>
            </div>
          ) : null}

          {membersOpen && (
            <div
              style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: "1px solid rgba(148,163,184,0.14)",
              }}
            >
              <div className="chip-row" style={{ marginBottom: 8 }}>
                <button
                  className="btn-outline"
                  onClick={() => void refreshGroupMembers()}
                  style={{ ...iconButtonStyle, minHeight: 34 }}
                  title="Refresh members"
                >
                  {actionContent("↻", "Refresh")}
                </button>
              </div>

              {chatMode === "group" ? (
                <div
                  style={{
                    display: "grid",
                    gap: 8,
                  }}
                >
                  {members.map((m) => {
                    const id = memberId(m);
                    const isSelf = id === currentUserId;
                    const isPartner = isCurrentPartnerMember(m);
                    const canAskPartner = canAskMemberToPartner(m);

                    return (
                      <div
                        key={`group_member_${id}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) auto",
                          gap: 8,
                          alignItems: "center",
                          border: "1px solid rgba(148,163,184,0.16)",
                          borderRadius: 14,
                          padding: "0.45rem 0.55rem",
                          background: "rgba(15,23,42,0.34)",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 800,
                              fontSize: "0.82rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={memberLabel(m)}
                          >
                            {memberLabel(m)}
                            {isSelf ? " (you)" : ""}
                          </div>

                          <div className="text-muted small" style={{ marginTop: 2 }}>
                            {isSelf
                              ? "You"
                              : isPartner
                              ? "Current partner"
                              : "Group member"}
                          </div>
                        </div>

                        {!isSelf && (
                          isPartner ? (
                            <span
                              style={{
                                border: "1px solid rgba(148,163,184,0.22)",
                                borderRadius: 999,
                                padding: "0.25rem 0.55rem",
                                fontSize: "0.72rem",
                                fontWeight: 850,
                                color: "var(--muted)",
                                whiteSpace: "nowrap",
                              }}
                              title="Already connected as partner"
                            >
                              {partnerActionLabel(m)}
                            </span>
                          ) : canAskPartner ? (
                            <a
                              className="btn-outline"
                              href={partnerSetupHref(m)}
                              title={`Ask ${memberLabel(m)} to become your partner`}
                              style={{
                                minHeight: 30,
                                paddingInline: 10,
                                fontSize: "0.72rem",
                                borderRadius: 999,
                                whiteSpace: "nowrap",
                              }}
                            >
                              Ask partner
                            </a>
                          ) : (
                            <span
                              style={{
                                border: "1px solid rgba(148,163,184,0.18)",
                                borderRadius: 999,
                                padding: "0.25rem 0.55rem",
                                fontSize: "0.72rem",
                                fontWeight: 850,
                                color: "var(--muted)",
                                whiteSpace: "nowrap",
                              }}
                              title="Multi-partner requests require entitlement and consent from existing partners."
                            >
                              Locked
                            </span>
                          )
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <>
                  {!!otherMembers.length && (
                    <div className="chip-row">
                      {otherMembers.map((m) => {
                        const id = memberId(m);
                        const selected = selectedPrivateIds.includes(id);

                        return (
                          <button
                            key={`private_member_${id}`}
                            className={selected ? "btn-primary" : "btn-outline"}
                            onClick={() => selectPrivateRecipient(id)}
                          >
                            {memberLabel(m)}
                            {selected ? " ✓" : ""}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {!otherMembers.length && (
                    <p className="text-muted small">No private contacts available.</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div
          ref={scrollRef}
          onScroll={() => {
            const nearBottom = isScrolledNearBottom();
            stickToBottomRef.current = nearBottom;
            userScrolledAwayRef.current = !nearBottom;

            if (nearBottom) {
              userScrolledAwayRef.current = false;
              setUnreadCount(0);
              lastSeenMessageCountRef.current = messages.length;
            }
          }}
          onWheel={() => {
            stickToBottomRef.current = false;
            userScrolledAwayRef.current = true;
          }}
          onTouchMove={() => {
            stickToBottomRef.current = false;
            userScrolledAwayRef.current = true;
          }}
          className="section"
          style={{
            minHeight: 0,
            minWidth: 0,
            maxWidth: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            paddingTop: 12,
            paddingBottom: 0,
          }}
        >
          {messages.length > 0 && olderMessagesHasMore && (
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
              <button
                className="btn-outline"
                disabled={olderMessagesLoading || olderMessagesCooldownUntil > Date.now()}
                onClick={() => void loadOlderMessages()}
                title="Load older encrypted messages"
              >
                {olderMessagesLoading
                  ? "Loading older…"
                  : olderMessagesCooldownUntil > Date.now()
                    ? "Wait before loading more…"
                    : "Load older messages"}
              </button>
            </div>
          )}

          {loading && !messages.length && (
            <p className="text-muted small">{t("common.loading")}</p>
          )}

          {!loading && !error && !messages.length && (
            <div
              style={{
                minHeight: "100%",
                display: "grid",
                alignContent: "center",
                justifyItems: "center",
                textAlign: "center",
                paddingInline: "1rem",
              }}
            >
              <div
                style={{
                  width: 66,
                  height: 66,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  border: "1px solid rgba(148,163,184,0.22)",
                  background: "rgba(255,255,255,0.06)",
                  fontSize: "1.8rem",
                }}
              >
                👥
              </div>

              <div
                style={{
                  marginTop: 14,
                  fontWeight: 900,
                  fontSize: "1.25rem",
                  maxWidth: "min(92vw, 520px)",
                  overflowWrap: "anywhere",
                  lineHeight: 1.2,
                }}
              >
                {title}
              </div>

              <div className="text-muted small" style={{ marginTop: 8, maxWidth: "92%", overflowWrap: "anywhere" }}>
                {chatMode === "group"
                  ? "This is the beginning of your group conversation."
                  : "This is the beginning of your private conversation in this context."}
              </div>
            </div>
          )}

          {visibleMessages.map((m, visibleIndex) => {
            const previousMessage = visibleIndex > 0 ? visibleMessages[visibleIndex - 1] : null;
            const currentDateKey = webMessageDateKey(m.createdAt);
            const previousDateKey = previousMessage ? webMessageDateKey(previousMessage.createdAt) : "";
            const showDateSeparator = !!currentDateKey && currentDateKey !== previousDateKey;
            const dateSeparatorLabel = showDateSeparator ? webMessageDateLabel(m.createdAt) : "";

            const mine = String(m.senderUserId || "") === currentUserId;
            const stableMessageId = String(m.id || m.clientMessageId || visibleIndex);
            const isSearchHit = !!normalizedChatSearchQuery && chatSearchHits.some((hit) => hit.id === stableMessageId);
            const isCurrentSearchHit = !!currentChatSearchHit && currentChatSearchHit.id === stableMessageId;
            const callInvite = extractLiveKitCallInviteFromText(m.body, {
              conversationId: activeConversationIdForRealtime(),
              originGroupId: params.originGroupId || "",
            });

            const mediaItemForCompact = (m as any)?.mediaItem || (m as any)?.media_item || null;
            const compactMediaId = String(
              mediaItemForCompact?.mediaId ||
              mediaItemForCompact?.media_id ||
              (m as any)?.mediaId ||
              (m as any)?.media_id ||
              ""
            );
            const compactVisibilityMode = String(
              mediaItemForCompact?.visibilityMode ||
              mediaItemForCompact?.visibility_mode ||
              mediaItemForCompact?.policy?.visibilityMode ||
              mediaItemForCompact?.policy?.visibility_mode ||
              ""
            ).toLowerCase();

            const compactDeleteReason = String(
              mediaItemForCompact?.blobDeleteReason ||
              mediaItemForCompact?.blob_delete_reason ||
              mediaItemForCompact?.deleteReason ||
              mediaItemForCompact?.delete_reason ||
              ""
            ).toLowerCase();

            const isCompactViewOnceMedia = !!mediaItemForCompact && (
              compactVisibilityMode === "view_once" ||
              compactDeleteReason.includes("view_once") ||
              compactDeleteReason.includes("view once")
            );

            const compactViewOnceConsumed = isCompactViewOnceMedia && (
              String(mediaItemForCompact?.blobStatus || mediaItemForCompact?.blob_status || "").toLowerCase() === "deleted" ||
              !!(mediaItemForCompact?.blobDeletedAt || mediaItemForCompact?.blob_deleted_at) ||
              !!(mediaItemForCompact?.viewCompletedAt || mediaItemForCompact?.view_completed_at) ||
              mediaItemForCompact?.viewedByMe === true ||
              mediaItemForCompact?.viewed_by_me === true ||
              mediaItemForCompact?.viewedByMe === 1 ||
              mediaItemForCompact?.viewed_by_me === 1 ||
              !!webConsumedOnceMediaById[compactMediaId]
            );

            const compactOwnViewOnce = mine && isCompactViewOnceMedia;
            const compactPrivateStatusState = privateMediaStatusStateForWeb(mediaItemForCompact || m);
            if (isCompactViewOnceMedia) {
              return (
                <div
                  key={stableMessageId}
                  ref={(el) => {
                    messageRowRefs.current[stableMessageId] = el;
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: mine ? "flex-end" : "flex-start",
                    marginBottom: 10,
                    paddingInline: 6,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  {showDateSeparator && <WebMessageDateSeparator label={dateSeparatorLabel} />}
                  <div
                    role={compactOwnViewOnce || compactViewOnceConsumed ? undefined : "button"}
                    tabIndex={compactOwnViewOnce || compactViewOnceConsumed ? -1 : 0}
                    onClick={() => {
                      if (compactOwnViewOnce) {
                        setStatusMsg("You cannot open your own 1x private photo.");
                        return;
                      }

                      if (compactViewOnceConsumed) {
                        setStatusMsg("This view-once media has already expired or been viewed.");
                        return;
                      }

                      void openWebMediaPreview(mediaItemForCompact || m, compactMediaId || m.clientMessageId || m.id, m);
                    }}
                    style={{
                      maxWidth: "min(82%, 420px)",
                      border: compactOwnViewOnce || compactViewOnceConsumed
                        ? "1px solid rgba(148,163,184,0.24)"
                        : "1px solid color-mix(in srgb, var(--primary) 58%, transparent)",
                      borderRadius: 14,
                      padding: "0.58rem 0.72rem",
                      background: compactOwnViewOnce || compactViewOnceConsumed
                        ? "rgba(15,23,42,0.42)"
                        : "color-mix(in srgb, var(--primary) 12%, rgba(15,23,42,0.58))",
                      cursor: compactOwnViewOnce || compactViewOnceConsumed ? "default" : "pointer",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 900,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        minWidth: 0,
                      }}
                    >
                      <PrivateMediaEyeSlashIcon size={22} />
                      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        {compactOwnViewOnce ? "Private photo sent" : compactViewOnceConsumed ? "Photo closed" : "Private photo"}
                      </span>
                    </div>
                    <div className="text-muted small" style={{ marginTop: 3 }}>
                      {compactOwnViewOnce
                        ? "Sent as 1x private photo"
                        : compactViewOnceConsumed
                          ? "No longer available"
                          : "View once"}
                    </div>
                    <div
                      className="text-muted small"
                      style={{
                        marginTop: 6,
                        fontSize: "0.72rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                      }}
                    >
                      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <strong>{mine ? "You" : m.senderDisplayName || "Member"}</strong>
                        {" · "}
                        {webMessageTimeLabel(m.createdAt)}
                      </span>
                      {compactOwnViewOnce ? (
                        <PrivateMediaStatusIcon state={compactPrivateStatusState} size={15} />
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={stableMessageId}
                data-web-media-id={
                  m.timelineKind === "media" && compactMediaId
                    ? compactMediaId
                    : undefined
                }
                ref={(el) => {
                  messageRowRefs.current[stableMessageId] = el;
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: mine ? "flex-end" : "flex-start",
                  marginBottom: 10,
                  paddingInline: 6,
                  width: "100%",
                  boxSizing: "border-box",
                  background: isSearchHit ? "rgba(244,114,182,0.08)" : "transparent",
                  borderRadius: 16,
                }}
              >
                {showDateSeparator && <WebMessageDateSeparator label={dateSeparatorLabel} />}
                <div
                  style={{
                    maxWidth: "min(84%, 720px)",
                    border: isCurrentSearchHit
                      ? "2px solid rgba(244,114,182,0.62)"
                      : isSearchHit
                      ? "1.5px solid rgba(244,114,182,0.46)"
                      : "1px solid rgba(148,163,184,0.24)",
                    borderRadius: 18,
                    padding: "0.62rem 0.78rem",
                    overflowWrap: "anywhere",
                    background: isCurrentSearchHit
                      ? "rgba(244,114,182,0.22)"
                      : mine
                      ? "rgba(59,130,246,0.18)"
                      : "rgba(15,23,42,0.42)",
                    boxShadow: mine
                      ? "0 10px 28px rgba(59,130,246,0.12)"
                      : "none",
                  }}
                >
                  <div className="text-muted small">
                    <strong>{mine ? "You" : m.senderDisplayName || "Member"}</strong>
                    {" · "}
                    {webMessageTimeLabel(m.createdAt)}
                  </div>

                  <div
                    style={{
                      marginTop: 4,
                      whiteSpace: "pre-wrap",
                      fontStyle: m.messageKind === "action" || m.body.startsWith("* ") ? "italic" : "normal",
                    }}
                  >
                    {renderMessageContent(m, callInvite, mine)}
                  </div>

                  {(() => {
                    const invite = extractLiveKitCallInviteFromText(m.body, {
                      conversationId: activeConversationIdForRealtime(),
                      originGroupId: params.originGroupId || "",
                    });

                    if (!invite || isCallInviteExpiredForWeb(invite)) return null;

                    return (
                      <div style={{ marginTop: 10 }}>
                        <a
                          href={liveKitCallHref(invite)}
                          onClick={(event) => {
                            event.preventDefault();
                            window.location.assign(liveKitCallHref(invite));
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 999,
                            padding: "0.45rem 0.75rem",
                            border: "1px solid color-mix(in srgb, var(--primary) 70%, transparent)",
                            background: "color-mix(in srgb, var(--primary) 16%, rgba(15,23,42,0.72))",
                            color: "inherit",
                            textDecoration: "none",
                            fontWeight: 900,
                            fontSize: "0.82rem",
                          }}
                        >
                          {webActiveCallLabel(invite)}
                        </a>
                      </div>
                    );
                  })()}


                  <div
                    style={{
                      marginTop: 6,
                      display: "flex",
                      justifyContent: mine ? "flex-end" : "space-between",
                      gap: 8,
                      alignItems: "center",
                    }}
                  >
                    <>
<button
                      type="button"
                      className="btn-ghost"
                      onClick={() => quoteMessage(m)}
                      title="Reply / quote"
                      style={{
                        minHeight: "auto",
                        padding: "0.15rem 0.35rem",
                        borderRadius: 999,
                        fontSize: "0.68rem",
                        color: "var(--muted)",
                      }}
                    >
                      Reply
                    </button>

                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => void deleteMessageFromWeb(m, "me")}
                      title="Delete for me"
                      style={{
                        minHeight: "auto",
                        padding: "0.15rem 0.35rem",
                        borderRadius: 999,
                        fontSize: "0.68rem",
                        color: "var(--muted)",
                      }}
                    >
                      Delete
                    </button>

                    {mine ? (
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => void deleteMessageFromWeb(m, "everyone")}
                        title="Delete for everyone"
                        style={{
                          minHeight: "auto",
                          padding: "0.15rem 0.35rem",
                          borderRadius: 999,
                          fontSize: "0.68rem",
                          color: "var(--muted)",
                        }}
                      >
                        Delete all
                      </button>
                    ) : null}
                  </>

                    {mine && (
                      <span
                        className="text-muted small"
                        title="Message delivery/read status."
                        style={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}
                      >
                        <PrivateMediaStatusIcon state={deliveryStatusStateForWebMessage(m, mine)} size={15} />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {!!mediaRequests.length && (
            <div
              style={{
                marginBottom: 10,
                display: "grid",
                gap: 8,
              }}
            >
              {mediaRequests.slice(0, 4).map((item) => {
                const direction = mediaRequestDirectionLabel(item);
                const canRespond = canRespondToMediaRequest(item);
                const canCancel = canCancelMediaRequest(item);

                return (
                  <div
                    key={item.id}
                    style={{
                      border: "1px solid rgba(148,163,184,0.18)",
                      borderRadius: 14,
                      padding: "0.65rem 0.75rem",
                      background: "rgba(15,23,42,0.48)",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        gap: 8,
                        alignItems: "start",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 900, fontSize: "0.84rem" }}>
                          {direction}: {item.requestKind} request
                        </div>

                        <div className="text-muted small" style={{ marginTop: 4 }}>
                          From {userLabelById(item.requesterUserId)}
                          {" → "}
                          {userLabelById(item.targetUserId)}
                          {" · "}
                          {item.temporaryMode === "view_once" ? "1x Private photo" : "∞ Normal"}
                          {" · "}
                          {item.status}
                          {item.expiresAt ? ` · expires ${new Date(item.expiresAt).toLocaleDateString()}` : ""}
                        </div>

                        {item.requesterNote && (
                          <div className="text-muted small" style={{ marginTop: 4 }}>
                            “{item.requesterNote}”
                          </div>
                        )}
                      </div>

                      <div className="chip-row" style={{ justifyContent: "flex-end" }}>
                        {canRespond && (
                          <>
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => void handleMediaRequestAction(item, "accepted")}
                            >
                              Accept
                            </button>
                            <button
                              type="button"
                              className="btn-outline"
                              onClick={() => void handleMediaRequestAction(item, "declined")}
                            >
                              Decline
                            </button>
                          </>
                        )}

                        {item.status === "accepted" && (
                          <button
                            type="button"
                            className="btn-outline"
                            onClick={() => void handleMediaRequestFulfilledPlaceholder(item)}
                            title="Placeholder until camera/voice upload exists."
                          >
                            Mark fulfilled
                          </button>
                        )}

                        {canCancel && (
                          <button
                            type="button"
                            className="btn-outline"
                            onClick={() => void handleMediaRequestAction(item, "cancelled")}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {mediaRequests.length > 4 && (
                <div className="text-muted small">
                  +{mediaRequests.length - 4} more pending media requests
                </div>
              )}
            </div>
          )}
        </div>

        {unreadCount > 0 && (
          <div
            // Floating new-message indicator above composer.
            style={{
              position: "absolute",
              left: "50%",
              bottom: 148,
              transform: "translateX(-50%)",
              zIndex: 20,
              pointerEvents: "none",
            }}
          >
            <button
              type="button"
              className="btn-primary"
              onClick={scrollToBottom}
              style={{
                pointerEvents: "auto",
                borderRadius: 999,
                minHeight: 34,
                paddingInline: 14,
                fontSize: "0.78rem",
                fontWeight: 900,
                boxShadow: "0 14px 34px rgba(0,0,0,0.32)",
              }}
            >
              {unreadCount} new ↓
            </button>
          </div>
        )}

        <div
          className="section"
          style={{
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
            borderRadius: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            borderTop: "1px solid rgba(148,163,184,0.14)",
            background: "rgba(2,6,23,0.24)",
            paddingTop: 0,
            paddingBottom: "max(0px, env(safe-area-inset-bottom))",
            marginTop: 0,
            marginBottom: 0,
            transform: "translateY(0)",
          }}
        >
          {!!typingUsersLabel() && (
            <div
              className="text-muted small"
              style={{
                marginBottom: 8,
                paddingInline: 10,
                fontWeight: 800,
              }}
            >
              {typingUsersLabel()} <span style={{ color: "var(--primary)", fontWeight: 900 }}>•••</span>
            </div>
          )}

          {replyTo && (
            <div
              style={{
                marginBottom: 8,
                border: "1px solid rgba(148,163,184,0.2)",
                borderRadius: 14,
                padding: "0.55rem 0.65rem",
                background: "rgba(15,23,42,0.72)",
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                gap: 8,
                alignItems: "center",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 850, fontSize: "0.78rem" }}>
                  Replying to {replyTo.senderDisplayName || "Member"}
                </div>
                <div className="text-muted small" style={{ marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {truncatePreview(replyTo.body, 120)}
                </div>
              </div>

              <button
                type="button"
                className="btn-outline"
                onClick={() => setReplyTo(null)}
                title="Cancel reply"
                aria-label="Cancel reply"
                style={{ minHeight: 30, borderRadius: 999, paddingInline: 10 }}
              >
                ×
              </button>
            </div>
          )}

          {emojiOpen && (
            <div className="chip-row" style={{ marginBottom: 8, flexWrap: "wrap" }}>
              {["😀", "😂", "😍", "🥰", "👍", "🙏", "🔥", "🎉", "❤️", "👀"].map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="btn-ghost"
                  onClick={() => insertIntoDraft(emoji)}
                  style={{ fontSize: "1.05rem" }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          {gifOpen && (
            <div
              style={{
                marginBottom: 8,
                border: "1px solid rgba(148,163,184,0.22)",
                borderRadius: 16,
                padding: "0.65rem 0.75rem",
                background: "rgba(15,23,42,0.72)",
              }}
            >
              <div className="text-muted small">
                GIF picker placeholder. Search/upload can be wired here later.
              </div>
            </div>
          )}

          {mediaOpen && (
            <div
              id="web-media-send-panel"
              style={{
                marginBottom: 8,
                border: "1px solid rgba(148,163,184,0.22)",
                borderRadius: 16,
                padding: "0.7rem",
                background: "rgba(15,23,42,0.72)",
              }}
            >
              <div className="section-title" style={{ marginBottom: 6 }}>
                Media
              </div>

              <p className="text-muted small" style={{ marginBottom: 10 }}>
                Choose a media or call action. Live voice/video/image collaboration is intended to use P2P WebRTC. Non-live voice/video/image messages should use E2EE encrypted upload.
              </p>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                {[
                  ["camera", "📷", "Image/video"],
                  ["voice", "🎙", "Voice note"],
                  ["request", "🙋", "Request"],
                  ["attach", "📎", "P2P/collab"],
                ].map(([flow, icon, label]) => (
                  <button
                    key={flow}
                    type="button"
                    className={mediaFlow === flow ? "btn-primary" : "btn-outline"}
                    onClick={() => openMediaFlow(flow as Exclude<MediaFlow, null>)}
                    style={{
                      display: "grid",
                      justifyItems: "center",
                      gap: 5,
                      minWidth: 0,
                      padding: "0.65rem 0.35rem",
                    }}
                  >
                    <span aria-hidden="true" style={{ fontSize: "1.08rem" }}>{icon}</span>
                    <span style={{ fontSize: "0.76rem", fontWeight: 850 }}>{label}</span>
                  </button>
                ))}
              </div>

              {mediaFlow === "camera" && (
                <div
                  style={{
                    border: "1px solid rgba(148,163,184,0.16)",
                    borderRadius: 14,
                    padding: "0.7rem",
                    background: "rgba(2,6,23,0.34)",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontWeight: 900, fontSize: "0.86rem" }}>
                    Camera
                  </div>

                  <p className="text-muted small" style={{ marginTop: 4, marginBottom: 8 }}>
                    Intended flow: open camera app → choose photo or video → capture → choose ∞ normal or 1x private → send.
                  </p>

                  <div className="chip-row" style={{ marginBottom: 8 }}>
                    <button
                      type="button"
                      className={mediaKind === "photo" ? "btn-primary" : "btn-outline"}
                      onClick={() => setMediaKind("photo")}
                    >
                      Photo
                    </button>
                    <button
                      type="button"
                      className={mediaKind === "video" ? "btn-primary" : "btn-outline"}
                      onClick={() => setMediaKind("video")}
                    >
                      Video
                    </button>
                  </div>

                  <div className="chip-row">
                    <button
                      type="button"
                      className={temporaryMode === "normal" ? "btn-primary" : "btn-outline"}
                      onClick={() => setTemporaryMode("normal")}
                    >
                      Normal send
                    </button>
                    <button
                      type="button"
                      className={temporaryMode === "view_once" ? "btn-primary" : "btn-outline"}
                      onClick={() => setTemporaryMode("view_once")}
                    >
                      Temporary / view once
                    </button>
                  </div>

                  <p className="text-muted small" style={{ marginTop: 8 }}>
                    Selected: {mediaKindLabel(mediaKind)} · {mediaSendModeLabel(temporaryMode)}
                  </p>

                  <label
                    className="btn-primary"
                    style={{
                      display: "inline-flex",
                      justifyContent: "center",
                      marginTop: 10,
                      width: "100%",
                      cursor: sending ? "not-allowed" : "pointer",
                      opacity: sending ? 0.65 : 1,
                    }}
                  >
                    {sending
                      ? "Uploading…"
                      : temporaryMode === "view_once"
                        ? "Choose file + send 1x"
                        : "Choose file + send"}
                    <input
                      type="file"
                      accept={mediaKind === "video" ? "video/*" : "image/*"}
                      capture={mediaKind === "video" ? undefined : "environment"}
                      disabled={sending}
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.currentTarget.files?.[0] || null;
                        e.currentTarget.value = "";
                        void sendSelectedWebMediaFile(file);
                      }}
                    />
                  </label>
                </div>
              )}

              {mediaFlow === "voice" && (
                <div
                  style={{
                    border: "1px solid rgba(148,163,184,0.16)",
                    borderRadius: 14,
                    padding: "0.7rem",
                    background: "rgba(2,6,23,0.34)",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontWeight: 900, fontSize: "0.86rem" }}>
                    Voice
                  </div>

                  <p className="text-muted small" style={{ marginTop: 4, marginBottom: 8 }}>
                    Intended flow: record voice → preview → choose ∞ normal or 1x private → send.
                  </p>

                  <div className="chip-row">
                    <button
                      type="button"
                      className={temporaryMode === "normal" ? "btn-primary" : "btn-outline"}
                      onClick={() => setTemporaryMode("normal")}
                    >
                      Normal send
                    </button>
                    <button
                      type="button"
                      className={temporaryMode === "view_once" ? "btn-primary" : "btn-outline"}
                      onClick={() => setTemporaryMode("view_once")}
                    >
                      Temporary / view once
                    </button>
                  </div>

                  <p className="text-muted small" style={{ marginTop: 8 }}>
                    Selected: voice · {mediaSendModeLabel(temporaryMode)}
                  </p>
                </div>
              )}

              {mediaFlow === "request" && (
                <div
                  style={{
                    border: "1px solid rgba(148,163,184,0.16)",
                    borderRadius: 14,
                    padding: "0.7rem",
                    background: "rgba(2,6,23,0.34)",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontWeight: 900, fontSize: "0.86rem" }}>
                    Request media
                  </div>

                  <p className="text-muted small" style={{ marginTop: 4, marginBottom: 8 }}>
                    Intended flow: choose request type → partner receives a temporary media request → partner can approve and create media.
                  </p>

                  <div className="chip-row">
                    <button
                      type="button"
                      className={mediaKind === "photo" ? "btn-primary" : "btn-outline"}
                      onClick={() => setMediaKind("photo")}
                    >
                      Request photo
                    </button>
                    <button
                      type="button"
                      className={mediaKind === "video" ? "btn-primary" : "btn-outline"}
                      onClick={() => setMediaKind("video")}
                    >
                      Request video
                    </button>
                    <button
                      type="button"
                      className={mediaKind === "voice" ? "btn-primary" : "btn-outline"}
                      onClick={() => setMediaKind("voice")}
                    >
                      Request voice
                    </button>
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontWeight: 850, fontSize: "0.82rem", marginBottom: 6 }}>
                      Send request to
                    </div>

                    {chatMode === "private" && visiblePrivateId ? (
                      <div className="text-muted small">
                        {userLabelById(visiblePrivateId)}
                      </div>
                    ) : mediaRequestTargets.length === 1 ? (
                      <div className="text-muted small">
                        {memberLabel(mediaRequestTargets[0])}
                      </div>
                    ) : (
                      <div className="chip-row">
                        {mediaRequestTargets.map((m) => {
                          const id = memberId(m);
                          const selected = selectedMediaRequestTargetId === id;

                          return (
                            <button
                              key={`media_request_target_${id}`}
                              type="button"
                              className={selected ? "btn-primary" : "btn-outline"}
                              onClick={() => setSelectedMediaRequestTargetId(id)}
                            >
                              {memberLabel(m)}
                              {selected ? " ✓" : ""}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <p className="text-muted small" style={{ marginTop: 8 }}>
                    Selected request: {mediaKindLabel(mediaKind)}
                    {mediaRequestTargetLabel() ? ` · to ${mediaRequestTargetLabel()}` : " · choose recipient"}
                  </p>
                </div>
              )}

              {mediaFlow === "attach" && (
                <div
                  style={{
                    border: "1px solid rgba(148,163,184,0.16)",
                    borderRadius: 14,
                    padding: "0.7rem",
                    background: "rgba(2,6,23,0.34)",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontWeight: 900, fontSize: "0.86rem" }}>
                    Attach stored media
                  </div>

                  <p className="text-muted small" style={{ marginTop: 4 }}>
                    Intended flow: start a P2P WebRTC live call, send a P2P image transfer, or open a shared collaboration session. This patch adds backend signaling scaffolding; browser WebRTC UI is the next bundle.
                  </p>
                </div>
              )}

              <button
                type="button"
                className={mediaFlow === "request" ? "btn-primary" : "btn-outline"}
                disabled={
                  mediaFlow !== "request" ||
                  mediaRequestSending ||
                  !effectiveMediaRequestTargetId
                }
                title={
                  mediaFlow === "request"
                    ? "Send this media request"
                    : mediaFlow === "camera"
                      ? "Use the Choose file button above to send encrypted media."
                      : "Disabled until this media flow is implemented."
                }
                onClick={() => void sendMediaRequest()}
                style={{
                  width: "100%",
                  justifyContent: "center",
                  opacity: mediaFlow === "request" ? 1 : 0.62,
                  cursor: mediaFlow === "request" ? "pointer" : "not-allowed",
                }}
              >
                {mediaRequestSending
                  ? "Sending request…"
                  : mediaFlow === "request"
                  ? `Send ${mediaKindLabel(mediaKind)} request${mediaRequestTargetLabel() ? ` to ${mediaRequestTargetLabel()}` : ""}`
                  : mediaFlow === "camera"
                    ? "Use Choose file above"
                    : "Continue — not implemented yet"}
              </button>
            </div>
          )}

          {showRealtimeDebugTools ? (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                marginBottom: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setShowP2PDebugModal(true)}
                title="Open P2P/WebRTC debug panel"
                aria-label="Open P2P/WebRTC debug panel"
                style={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "rgba(255,255,255,0.06)",
                  color: "inherit",
                  cursor: "pointer",
                  fontSize: "1rem",
                  padding: 0,
                }}
              >
                📡
              </button>
            </div>
          ) : null}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) 44px",
              gap: 8,
              alignItems: "center",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                minHeight: 36,
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,0.28)",
                background: "rgba(15,23,42,0.86)",
                boxShadow: "0 10px 32px rgba(0,0,0,0.22)",
                display: "grid",
                gridTemplateColumns: "18px 30px 18px minmax(0, 1fr)",
                alignItems: "center",
                gap: 8,
                padding: "0 12px",
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setEmojiOpen((v) => !v);
                  setGifOpen(false);
                  setMediaOpen(false);
                }}
                title="Emoji"
                aria-label="Emoji"
                style={{
                  border: 0,
                  background: "transparent",
                  color: "rgba(226,232,240,0.9)",
                  padding: 0,
                  fontSize: "1rem",
                  lineHeight: 1,
                  cursor: "pointer",
                }}
              >
                ☺
              </button>

              <button
                type="button"
                onClick={() => {
                  setGifOpen((v) => !v);
                  setEmojiOpen(false);
                  setMediaOpen(false);
                }}
                title="GIF"
                aria-label="GIF"
                style={{
                  border: 0,
                  background: "transparent",
                  color: "rgba(226,232,240,0.82)",
                  padding: 0,
                  fontSize: "0.78rem",
                  fontWeight: 900,
                  letterSpacing: "0.01em",
                  cursor: "pointer",
                }}
              >
                GIF
              </button>

              <button
                type="button"
                onClick={() => {
                  setMediaOpen((v) => {
                    const next = !v;
                    if (!next) {
                      setMediaFlow(null);
                      setMediaKind(null);
                      setTemporaryMode("normal");
                      setSelectedMediaRequestTargetId("");
                    }
                    return next;
                  });
                  setEmojiOpen(false);
                  setGifOpen(false);
                  setStatusMsg("Media/call flows are placeholders: P2P live voice/video/image collaboration and E2EE upload for leave-a-message media are scaffolded for the next implementation bundle.");
                }}
                title="Media"
                aria-label="Media"
                style={{
                  border: 0,
                  background: "transparent",
                  color: "rgba(226,232,240,0.82)",
                  padding: 0,
                  fontSize: "1rem",
                  fontWeight: 900,
                  lineHeight: 1,
                  cursor: "pointer",
                }}
              >
                +
              </button>

              



<textarea
                rows={1}
                style={{
                  width: "100%",
                  minWidth: 0,
                  minHeight: 28,
                  maxHeight: 90,
                  resize: "none",
                  border: 0,
                  outline: 0,
                  background: "transparent",
                  color: "inherit",
                  font: "inherit",
                  padding: "4px 0 4px",
                  overflow: "hidden",
                }}
                placeholder={tt("messages.placeholder", "Message")}
                aria-label="Message"
                value={draft}
                onChange={(e) => { setDraft(e.target.value); void sendWebTypingHint(); }}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
              />
            </div>

            <button
              className="btn-primary"
              onClick={handleSend}
              disabled={
                sending ||
                !draft.trim() ||
                (chatMode === "group" && !allRecipientIds.length) ||
                (chatMode === "private" && !selectedPrivateIds.length)
              }
              title={tt("messages.send", "Send")}
              aria-label={tt("messages.send", "Send")}
              style={{
                width: 40,
                height: 40,
                minHeight: 40,
                minWidth: 40,
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                flexShrink: 0,
              }}
            >
              ➤
            </button>
          </div>
        </div>
      </div>

      {channelsOpen && (
        <>
          <div
            onClick={() => setChannelsOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 60,
              background: "rgba(2,6,23,0.62)",
              backdropFilter: "blur(3px)",
            }}
          />

          <aside
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              zIndex: 61,
              width: "min(92vw, 380px)",
              maxWidth: "100vw",
              background: "rgba(2,6,23,0.96)",
              borderLeft: "1px solid rgba(148,163,184,0.22)",
              boxShadow: "-18px 0 42px rgba(0,0,0,0.35)",
              display: "grid",
              gridTemplateRows: "auto minmax(0, 1fr) auto",
              overflow: "hidden",
            }}
            aria-label="Groups and channels"
          >
            <div
              style={{
                padding: "1rem",
                borderBottom: "1px solid rgba(148,163,184,0.18)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontWeight: 900, fontSize: "1rem" }}>
                    Groups & channels
                  </div>
                  <div className="text-muted small" style={{ marginTop: 4 }}>
                    Main partner chat stays primary. Other spaces are still available.
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => setChannelsOpen(false)}
                  title="Close"
                  aria-label="Close"
                  style={{
                    width: 38,
                    height: 38,
                    minHeight: 38,
                    padding: 0,
                    borderRadius: 999,
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div
              style={{
                overflowY: "auto",
                padding: "0.85rem",
              }}
            >
              {channelsLoading && (
                <p className="text-muted small">Loading groups and channels…</p>
              )}

              {!channelsLoading && !channels.length && (
                <div
                  style={{
                    border: "1px solid rgba(148,163,184,0.18)",
                    borderRadius: 16,
                    padding: "0.85rem",
                    background: "rgba(15,23,42,0.52)",
                  }}
                >
                  <div style={{ fontWeight: 800 }}>No spaces yet</div>
                  <p className="text-muted small" style={{ marginTop: 6 }}>
                    Create a private group or public channel to start a separate conversation.
                  </p>
                </div>
              )}

              {!channelsLoading && !!partnerChannels.length && (
                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontWeight: 900,
                      fontSize: "0.86rem",
                      marginBottom: 8,
                      color: "var(--text)",
                    }}
                  >
                    Partner messages
                  </div>

                  <div
                    className="text-muted small"
                    style={{
                      marginBottom: 8,
                      border: "1px solid rgba(244,114,182,0.22)",
                      background: "rgba(244,114,182,0.08)",
                      borderRadius: 14,
                      padding: "0.55rem 0.65rem",
                    }}
                  >
                    Use this for your private partner conversation. Groups and public channels are separated below.
                  </div>

                  {partnerChannels.map((channel) => {
                    const active = params.originGroupId === channel.group_id;
                    const unread = Number(channel.unread_count || 0);

                    return (
                      <a
                        key={channel.group_id}
                        href={groupChatHref(channel.group_id)}
                        onClick={() => setChannelsOpen(false)}
                        style={{
                          display: "block",
                          textDecoration: "none",
                          color: "inherit",
                          border: active
                            ? "1px solid color-mix(in srgb, var(--primary) 65%, transparent)"
                            : "1px solid rgba(244,114,182,0.28)",
                          borderRadius: 16,
                          padding: "0.8rem",
                          marginBottom: 8,
                          background: active
                            ? "color-mix(in srgb, var(--primary) 14%, rgba(15,23,42,0.72))"
                            : "rgba(244,114,182,0.08)",
                        }}
                      >
                        <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              ♥ {channelLabel(channel)}
                            </div>
                            <div className="text-muted small" style={{ marginTop: 4 }}>
                              {channelSubtitle(channel)}
                              {channel.member_count ? ` · ${channel.member_count} members` : ""}
                            </div>
                          </div>

                          {!!unread && (
                            <span
                              style={{
                                minWidth: 24,
                                height: 24,
                                paddingInline: 7,
                                borderRadius: 999,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "var(--primary)",
                                color: "white",
                                fontSize: "0.72rem",
                                fontWeight: 900,
                              }}
                            >
                              {unread}
                            </span>
                          )}
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}

              {!channelsLoading && (
                <div>
                  <div
                    style={{
                      fontWeight: 900,
                      fontSize: "0.86rem",
                      marginBottom: 8,
                      color: "var(--text)",
                    }}
                  >
                    Other groups & channels
                  </div>

                  {!otherChannels.length ? (
                    <div
                      className="text-muted small"
                      style={{
                        border: "1px solid rgba(148,163,184,0.18)",
                        borderRadius: 16,
                        padding: "0.85rem",
                        background: "rgba(15,23,42,0.52)",
                      }}
                    >
                      No other joined channels.
                    </div>
                  ) : (
                    otherChannels.map((channel) => {
                      const active = params.originGroupId === channel.group_id;
                      const unread = Number(channel.unread_count || 0);

                      return (
                        <a
                          key={channel.group_id}
                          href={groupChatHref(channel.group_id)}
                          onClick={() => setChannelsOpen(false)}
                          style={{
                            display: "block",
                            textDecoration: "none",
                            color: "inherit",
                            border: active
                              ? "1px solid color-mix(in srgb, var(--primary) 65%, transparent)"
                              : "1px solid rgba(148,163,184,0.18)",
                            borderRadius: 16,
                            padding: "0.8rem",
                            marginBottom: 8,
                            background: active
                              ? "color-mix(in srgb, var(--primary) 14%, rgba(15,23,42,0.72))"
                              : "rgba(15,23,42,0.52)",
                          }}
                        >
                          <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 850, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                # {channelLabel(channel)}
                              </div>
                              <div className="text-muted small" style={{ marginTop: 4 }}>
                                {channelSubtitle(channel)}
                                {channel.member_count ? ` · ${channel.member_count} members` : ""}
                              </div>
                            </div>

                            {!!unread && (
                              <span
                                style={{
                                  minWidth: 24,
                                  height: 24,
                                  paddingInline: 7,
                                  borderRadius: 999,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: "var(--primary)",
                                  color: "white",
                                  fontSize: "0.72rem",
                                  fontWeight: 900,
                                }}
                              >
                                {unread}
                              </span>
                            )}
                          </div>
                        </a>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            <div
              style={{
                padding: "0.85rem",
                borderTop: "1px solid rgba(148,163,184,0.18)",
                display: "grid",
                gap: 8,
              }}
            >
              <a className="btn-primary" href="/groups">
                + Create group / channel
              </a>

              <button
                type="button"
                className="btn-outline"
                onClick={() => void loadChannels()}
              >
                Refresh
              </button>
            </div>
          </aside>
        </>
      )}

      {showP2PDebugModal ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "rgba(0,0,0,0.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16
          }}
          onClick={() => setShowP2PDebugModal(false)}
        >
          <div
            style={{
              width: "min(980px, 100%)",
              maxHeight: "min(780px, 92vh)",
              overflow: "auto",
              borderRadius: 18,
              border: "1px solid rgba(255,255,255,0.16)",
              background: "#111827",
              boxShadow: "0 20px 80px rgba(0,0,0,0.55)",
              padding: 16
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              marginBottom: 12
            }}>
              <div style={{ fontWeight: 900, fontSize: "1.05rem" }}>
                Calls / WebRTC test panel
              </div>
              <button
                type="button"
                onClick={() => setShowP2PDebugModal(false)}
                style={{
                  border: "1px solid rgba(255,255,255,0.18)",
                  borderRadius: 999,
                  padding: "6px 12px",
                  background: "rgba(255,255,255,0.08)",
                  color: "inherit",
                  cursor: "pointer"
                }}
              >
                Close
              </button>
            </div>

                        <section
              style={{
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 16,
                padding: 12,
                marginBottom: 12,
                background: "rgba(255,255,255,0.04)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div style={{ fontWeight: 800 }}>Realtime / media tools</div>
                  <div
                    style={{
                      marginTop: 12,
                      padding: 12,
                      border: "1px solid rgba(148, 163, 184, 0.35)",
                      borderRadius: 12,
                      background: "rgba(15, 23, 42, 0.04)",
                    }}
                  >
                    <div style={{ fontWeight: 800, marginBottom: 6 }}>
                      Support diagnostics
                    </div>

                    <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 10 }}>
                      Copy a redacted support bundle. It should not include tokens,
                      passwords, plaintext messages, private keys, media, ciphertext,
                      envelopes, or full payloads.
                    </div>

                    <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 10 }}>
                      Support debug mode is {supportDebugEnabled ? "enabled" : "disabled"}.
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      <button
                        type="button"
                        onClick={
                          supportDebugEnabled
                            ? handleDisableWebSupportDebugMode
                            : handleEnableWebSupportDebugMode
                        }
                        style={{
                          padding: "8px 10px",
                          borderRadius: 10,
                          border: "1px solid rgba(148, 163, 184, 0.45)",
                          fontWeight: 700,
                        }}
                      >
                        {supportDebugEnabled
                          ? "Disable support debug mode"
                          : "Enable support debug mode"}
                      </button>

                      <button
                        type="button"
                        disabled={supportBundleBusy}
                        onClick={handleCopyRedactedWebSupportBundle}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 10,
                          border: "1px solid rgba(148, 163, 184, 0.45)",
                          fontWeight: 700,
                          opacity: supportBundleBusy ? 0.6 : 1,
                        }}
                      >
                        {supportBundleBusy
                          ? "Creating bundle…"
                          : "Copy redacted support bundle"}
                      </button>
                    </div>

                    {supportBundleStatus ? (
                      <div style={{ marginTop: 10, fontSize: 13, opacity: 0.8 }}>
                        {supportBundleStatus}
                      </div>
                    ) : null}
                  </div>


                  <div style={{ opacity: 0.72, fontSize: "0.85rem" }}>
                    E2EE media envelopes, P2P share offers, and spicy tease events.
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" disabled={realtimeBusy} onClick={() => sendMediaEnvelopePlaceholder("voice")}>
                    Voice E2EE
                  </button>
                  <button type="button" disabled={realtimeBusy} onClick={() => sendMediaEnvelopePlaceholder("photo")}>
                    Image E2EE
                  </button>
                  <button type="button" disabled={realtimeBusy} onClick={() => sendMediaEnvelopePlaceholder("video")}>
                    Video E2EE
                  </button>
                  <button type="button" disabled={realtimeBusy} onClick={() => createP2PSharePlaceholder("file")}>
                    P2P file
                  </button>
                  <button type="button" disabled={realtimeBusy} onClick={() => createP2PSharePlaceholder("collab")}>
                    P2P collab
                  </button>
                  <button type="button" disabled={realtimeBusy || !params.originGroupId} onClick={sendFirstTeasePlaceholder}>
                    Send tease
                  </button>
                  <button type="button" disabled={encryptedFlirtsBusy || !params.originGroupId} onClick={() => void loadRecentEncryptedFlirts()}>
                    {encryptedFlirtsBusy ? "Loading flirts…" : "Encrypted flirts"}
                  </button>
                {encryptedFlirts.length ? (
                  <div
                    style={{
                      marginTop: 12,
                      border: "1px solid rgba(148,163,184,0.2)",
                      borderRadius: 14,
                      padding: 10,
                      background: "rgba(15,23,42,0.58)",
                    }}
                  >
                    <div style={{ fontWeight: 900, marginBottom: 8 }}>Encrypted flirts</div>
                    <div style={{ display: "grid", gap: 8 }}>
                      {encryptedFlirts.map((item) => (
                        <div
                          key={item.eventId || `${item.createdAt}_${item.plaintext}`}
                          style={{
                            border: "1px solid rgba(148,163,184,0.16)",
                            borderRadius: 12,
                            padding: 10,
                            background: "rgba(2,6,23,0.38)",
                          }}
                        >
                          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
                            {item.contentType === "flirt.media_request"
                              ? "Photo request"
                              : item.contentType === "flirt.media_offer"
                                ? "Photo offer"
                                : "Flirt"}
                          </div>
                          <div style={{ fontWeight: 800 }}>{item.plaintext}</div>
                          <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4 }}>
                            Sender {item.senderUserId || "unknown"} · {item.createdAt || item.eventId}
                          </div>

                          {item.contentType === "flirt.media_request" ? (
                            <div className="chip-row" style={{ marginTop: 8 }}>
                              <button
                                type="button"
                                disabled={!!respondedEncryptedFlirtIds[String(item.eventId || item.createdAt || item.plaintext || "photo_request")]}
                                onClick={() => {
                                  setRespondedEncryptedFlirtIds((old) => ({
                                    ...old,
                                    [String(item.eventId || item.createdAt || item.plaintext || "photo_request")]: true,
                                  }));
                                  setShowP2PDebugModal(false);
                                  setPrivatePhotoResponseActive(true);
                                  setTemporaryMode("view_once");
                                  setMediaKind("photo");
                                  setMediaFlow("camera");
                                  setMediaOpen(true);
                                  setStatusMsg(
                                    `Private photo response: ${item.plaintext || "Photo request"}. Photo mode is open below; use 1x Private photo.`
                                  );

                                  window.setTimeout(() => {
                                    try {
                                      document.getElementById("web-media-send-panel")?.scrollIntoView({
                                        behavior: "smooth",
                                        block: "center",
                                      });
                                    } catch {}
                                  }, 50);
                                }}
                              >
                                {respondedEncryptedFlirtIds[String(item.eventId || item.createdAt || item.plaintext || "photo_request")]
                                  ? "Sent"
                                  : "Send private photo"}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const msg = `Not now selected for: ${item.plaintext || "Photo request"}. Sending a private decline reply is not wired yet.`;
                                  setStatusMsg(msg);
                                  window.alert(msg);
                                }}
                              >
                                Not now
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                </div>
              </div>
            </section>
<div id="message-livekit-call-tools">
              <MessageLiveKitPanelLazy
                initialConversationId={activeConversationIdForRealtime()}
                initialOriginGroupId={params.originGroupId || ''}
                onCallCreated={postLiveKitCallInviteToChat}
                displayNameByIdentity={liveKitDisplayNameByIdentity}
              />
            </div>
          </div>
        </div>
      ) : null}

    

      {webMediaDeleteDialog ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10001,
            display: "grid",
            placeItems: "center",
            padding: "1rem",
            background: "rgba(2,6,23,0.58)",
            backdropFilter: "blur(8px)",
          }}
          onClick={() => setWebMediaDeleteDialog(null)}
        >
          <div
            style={{
              width: "min(420px, 100%)",
              borderRadius: 18,
              border: "1px solid rgba(148,163,184,0.24)",
              background: "rgba(15,23,42,0.96)",
              boxShadow: "0 24px 90px rgba(0,0,0,0.48)",
              padding: "1rem",
            }}
            onClick={(ev) => ev.stopPropagation()}
          >
            <div style={{ fontWeight: 950, fontSize: "1.05rem" }}>
              Delete media?
            </div>

            <div className="text-muted" style={{ marginTop: 8 }}>
              Choose what should happen to this media.
            </div>

            <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
              <button
                type="button"
                className="btn-outline"
                onClick={async () => {
                  const target = webMediaDeleteDialog!.message;
                  if (!target) {
                    setStatusMsg("Cannot delete this media because the original message row is missing.");
                    setWebMediaDeleteDialog(null);
                    return;
                  }

                  setWebMediaDeleteDialog(null);
                  await deleteMessageFromWeb(target, "me");
                  await closeWebMediaPreview();
                }}
              >
                Delete for me
              </button>

              {webMediaDeleteDialog!.canDeleteEveryone && !webMediaDeleteDialog!.restrictedOnce ? (
                <button
                  type="button"
                  className="btn-outline"
                  onClick={async () => {
                    const target = webMediaDeleteDialog!.message;
                    if (!target) {
                      setStatusMsg("Cannot delete this media because the original message row is missing.");
                      setWebMediaDeleteDialog(null);
                      return;
                    }

                    setWebMediaDeleteDialog(null);
                    await deleteMessageFromWeb(target, "everyone");
                    await closeWebMediaPreview();
                  }}
                >
                  Delete for everyone
                </button>
              ) : null}

              <button
                type="button"
                className="btn-outline"
                onClick={() => setWebMediaDeleteDialog(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {webMediaPreview ? (
        <div
          role="dialog"
          aria-modal="true"
          onMouseMove={showWebMediaControlsTemporarily}
          onClick={showWebMediaControlsTemporarily}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(2,6,23,0.96)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: webMediaFitMode === "fit" ? "center" : "start center",
              padding: webMediaFitMode === "fit" ? "0.5rem" : "4.5rem 1rem 1rem",
              boxSizing: "border-box",
              overflow: webMediaFitMode === "fit" ? "hidden" : "auto",
            }}
          >
            {!webMediaPreview!.url ? (
              <div
                style={{
                  maxWidth: 560,
                  border: "1px solid rgba(148,163,184,0.22)",
                  borderRadius: 18,
                  padding: "1rem",
                  background: "rgba(15,23,42,0.72)",
                  textAlign: "center",
                }}
              >
                <div style={{ fontWeight: 900, fontSize: "1.05rem" }}>
                  Encrypted media could not be opened
                </div>
                <div className="text-muted" style={{ marginTop: 8 }}>
                  The encrypted media could not be decrypted or played in this browser session.
                </div>
              </div>
            ) : webMediaPreview!.mimeType.startsWith("video/") || webMediaPreview!.kind === "video" ? (
              <video
                src={webMediaPreview!.url}
                controls
                autoPlay
                playsInline
                style={{
                  display: "block",
                  width: webMediaFitMode === "fit" ? "auto" : "auto",
                  height: webMediaFitMode === "fit" ? "auto" : "auto",
                  maxWidth: webMediaFitMode === "fit" ? "calc(100vw - 1rem)" : "none",
                  maxHeight: webMediaFitMode === "fit" ? "calc(100vh - 1rem)" : "none",
                  objectFit: "contain",
                  borderRadius: webMediaFitMode === "fit" ? 0 : 14,
                  background: "#000",
                  boxShadow: "0 18px 80px rgba(0,0,0,0.55)",
                }}
              />
            ) : webMediaPreview!.mimeType.startsWith("audio/") || webMediaPreview!.kind === "voice" ? (
              <div
                style={{
                  width: "min(620px, 94vw)",
                  borderRadius: 18,
                  padding: "1rem",
                  background: "rgba(15,23,42,0.82)",
                  border: "1px solid rgba(148,163,184,0.22)",
                }}
              >
                <div style={{ fontWeight: 900, marginBottom: 10 }}>{webMediaPreview!.title}</div>
                <audio
                  src={webMediaPreview!.url}
                  controls
                  autoPlay
                  style={{ width: "100%" }}
                />
              </div>
            ) : webMediaPreview!.mimeType.startsWith("image/") || webMediaPreview!.kind === "image" ? (
              <img
                src={webMediaPreview!.url}
                alt=""
                style={{
                  display: "block",
                  width: webMediaFitMode === "fit" ? "auto" : "auto",
                  height: webMediaFitMode === "fit" ? "auto" : "auto",
                  maxWidth: webMediaFitMode === "fit" ? "calc(100vw - 1rem)" : "none",
                  maxHeight: webMediaFitMode === "fit" ? "calc(100vh - 1rem)" : "none",
                  objectFit: "contain",
                  borderRadius: webMediaFitMode === "fit" ? 0 : 14,
                  boxShadow: "0 18px 80px rgba(0,0,0,0.55)",
                }}
              />
            ) : (
              <div
                style={{
                  maxWidth: 520,
                  border: "1px solid rgba(148,163,184,0.22)",
                  borderRadius: 18,
                  padding: "1rem",
                  background: "rgba(15,23,42,0.72)",
                  textAlign: "center",
                }}
              >
                <div style={{ fontWeight: 900 }}>Unsupported media preview</div>
                <div className="text-muted" style={{ marginTop: 8 }}>
                  {webMediaPreview!.mimeType || webMediaPreview!.kind || "encrypted media"}
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              zIndex: 2,
              opacity: webMediaControlsVisible ? 1 : 0,
              pointerEvents: webMediaControlsVisible ? "auto" : "none",
              transition: "opacity 160ms ease",
              borderRadius: 0,
              padding: "0.65rem 1rem",
              background: "linear-gradient(180deg, rgba(2,6,23,0.82), rgba(2,6,23,0.34))",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
              backdropFilter: "blur(10px)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {webMediaPreview!.title}
              </div>
              <div className="text-muted small">
                {webMediaPreview!.restrictedOnce
                  ? "1x media · sharing/download disabled"
                  : webMediaPreview!.mimeType || webMediaPreview!.kind}
              </div>
            </div>

            {(webMediaPreview!.mimeType.startsWith("video/") ||
              webMediaPreview!.mimeType.startsWith("image/") ||
              webMediaPreview!.kind === "video" ||
              webMediaPreview!.kind === "image") && webMediaPreview!.url ? (
              <button
                type="button"
                className="btn-outline"
                onClick={() => setWebMediaFitMode((old) => old === "fit" ? "original" : "fit")}
              >
                {webMediaFitMode === "fit" ? "Original" : "Fit"}
              </button>
            ) : null}

            {!webMediaPreview!.restrictedOnce && webMediaPreview!.url ? (
              <a
                className="btn-outline"
                href={webMediaPreview!.url}
                download
                onClick={(ev) => {
                  if (webMediaActionBlocked(webMediaPreview!.item)) {
                    ev.preventDefault();
                  }
                }}
              >
                Download
              </a>
            ) : null}

            <button
              type="button"
              className="btn-outline"
              onClick={() => {
                if (!webMediaPreview) return;

                setWebMediaDeleteDialog({
                  message: webMediaPreview!.sourceMessage || webMediaPreview!.item,
                  restrictedOnce: Boolean(webMediaPreview!.restrictedOnce),
                  canDeleteEveryone: Boolean(webMediaPreview!.canDeleteEveryone),
                });
              }}
            >
              Delete
            </button>

            {webMediaPreview!.restrictedOnce && webMediaPreviewCountdown != null ? (
              <span style={{ opacity: 0.75, fontSize: 12, alignSelf: 'center' }}>
                closes in {webMediaPreviewCountdown}s
              </span>
            ) : null}

            <button
              type="button"
              className="btn-outline"
              onClick={() => void closeWebMediaPreview()}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}

</div>
);
}

